import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { MultiSelect } from 'react-multi-select-component';
import {
  confirmAction,
  confirmDelete,
  confirmRemove,
} from '../../utils/confirm';
import classnames from 'classnames';
import {
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  UncontrolledTooltip,
} from 'reactstrap';

import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import { Indonesian } from 'flatpickr/dist/l10n/id.js'; // untuk locale ID
import { incoTermTable } from '../../common/data';
import { isNumericOnly } from '../../utils/validators';
import logger from '../../utils/logger';
import axios from 'axios';
import { api } from '../../config';
import notify, { MESSAGES } from '../../utils/notify';
import Select from 'react-select';
import { addDays, format, startOfDay, subDays } from 'date-fns';
import { convertToDisplayDateFormatddmmmyyyy } from '../../utils/dateformatter';
import {
  convertToInputDateFormat,
  convertToDisplayDateFormat,
  formatDateToDDMMYYYY,
  convertDisplayddMMyyyy,
  getSafeInputDate,
} from '../../utils/dateformatter';
import DOMPurify from 'dompurify';
import { formatNumber, formatNumberUS } from '../../utils/numberformatter';

import {
  InfoPrompt,
  PricingPeriodData,
  CargoType,
  PartyType,
  PUBLISH_PERIOD_DATE_RANGE,
  PUBLISH_PERIOD_MONTHLY,
} from '../../constants/masterData';

import {
  getDyedUndyedLabel,
  isGasolineProductId,
} from '../../shared/offer-domain/constants';
import DeliveryVesselFirstLocation from './component/DeliveryVesselFirstLocation';
import DeliveryVesselSecondLocation from './component/DeliveryVesselSecondLocation';
import DeliveryVesselFirstLocationNotes from './component/DeliveryVesselFirstLocationNotes.js';
import CargoTypeComposition, {
  compositionQuantityStepFor,
  frequencyFor,
  quantityStepFor,
} from './component/CargoTypeComposition';
import {
  LPG_PRODUCT,
  compositionIdPrefix,
  compositionTitles,
  emptyProductTypeEntry,
  isMixProductType,
  primaryCompositionFields,
  productTypeEntryFor,
} from './utils/productTypes';
import ViewOriginModal from './component/ViewOriginModal';
import Composition from './component/Composition';
import AddPortModal from './component/AddPortModal';
import EditPortModal from './component/EditPortModal';
import AddClauseIndexModal from './component/AddClauseIndexModal';
import ClauseTemplateModal from './component/ClauseTemplateModal';

import { Editor, EditorProvider } from 'react-simple-wysiwyg';
import RichTextToolbar from './component/RichTextToolbar';
import IncotermTableFragment from './component/IncotermTableFragment';
import QuantityTableFragment from './component/QuantityTableFragment';
import RequirementGroup from './component/RequirementGroup';
import StepActionBar from './component/StepActionBar';
import {
  focusFieldById,
  focusFirstField,
  requirementFieldId,
} from './utils/focusField';
import { toClauseItem } from './utils/procurementHelpers';
import './component/procurementDetail.css';
import '../../App.css';
import { getAuthToken } from '../../auth/tokenvalidation';
import apiClient from '../../config/apiClient';
import ActionButton from '../../Components/Common/ActionButton';
import {
  SkeletonCard,
  SkeletonForm,
  SkeletonTable,
} from '../../Components/Common/Skeleton';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

// The 13 requirement sections, grouped into four collapsible cards.
const REQ_GROUPS = [
  {
    key: 'contract',
    title: 'Contract & Product',
    icon: 'ri-file-list-2-line',
    sections: [
      { id: 'req-sec-contract', label: 'Contract' },
      { id: 'req-sec-product', label: 'Product' },
      { id: 'req-sec-composition', label: 'Composition', lpgOnly: true },
      { id: 'req-sec-spec', label: 'Product specification' },
    ],
  },
  {
    key: 'delivery',
    title: 'Quantity, Delivery & Port',
    icon: 'ri-ship-2-line',
    sections: [
      { id: 'req-sec-delivery', label: 'Quantity & delivery' },
      { id: 'req-sec-port', label: 'Port restriction' },
      { id: 'req-sec-port-notes', label: 'Port notes' },
    ],
  },
  {
    key: 'terms',
    title: 'Terms & Clauses',
    icon: 'ri-scales-3-line',
    sections: [
      { id: 'req-sec-clause', label: 'Clauses' },
      { id: 'req-sec-payment', label: 'Payment terms' },
      { id: 'req-sec-compliance', label: 'Compliance' },
      { id: 'req-sec-other', label: 'Other terms & conditions' },
      { id: 'req-sec-additional', label: 'Additional notes' },
    ],
  },
  {
    key: 'validity',
    title: 'Submission & Validity',
    icon: 'ri-timer-line',
    sections: [{ id: 'req-sec-validity', label: 'Deadline & validity' }],
  },
];

const REQ_SECTIONS = REQ_GROUPS.flatMap(group => group.sections);

// Error keys set by the requirement validation, mapped to the group that
// shows the field, so a failed save opens the right cards.
const ERROR_GROUP = {
  contractType: 'contract',
  contractPeriodTermStart: 'contract',
  contractPeriodTermEnd: 'contract',
  productNameId: 'contract',
  productTypeId: 'contract',
  productSpecification: 'contract',
  descriptionOfPaymentTerms: 'terms',
  otherTermsAndCondition: 'terms',
  proposalSubmissionDeadline: 'validity',
  proposalValidityPeriodMax: 'validity',
  quantity: 'delivery',
  deliveryProductTypeId: 'delivery',
  deliveryUnit: 'delivery',
  frequency: 'delivery',
  pricingPeriod: 'delivery',
  indexPriceId: 'delivery',
  otherIndexPriceId: 'delivery',
  startDatePublishIndex: 'delivery',
  endDatePublishIndex: 'delivery',
  schemaBL: 'delivery',
};
const errorKeyToGroup = key => {
  if (ERROR_GROUP[key]) return ERROR_GROUP[key];
  if (/^composition|Min$|Max$/.test(key)) return 'contract';
  return 'terms'; // clause errors are keyed by clause id
};

const isBlank = value =>
  value === null ||
  value === undefined ||
  value === '' ||
  value === '<br>' ||
  (typeof value === 'string' && value.trim() === '');

const isFilledLocation = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

// Total lokasi di seluruh baris incoterm:
// - fob      : jumlah entri incotermFOBLocations
// - location : jumlah incotermLocation / incotermLocation2 / incotermLocation3 yang terisi
const countIncotermLocations = (items = []) =>
  items.reduce(
    (totals, data) => ({
      fob:
        totals.fob +
        (Array.isArray(data.incotermFOBLocations)
          ? data.incotermFOBLocations.length
          : 0),
      location:
        totals.location +
        [
          data.incotermLocation,
          data.incotermLocation2,
          data.incotermLocation3,
        ].filter(isFilledLocation).length,
    }),
    { fob: 0, location: 0 }
  );

const FOB_INCOTERM_ID = 'e0173dfd-e109-4054-8640-23d95befd326';
const MAX_INCOTERM_LOCATIONS = 3;
// Guards the quantity list against a very small step on a wide Min–Max range.
const MAX_QUANTITY_OPTIONS = 500;

// Quantity & Delivery Time modal: error keys that live on its Pricing tab
// (everything else is on the Quantity tab), and keys whose field id differs.
const DELIVERY_PRICING_FIELDS = [
  'pricingPeriod',
  'indexPriceId',
  'otherIndexPriceId',
  'startDatePublishIndex',
  'endDatePublishIndex',
  'schemaBL',
];
const DELIVERY_FIELD_IDS = {
  cargoType: 'deliveryCargoType',
  alphaVariant: 'delivery_isDyed',
};

// Proposal dates come back as Date (loaded) or 'd/m/Y H:i' (typed).
const parseProposalDate = d => {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d === 'string' && d.includes('/')) {
    const [datePart, timePart] = d.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours, minutes] = (timePart || '0:0').split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Submission Deadline: earliest H-1 (yesterday). Validity Period: earliest
// H+1 (tomorrow). Both counted from today's date at 00:00.
const minSubmissionDeadline = () => subDays(startOfDay(new Date()), 1);
const minValidityPeriod = () => addDays(startOfDay(new Date()), 1);

// Flatpickr drops a value that falls before minDate, so an older saved date
// would render blank. Lower the bound to that value; save validation still
// rejects it.
const pickerMinDate = (min, value) => {
  const current = parseProposalDate(value);
  return current && current < min ? current : min;
};

// Empty publish index dates can come back from the API as the 1970 epoch.
const hasPublishIndexDate = value => {
  if (!value || String(value).startsWith('1970-01-01')) return false;
  const time = new Date(value).getTime();
  return !Number.isNaN(time) && time !== 0;
};

// LPG procurements price on the LPG case index prices only, every other
// product on the rest (an index price without the flag counts as non-LPG).
const indexPriceFitsProduct = (indexPrice, isLpg) =>
  (indexPrice?.isLpgCase === true) === isLpg;

// Publish index dates come as dd/MM/yyyy from the modal and as ISO strings
// from the API; read the calendar day without a timezone shift.
const publishIndexDay = value => {
  if (!value) return null;
  const text = String(value);
  const dmy = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
  const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
  return null;
};

// A monthly index price is published for exactly one calendar month.
const isWholePublishMonth = (startValue, endValue) => {
  const start = publishIndexDay(startValue);
  const end = publishIndexDay(endValue);
  if (!start || !end) return false;
  const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return start.getDate() === 1 && end.getTime() === lastDay.getTime();
};

const PUBLISH_MONTH_REQUIRED = 'Publish Index Month is required.';

// Quantity rows always show ascending; empty values sit at the bottom so
// they never cover the active numbers.
const sortQtyItemsAsc = items =>
  [...(items || [])].sort((a, b) => {
    const num = value => {
      const parsed = Number(value);
      return value == null || value === '' || Number.isNaN(parsed)
        ? null
        : parsed;
    };
    const an = num(a?.quantity);
    const bn = num(b?.quantity);
    if (an == null && bn == null) return 0;
    if (an == null) return 1;
    if (bn == null) return -1;
    return an - bn;
  });

// Min–Max bounds a manually entered quantity row must stay within, taken
// from the same composition the auto-generated quantity options come from.
const quantityBoundsFor = (form, productTypeId, cargoType) => {
  const typeId = productTypeId || form.productTypeId;
  const source = productTypeEntryFor(form, typeId) || form;

  if (isMixProductType(typeId)) {
    let butaneMin, propaneMin, butaneMax, propaneMax;
    if (cargoType === 1) {
      butaneMin = source.fullCargoVLGCRefrigrateButaneMin;
      propaneMin = source.fullCargoVLGCRefrigratePropaneMin;
      butaneMax = source.fullCargoVLGCRefrigrateButaneMax;
      propaneMax = source.fullCargoVLGCRefrigratePropaneMax;
    } else if (cargoType === 2) {
      butaneMin = source.halfCargoVLGCRefrigrateButaneMin;
      propaneMin = source.halfCargoVLGCRefrigratePropaneMin;
      butaneMax = source.halfCargoVLGCRefrigrateButaneMax;
      propaneMax = source.halfCargoVLGCRefrigratePropaneMax;
    } else if (cargoType === 3) {
      butaneMin = source.semiRefrigeratedRefrigrateButaneMin;
      propaneMin = source.semiRefrigeratedRefrigratePropaneMin;
      butaneMax = source.semiRefrigeratedRefrigrateButaneMax;
      propaneMax = source.semiRefrigeratedRefrigratePropaneMax;
    } else {
      return null; // cargo type not picked yet
    }
    if (
      butaneMin == null ||
      propaneMin == null ||
      butaneMax == null ||
      propaneMax == null
    ) {
      return null;
    }
    return {
      min: Number(butaneMin) + Number(propaneMin),
      max: Number(butaneMax) + Number(propaneMax),
    };
  }

  if (source.compositionMin == null || source.compositionMax == null) {
    return null;
  }
  return {
    min: Number(source.compositionMin),
    max: Number(source.compositionMax),
  };
};

// Pricing fields a saved delivery term still misses, with the same rules the
// modal applies on Update. A delivery created through Add Incoterm is saved
// before its Pricing tab is filled in. `isMonthlyIndexPrice` tells whether an
// index price is set by publish month.
const deliveryPricingErrors = (
  item,
  { isMonthlyIndexPrice = () => false } = {}
) => {
  const missing = {};
  if (!item.pricingPeriod) {
    missing.pricingPeriod = 'Pricing Period is required.';
    return missing;
  }
  if (item.pricingPeriod !== 3) {
    if (!item.indexPriceId) {
      missing.indexPriceId = 'Index Price Reference is required.';
    }
  }
  if (item.pricingPeriod === 1 && isMonthlyIndexPrice(item.indexPriceId)) {
    if (
      !isWholePublishMonth(item.startDatePublishIndex, item.endDatePublishIndex)
    ) {
      missing.startDatePublishIndex = PUBLISH_MONTH_REQUIRED;
    }
  } else if (item.pricingPeriod === 1) {
    if (!hasPublishIndexDate(item.startDatePublishIndex)) {
      missing.startDatePublishIndex =
        'Publish Index Period start date is required.';
    }
    if (!hasPublishIndexDate(item.endDatePublishIndex)) {
      missing.endDatePublishIndex =
        'Publish Index Period end date is required.';
    }
  }
  if (item.pricingPeriod === 2 && isBlank(item.schemaBL)) {
    missing.schemaBL = 'Schema B/L Date is required.';
  }
  return missing;
};

// Selects inside the scrollable incoterm modal: the menu renders on <body> so
// the modal body/footer cannot clip it, opens upward when there is no room,
// is at least wide enough for long port names, and closes when the modal
// body scrolls (a portalled menu would otherwise stay behind).
const MODAL_SELECT_PROPS = {
  menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
  menuPlacement: 'auto',
  closeMenuOnScroll: e =>
    e.target instanceof Element && e.target.classList.contains('modal-body'),
  styles: {
    menuPortal: base => ({ ...base, zIndex: 2000 }),
    menu: base => ({ ...base, minWidth: 220 }),
  },
};

// Incoterms whose form splits the quantity per discharge port (same list the
// old modal used to decide when to show the quantity inputs).
const QTY_SPLIT_INCOTERMS = [
  'e0173dfd-e109-4054-8640-23d95befd326',
  'e0173dfd-e109-4054-8640-23d95befd327',
  'e0173dfd-e109-4054-8640-23d95befd328',
  'e0173dfd-e109-4054-8640-23d95befd329',
];

const INCOTERM_PORTS = [
  { no: 1, locationField: 'incotermLocationId' },
  { no: 2, locationField: 'incotermLocation2Id' },
  { no: 3, locationField: 'incotermLocation3Id' },
];

// Nilai lokasi bisa berupa array (FOB / MultiPort) atau nilai tunggal.
const countLocationValue = value =>
  Array.isArray(value)
    ? value.filter(isFilledLocation).length
    : isFilledLocation(value)
      ? 1
      : 0;

// Jumlah lokasi pada form yang sedang diisi. Hanya dipakai untuk incoterm
// non-FOB — penambahan lokasi lewat MultiSelect incotermFOB tidak dibatasi.
const countFormIncotermLocations = (data = {}) =>
  countLocationValue(data.incotermLocationId) +
  countLocationValue(data.incotermLocation2Id) +
  countLocationValue(data.incotermLocation3Id);

// Satu nama port cukup tampil sekali di tabel Port Restriction.
// Perbandingan mengabaikan huruf besar/kecil dan spasi di tepi,
// jadi "Tanjung Priok" dan "tanjung priok " dianggap sama.
const dedupePortsByName = ports => {
  const seen = new Set();
  return (Array.isArray(ports) ? ports : []).filter(port => {
    const key = String(port?.name ?? '')
      .trim()
      .toLowerCase();
    if (!key) return true; // entri tanpa nama dibiarkan tampil, bukan disembunyikan
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// A clause field, preferring the unsaved edit in procurementClauseDetails.
const clauseValue = (dataCause, item, key) =>
  dataCause.procurementClauseDetails?.find(
    d => String(d.id) === String(item.id)
  )?.[key] ?? item[key];

const RequirementTab = ({
  goNext,
  _ids,
  form,
  setForm,
  dataCause,
  setDataCause,
  handleSaveDraftProc,
  handleSaveCompleteProc,
  validatedeliveryModal,
  errors,
  setErrors,
  setIsLoading,
  isLoading,
  onRefreshForm,
  _completedMode,
}) => {
  const quantityRefs = useRef();
  const isendDate = form.contractType
    ? parseInt(form.contractType, 10) !== 1
    : false;
  // LPG keeps its Frequency on the product composition (per cargo type for
  // the mixed types), so only other products enter it per delivery term.
  const needsFrequency = !LPG_PRODUCT.includes(form.productNameId);
  const isLpgForm = LPG_PRODUCT.includes(form.productNameId);
  // 'Quantity And Delivery Time': alur non-LPG berlaku untuk semua produk —
  // quantity di-entry manual via Add Quantity dan validasinya ikut semua.
  // Selain area ini (Frequency, komposisi, incoterm) tetap alur per-produk.
  const isNonLpgQuantityFlow = true;
  // Gasoline parcels choose which alpha variants (Dyed / Undyed) to request.
  const isGasolineForm = isGasolineProductId(form.productNameId);
  const productTypeEntries = form.procurementProductTypes || [];
  const [unitSizeType, setUnitSizeType] = useState('');
  const [deletemodal, setDeleteModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalincoterm, setModalIncoterm] = useState(false);
  const [modalloadingdischarge, setModalLoadingDischarge] = useState(false);
  const [modalquantityincoterm, setModalQuantityIncoterm] = useState(false);
  const [productName, setProductName] = useState({ items: [] });
  const [productTypeData, setProductTypeData] = useState({ items: [] });
  const [currencyName, setCurrencyName] = useState({ items: [] });
  const [, setRatioData] = useState({ items: [] });
  const [invoiceQuantityData, setInvoiceQuantityData] = useState({ items: [] });
  const [modelinvoiceQuantityData, setModelInvoiceQuantityData] = useState({
    items: [],
  });
  const [qtyDeliveryData, setQtyDeliveryData] = useState({ items: [] });
  const [qtyDeliveryDataQuantity, setQtyDeliveryDataQuantity] = useState({
    items: [],
  });

  const [qtyDeliveryDataTotalQty, setQtyDeliveryDataTotalQty] = useState({
    items: [],
  });
  // Id baris quantity yang sedang bisa diedit (null = semua terkunci/readonly)
  const [editingQtyId, setEditingQtyId] = useState(null);
  // Pesan error min/max per baris quantity (key = id baris)
  const [qtyRowErrors, setQtyRowErrors] = useState({});

  const [IncotermRouteData, setIncotermRouteData] = useState({ items: [] });
  const [IncotermLocationData, setIncoTermLocationData] = useState({
    items: [],
  });
  //const [setIsLoadingIncotermLoc] = useState(false);
  const [lawData, setLawData] = useState({ items: [] });
  const [abitrationData, setAbitrationData] = useState({ items: [] });
  const [dataindexprice, setDataIndexPrice] = useState({ items: [] });
  const indexPriceOptions = useMemo(
    () =>
      dataindexprice.items.filter(item =>
        indexPriceFitsProduct(item, isLpgForm)
      ),
    [dataindexprice, isLpgForm]
  );
  // The index price decides how its publish period is set: a date range, or
  // one publish month. Unknown (none picked yet) keeps the date range.
  const publishPeriodOf = id =>
    dataindexprice.items.find(item => item.value === id)?.publishPeriodType ??
    PUBLISH_PERIOD_DATE_RANGE;
  const isMonthlyIndexPrice = id =>
    publishPeriodOf(id) === PUBLISH_PERIOD_MONTHLY;
  const [incotermtype, setIncotremType] = useState({ items: [] });
  const [qtyDeliveryDataIncoterm, setQtyDeliveryDataIncoterm] = useState({
    items: [],
  });
  const [viewOriginModal, setViewOriginModal] = useState(false);
  const [viewOriginLocations, setViewOriginLocations] = useState([]);
  const [viewOriginRoutes, setViewOriginRoutes] = useState('');
  const incotermLocationTotals = countIncotermLocations(
    qtyDeliveryDataIncoterm.items
  );
  const qtyDeliveryDataTotalQtySum = qtyDeliveryDataTotalQty.items.reduce(
    (sum, row) => sum + (Number(row.quantity) || 0),
    0
  );
  const uniquePortRestrictions = dedupePortsByName(form.portRestriction);
  const token = getAuthToken();
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const [insertOption, setInsertOption] = useState('');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesByClauseDetailId, setTemplatesByClauseDetailId] = useState(
    {}
  );
  const [activeTab, setactiveTab] = useState('1');
  const toggles = tab => {
    if (activeTab !== tab) {
      setactiveTab(tab);
    }
  };

  const [activeTabs, setActiveTabs] = useState({});
  const toggle = (id, tab) => {
    setActiveTabs(prev => ({
      ...prev,
      [id]: tab,
    }));
  };

  const [jettyRestrictions, setJettyRestrictions] = useState([]);
  const [jettyRestrictionsLoading, setJettyRestrictionsLoading] =
    useState(false);
  const [addPortModal, setAddPortModal] = useState(false);
  const [editPortItem, setEditPortItem] = useState(null);
  // Naikkan nilainya setelah jetty baru disimpan supaya effect fetch jetty
  // memuat ulang daftar tanpa reload halaman.
  const [jettyRefreshKey, setJettyRefreshKey] = useState(0);

  // Jetty yang dipilih untuk procurement ini (snapshot nilai DWT/LOA/Draft
  // tersimpan per procurement, bukan dibaca langsung dari master).
  useEffect(() => {
    if (!form.id) {
      setJettyRestrictions([]);
      return;
    }

    let cancelled = false;
    const loadJettyRestrictions = async () => {
      setJettyRestrictionsLoading(true);
      try {
        const response = await axios.get(
          `${api.API_URL}/procurement-port-restriction/by-procurement/${form.id}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const result = response?.data ?? response;
        const items = result?.data ?? result ?? [];
        if (!cancelled) {
          setJettyRestrictions(Array.isArray(items) ? items : []);
        }
      } catch (error) {
        if (!cancelled) {
          logger.error('Error fetching procurement port restriction:', error);
          setJettyRestrictions([]);
        }
      } finally {
        if (!cancelled) {
          setJettyRestrictionsLoading(false);
        }
      }
    };

    loadJettyRestrictions();
    // Cegah response lama menimpa response baru.
    return () => {
      cancelled = true;
    };
  }, [form.id, token, jettyRefreshKey]);

  const groupedJettyRestrictions = useMemo(() => {
    const groups = new Map();

    // Grup hanya dari port Delivery Term procurement ini. Jetty milik location
    // yang sudah tidak ada di Delivery Term tidak ditampilkan.
    uniquePortRestrictions.forEach(port => {
      if (port.locationId && !groups.has(port.locationId)) {
        groups.set(port.locationId, {
          key: port.locationId,
          name: port.name || '-',
          items: [],
        });
      }
    });

    jettyRestrictions.forEach(item => {
      groups.get(item.locationId)?.items.push(item);
    });

    return Array.from(groups.values());
  }, [uniquePortRestrictions, jettyRestrictions]);

  // Opsi Location pada modal Add Port: hanya port Delivery Term procurement
  // ini. Value = Location.Id (satuan yang dipakai master jetty).
  const portLocationOptions = useMemo(
    () =>
      uniquePortRestrictions
        .map(port => ({
          value: port.locationId || '',
          label: port.name || '-',
        }))
        .filter(option => option.value),
    [uniquePortRestrictions]
  );

  const handleRemovePort = async item => {
    if (!(await confirmRemove('port', item.name))) return;
    try {
      await axios.delete(
        `${api.API_URL}/procurement-port-restriction/${item.id}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      notify.success('Port removed from this procurement.');
      setJettyRefreshKey(prev => prev + 1);
    } catch (error) {
      notify.apiError(error, 'remove the port');
    }
  };

  const [incotermErrors, setIncotermErrors] = useState({});

  const handleNumberChange = fieldName => e => {
    const input = e.target.value;
    if (isNumericOnly(input)) {
      setForm(prev => ({
        ...prev,
        [fieldName]: input === '' ? null : parseInt(input),
      }));
    }
  };

  const handleNoteTextChange = (event, field) => {
    setForm(form => ({
      ...form,
      [field]: event.target.value,
    }));
  };

  const handleNoteTextChanges = (event, field) => {
    const itemId = field.replace('clauseDesc_', '');
    const clauseDetail = dataCause?.procurementClauseDetails?.find(
      d => String(d.id) === String(itemId)
    );
    const fallbackItem =
      dataCause?.items?.find(i => String(i.id) === String(itemId)) || {};
    const originalItem = clauseDetail || fallbackItem;
    setDataCause(dataCause => {
      const updatedItem = {
        ...originalItem,
        id: String(itemId),
        clauseDetailId: originalItem.clauseDetailId ?? null,
        clauseDescription: event.target.value,
        isShowToDMUT: originalItem.isShowToDMUT ?? null,
      };
      const prev = dataCause.procurementClauseDetails ?? dataCause?.items ?? [];
      const idx = prev.findIndex(i => String(i.id) === String(itemId));
      const procurementClauseDetails =
        idx >= 0
          ? prev.map((i, n) => (n === idx ? { ...i, ...updatedItem } : i))
          : [...prev, updatedItem];
      const rest = { ...dataCause };
      delete rest[itemId];
      delete rest[field];
      return {
        ...rest,
        procurementClauseDetails,
      };
    });
  };

  const handleDropdownInsert = (value, editorId) => {
    if (!value) return;
    // per-clause lists hold templates past the first page of templateOptions
    // and the ones just created from this page
    const tpl = [
      ...templateOptions,
      ...Object.values(templatesByClauseDetailId).flat(),
    ].find(t => String(t.id) === String(value));
    if (!tpl || !tpl.template) return;

    const sanitizedHtml = DOMPurify.sanitize(tpl.template, {
      ALLOWED_TAGS: [
        'b',
        'i',
        'u',
        's',
        'strong',
        'em',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'span',
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'a',
        'sub',
        'sup',
      ],
      ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
    });

    const editorEl = editorId
      ? document.getElementById(editorId)
      : editorRef.current;
    if (editorEl) {
      editorEl.focus();
      //document.execCommand('insertHTML', false, tpl.template);
      //handleNoteTextChange({ target: { value: editorEl.innerHTML } }, editorId);
      document.execCommand('insertHTML', false, sanitizedHtml);
      const cleanOutput = DOMPurify.sanitize(editorEl.innerHTML);
      handleNoteTextChange({ target: { value: cleanOutput } }, editorId);
    }
    setInsertOption('');
  };

  const handleTextChangeDelivery = field => event => {
    setDeliveryFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDeliveryTextChange = e => {
    const { name, value } = e.target;
    setDeliveryFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeliveryDateChange = e => {
    const { name, value } = e.target;
    setDeliveryFormData(prev => ({
      ...prev,
      [name]: convertToDisplayDateFormat(value),
    }));
  };

  const validatePublishIndexDates = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const [sDay, sMonth, sYear] = startDate.split('/').map(Number);
    const [eDay, eMonth, eYear] = endDate.split('/').map(Number);
    const start = new Date(sYear, sMonth - 1, sDay);
    const end = new Date(eYear, eMonth - 1, eDay);
    if (end < start) {
      return 'End Date cannot be earlier than Start Date';
    }
    return null;
  };

  // Quantity & Delivery Time modal: the field is on the Pricing tab or the
  // Quantity tab, so open that tab first, then focus once it has rendered.
  const focusDeliveryField = key => {
    setactiveTab(DELIVERY_PRICING_FIELDS.includes(key) ? '2' : '1');
    window.setTimeout(() => focusFieldById(DELIVERY_FIELD_IDS[key] || key), 60);
  };

  const notifyFieldErrors = fieldErrors => {
    const keys = Object.keys(fieldErrors);
    notify.warning(
      <div>
        Please correct the following fields (click one to go to it):
        <ul className='mb-0 mt-1' style={{ paddingLeft: '1.25rem' }}>
          {keys.map(key => (
            <li key={key}>
              <button
                type='button'
                className='btn btn-link p-0 text-start align-baseline'
                style={{ fontSize: 'inherit' }}
                onClick={() => focusDeliveryField(key)}
              >
                {fieldErrors[key]}
              </button>
            </li>
          ))}
        </ul>
      </div>,
      { className: 'hy-toast hy-toast--warning hy-toast--wide' }
    );
    if (keys.length > 0) focusDeliveryField(keys[0]);
  };

  const validateProposalDates = () => {
    const { proposalSubmissionDeadline, proposalValidityPeriodMax } = form;
    const submission = parseProposalDate(proposalSubmissionDeadline);
    const validity = parseProposalDate(proposalValidityPeriodMax);
    const minSubmission = minSubmissionDeadline();
    const minValidity = minValidityPeriod();

    const dateErrors = {};
    if (submission && submission < minSubmission) {
      dateErrors.proposalSubmissionDeadline = `Submission Deadline cannot be earlier than ${format(
        minSubmission,
        'dd MMM yyyy'
      )} (H-1)`;
    }
    if (validity && validity < minValidity) {
      dateErrors.proposalValidityPeriodMax = `Validity Period cannot be earlier than ${format(
        minValidity,
        'dd MMM yyyy'
      )} (H+1)`;
    }
    if (Object.keys(dateErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...dateErrors }));
      notify.warning(Object.values(dateErrors).join('. ') + '.');
      return false;
    }

    if (!submission || !validity) return true;

    if (validity < submission) {
      setErrors(prev => ({
        ...prev,
        proposalValidityPeriodMax:
          'Validity Period cannot be earlier than Submission Deadline',
      }));
      notify.warning(
        'Validity Period cannot be earlier than the Submission Deadline. Choose a later date.'
      );
      return false;
    }
    return true;
  };

  // const handleDeliverySelectChange = selectedOption => {
  //   setDeliveryFormData(prev => ({
  //     ...prev,
  //     ratioLpgId: selectedOption.value,
  //   }));
  // };

  const handleDeliverySelectChange = (e, field) => {
    const value = e ? e.value : '';
    setDeliveryFormData(prev => ({ ...prev, [field]: value }));
  };

  const validatequantityanddelivery = () => {
    if (
      isNonLpgQuantityFlow &&
      (!qtyDeliveryDataTotalQty.items ||
        qtyDeliveryDataTotalQty.items.length === 0)
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: 'Quantity cannot be empty.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (
      isNonLpgQuantityFlow &&
      Array.isArray(qtyDeliveryDataTotalQty.items) &&
      qtyDeliveryDataTotalQty.items.some(
        item =>
          item.quantity === '' ||
          item.quantity == null ||
          Number.isNaN(Number(item.quantity))
      )
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: 'Quantity must be filled.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (
      isNonLpgQuantityFlow &&
      Array.isArray(qtyDeliveryDataTotalQty.items) &&
      qtyDeliveryDataTotalQty.items.some(item => Number(item.quantity) <= 0)
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: 'Quantity must be greater than 0.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (
      quantityBounds &&
      isNonLpgQuantityFlow &&
      Array.isArray(qtyDeliveryDataTotalQty.items) &&
      qtyDeliveryDataTotalQty.items.some(
        item =>
          item.quantity !== '' &&
          item.quantity != null &&
          (Number(item.quantity) < quantityBounds.min ||
            Number(item.quantity) > quantityBounds.max)
      )
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: quantityRangeMessage(quantityBounds),
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (!validateDeliveryProductType()) return false;

    if (isDeliveryMixType && !deliveryformData.cargoType) {
      setErrors(prev => ({ ...prev, cargoType: 'Please select Cargo Type' }));
      notify.warning(MESSAGES.mandatory);
      focusDeliveryField('cargoType');
      return false;
    }

    let tempErrors = {};
    if (
      LPG_PRODUCT.includes(form.productNameId) &&
      !deliveryformData.deliveryUnit?.trim()
    ) {
      tempErrors.deliveryUnit = 'Please enter Delivery Unit';
    }

    if (
      isGasolineForm &&
      deliveryformData.isDyed === false &&
      deliveryformData.isUndyed === false
    ) {
      tempErrors.alphaVariant = 'Select Dyed, Undyed, or both';
    }

    const freq = deliveryformData.frequency;
    const isEmpty = freq == null || String(freq).trim() === '';

    if (needsFrequency && isEmpty) {
      tempErrors.frequency = 'Please enter Frequency';
    }

    if (!deliveryformData.pricingPeriod) {
      tempErrors.pricingPeriod = 'Pricing Period is required.';
    }

    if (deliveryformData.pricingPeriod !== 3) {
      if (!deliveryformData.indexPriceId) {
        tempErrors.indexPriceId = 'Index Price Reference is required.';
      }
    }

    if (
      deliveryformData.pricingPeriod === 1 &&
      isMonthlyIndexPrice(deliveryformData.indexPriceId)
    ) {
      if (
        !isWholePublishMonth(
          deliveryformData.startDatePublishIndex,
          deliveryformData.endDatePublishIndex
        )
      ) {
        tempErrors.startDatePublishIndex = PUBLISH_MONTH_REQUIRED;
      }
    } else if (deliveryformData.pricingPeriod === 1) {
      if (!deliveryformData.startDatePublishIndex) {
        tempErrors.startDatePublishIndex =
          'Publish Index Period start date is required.';
      }
      if (!deliveryformData.endDatePublishIndex) {
        tempErrors.endDatePublishIndex =
          'Publish Index Period end date is required.';
      }
      if (
        deliveryformData.startDatePublishIndex &&
        deliveryformData.endDatePublishIndex
      ) {
        const err = validatePublishIndexDates(
          deliveryformData.startDatePublishIndex,
          deliveryformData.endDatePublishIndex
        );
        if (err) {
          tempErrors.endDatePublishIndex = err;
        }
      }
    }

    if (deliveryformData.pricingPeriod === 2) {
      if (!deliveryformData.schemaBL?.trim()) {
        tempErrors.schemaBL = 'Schema B/L Date is required.';
      }
    }

    if (Object.keys(tempErrors).length !== 0) {
      notifyFieldErrors(tempErrors);
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const validatequantityadddelivery = () => {
    if (!validateDeliveryProductType()) return false;

    if (
      isNonLpgQuantityFlow &&
      (!qtyDeliveryDataTotalQty.items ||
        qtyDeliveryDataTotalQty.items.length === 0)
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: 'Quantity cannot be empty.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (
      isNonLpgQuantityFlow &&
      Array.isArray(qtyDeliveryDataTotalQty.items) &&
      qtyDeliveryDataTotalQty.items.some(
        item =>
          item.quantity === '' ||
          item.quantity == null ||
          Number.isNaN(Number(item.quantity))
      )
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: 'Quantity must be filled.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (
      isNonLpgQuantityFlow &&
      Array.isArray(qtyDeliveryDataTotalQty.items) &&
      qtyDeliveryDataTotalQty.items.some(item => Number(item.quantity) <= 0)
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: 'Quantity must be greater than 0.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    if (
      quantityBounds &&
      isNonLpgQuantityFlow &&
      Array.isArray(qtyDeliveryDataTotalQty.items) &&
      qtyDeliveryDataTotalQty.items.some(
        item =>
          item.quantity !== '' &&
          item.quantity != null &&
          (Number(item.quantity) < quantityBounds.min ||
            Number(item.quantity) > quantityBounds.max)
      )
    ) {
      setErrors(prev => ({
        ...prev,
        quantity: quantityRangeMessage(quantityBounds),
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    let tempErrors = {};
    if (!deliveryformData.deliveryUnit?.trim()) {
      tempErrors.deliveryUnit = 'Please enter Delivery Unit';
    }
    if (isDeliveryMixType && !deliveryformData.cargoType) {
      tempErrors.cargoType = 'Please select Cargo Type';
    }
    if (
      isGasolineForm &&
      deliveryformData.isDyed === false &&
      deliveryformData.isUndyed === false
    ) {
      tempErrors.alphaVariant = 'Select Dyed, Undyed, or both';
    }
    if (
      needsFrequency &&
      String(deliveryformData.frequency ?? '').trim() === ''
    ) {
      tempErrors.frequency = 'Please enter Frequency';
    }

    if (Object.keys(tempErrors).length !== 0) {
      notifyFieldErrors(tempErrors);
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const toggledeletemodal = () => {
    setDeleteModal(!deletemodal);
  };

  const deliverymodal = () => {
    if (validatedeliveryModal()) {
      setModal(!modal);
    }
  };

  const togglemodalincoterm = () => {
    setIncotermErrors({});
    setModalIncoterm(!modalincoterm);
  };

  const toggleloadingdischrge = () => {
    setModalLoadingDischarge(!modalloadingdischarge);
  };

  // Creates the delivery term and returns its new id (null when validation
  // or the request fails).
  const handleAddDataDelivery = async _e => {
    let newId = null;
    if (validatequantityadddelivery()) {
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${api.API_URL}/quantity-delivery`,
          selectedFieldsDeliveryFormData,
          {
            withXSRFToken: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        newId = response?.id ?? null;
        sortQtyItemsAfterSubmit();
        fetchDeliveryDataDetail(response.id);
        fetchDeliveryDataDetailIncoterm(response.id);
        //fetchDeliveryDataDetailQuantity(response.id);
      } catch (error) {
        notify.apiError(error, 'save the delivery term');
        logger.error('Error handleAddDataDelivery: ', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
        handleSaveDraftProc(); // tetap jalan
        LoadDefault();
      }
    }
    return newId;
  };

  // Add Incoterm requires a saved delivery term: the button stays hidden
  // until the delivery has been saved (and its quantities loaded).
  const handleAddIncotermClick = () => {
    if (deliveryformData.id) handleIncotermOpenModalNew(deliveryformData.id);
  };

  const handleUpdDataDelivery = async _e => {
    if (validatequantityanddelivery()) {
      try {
        setIsLoading(true);
        await axios.put(
          `${api.API_URL}/quantity-delivery/${deliveryformData.id}`,
          selectedFieldsDeliveryFormData,
          {
            withXSRFToken: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        notify.success(MESSAGES.updated);
        sortQtyItemsAfterSubmit();
        fetchDeliveryDataDetail(deliveryformData.id);
        fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      } catch (error) {
        notify.apiError(error, 'update the delivery term');
        logger.error('Error handleUpdDataDelivery: ', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
        handleSaveDraftProc();
        // setModal(!modal);
        LoadDefault();
      }
    }
  };

  const handleTypeChange = e => {
    const contractTypeValue = e.target.value;
    const flags = parseInt(contractTypeValue);
    setForm(prev => ({
      ...prev,
      contractType: contractTypeValue,
      contractPeriodTermEnd: flags !== 1 ? null : prev.contractPeriodTermEnd,
    }));
  };

  useEffect(() => {
    quantityRefs.current = qtyDeliveryData.items.map(() => React.createRef());
  }, [qtyDeliveryData.items]);

  const fetchAbitration = useCallback(async () => {
    try {
      const response = await axios.get(
        `${api.API_URL}/law/getAll-arbitration`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const items = response.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setAbitrationData({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  const fetchQtyDeliveryList = useCallback(async () => {
    if (!form?.id) {
      return;
    }
    setError(null);
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-delivery?ProcurementId=${form.id}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.items) {
        logger.warn('No items found in response');
        return;
      }

      const items = response.items
        .map(item => ({
          procurementId: item.procurementId,
          ratioLpgId: item.ratioLpgId,
          ratioName: item.ratioLpg?.ratioName ?? null,
          deliveryUnit: item.deliveryUnit,
          productTypeId: item.productTypeId ?? null,
          productTypeName: item.productType?.name ?? null,
          isDyed: item.isDyed !== false,
          isUndyed: item.isUndyed !== false,
          frequency: item.frequency,
          pricingPeriod: item.pricingPeriod,
          indexPriceId: item.indexPriceId ?? null,
          otherIndexPriceId: item.otherIndexPriceId ?? null,
          indexPricename: item.indexPrice?.name ?? null,
          otherIndexPricename: item.otherIndexPrice?.name ?? null,
          startDatePublishIndex: item.startDatePublishIndex,
          endDatePublishIndex: item.endDatePublishIndex,
          schemaBL: item.schemaBL,
          infoPrompt: item.infoPrompt,
          priceAmount: item.priceAmount,
          notesForPricing: item.notesForPricing,
          isCorrelationBetweenTwoIndex: item.isCorrelationBetweenTwoIndex,
          id: item.id,
          cargoType: item.cargoType,
          createdDate: item.createdDate ?? null,
        }))
        .sort(
          (b, a) =>
            new Date(b.createdDate ?? 0).getTime() -
            new Date(a.createdDate ?? 0).getTime()
        );

      setQtyDeliveryData({ items });
    } catch (error) {
      setError(error.message);
    }
  }, [form?.id, token, setError]);

  useEffect(() => {
    const fetchProductName = async () => {
      try {
        const response = await apiClient.get(`/product`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.data.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setProductName({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchCurrencyName = async () => {
      try {
        const response = await apiClient.get(`/paymentcurrency`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.data.items.map(item => ({
          value: item.id,
          label: item.currencyName + ' (' + item.currencyCode + ')',
        }));
        setCurrencyName({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchRatio = async () => {
      try {
        const response = await apiClient.get(`/ratio-lpg`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.data.items.map(item => ({
          value: item.id,
          label: item.ratioName,
        }));
        setRatioData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchLaw = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/law`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setLawData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchInvoiceQuantity = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/invoicing-quantity`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setInvoiceQuantityData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchIndexPrice = async () => {
      try {
        const response = await axios.get(
          `${api.API_URL}/indexprice?PageNumber=1&PageSize=200`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const list = response.items || [];
        const items = list.map(item => ({
          value: item.id,
          label: item.name,
          isLpgCase: item.isLpgCase,
          publishPeriodType: item.publishPeriodType,
        }));
        setDataIndexPrice({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchIncotermRoute = async () => {
      try {
        const response = await axios.get(
          `${api.API_URL}/incoterm/get-incoterm-route`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setIncotermRouteData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchIncotermType = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/incoterm`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setIncotremType({ items });
      } catch (error) {
        logger.error('Error index price:', error);
      }
    };

    const fetchTemplateData = async (clauseDetailId = null) => {
      try {
        let url = `${api.API_URL}/template-procurement-document?PageNumber=1&PageSize=50`;
        if (clauseDetailId) {
          url += `&ClauseDetailId=${clauseDetailId}`;
        }
        const response = await axios.get(url, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items || [];
        if (clauseDetailId) {
          setTemplatesByClauseDetailId(prev => ({
            ...prev,
            [clauseDetailId]: items,
          }));
        } else {
          setTemplateOptions(items);
        }
        return items;
      } catch (error) {
        logger.error('Error fetchTemplateData:', error);
        return [];
      }
    };

    const loadAll = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          fetchProductName(),
          fetchCurrencyName(),
          fetchRatio(),
          fetchInvoiceQuantity(),
          fetchLaw(),
          fetchIndexPrice(),
          fetchQtyDeliveryList(),
          fetchIncotermRoute(),
          fetchIncotermType(),
          fetchTemplateData(),
        ]);

        setDeliveryFormData(prev => ({
          ...prev,
          procurementId: form.id,
        }));
      } catch (error) {
        logger.error('Error fetching data: ', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAll();
  }, [form.id, fetchQtyDeliveryList, token, setIsLoading]);

  useEffect(() => {
    if (productTypeData.items?.length > 0 && form.productTypeId) {
      const found = productTypeData.items.find(
        item => item.value === form.productTypeId
      );
      if (found) {
        setUnitSizeType(found.uom);
      }
    }
  }, [productTypeData, form.productTypeId]);

  const fetchModeInvoiceQuantity = useCallback(async () => {
    try {
      const response = await axios.get(
        `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const items = response.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setModelInvoiceQuantityData({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadfetchModeInvoiceQuantity = async () => {
      try {
        if (form.invoicingQuantityId) {
          await Promise.all([fetchModeInvoiceQuantity()]);
        }
      } catch (error) {
        logger.error('Error fetching data: ', error);
      }
    };
    loadfetchModeInvoiceQuantity();
  }, [form.invoicingQuantityId, fetchModeInvoiceQuantity]);

  useEffect(() => {
    const loadfetchAbitration = async () => {
      try {
        if (form.lawId) {
          await Promise.all([fetchAbitration()]);
        }
      } catch (error) {
        logger.error('Error fetching data: ', error);
      }
    };
    loadfetchAbitration();
  }, [form.lawId, fetchAbitration]);

  const fetchProductType = useCallback(
    async productTypeId => {
      try {
        const response = await axios.get(
          `${api.API_URL}/product-type?ProductId=${productTypeId}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
          uom: item.uoM,
          pricingunit: item.pricingUnit,
        }));
        setProductTypeData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    },
    [token]
  );

  useEffect(() => {
    if (form.productNameId) {
      fetchProductType(form.productNameId);
    }
  }, [form.productNameId, fetchProductType]);

  // useEffect(() => {
  //   fetchTemplateData();
  // });

  const LoadDefault = async () => {
    try {
      const promises = [fetchQtyDeliveryList()];

      // if (deliveryformData?.id) {
      //   promises.push;
      //fetchDeliveryDataDetailQuantity(deliveryformData.id),
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      //}
      await Promise.all(promises);
      setDeliveryFormData(prev => ({
        ...prev,
        procurementId: form.id,
      }));
    } catch (error) {
      logger.error('LoadDefault error:', error);
    }
  };

  const handleSelectChangeDelivery = (e, field) => {
    setDeliveryFormData(prev => ({
      ...prev,
      [field]: e ? e.value : '',
      ...(field === 'otherIndexPriceId'
        ? { isCorrelationBetweenTwoIndex: e && e.value !== null }
        : {}),
      // Dates entered for one kind of publish period do not fit the other.
      ...(field === 'indexPriceId' &&
      publishPeriodOf(prev.indexPriceId) !== publishPeriodOf(e?.value)
        ? { startDatePublishIndex: null, endDatePublishIndex: null }
        : {}),
      ...(field === 'pricingPeriod' && e?.value === 3
        ? {
            indexPriceId: null,
            otherIndexPriceId: null,
            schemaBL: null,
            startDatePublishIndex: null,
            endDatePublishIndex: null,
          }
        : {}),
    }));
  };

  const handleProductNameChange = selectedOption => {
    if (selectedOption) {
      setForm(prev => ({
        ...prev,
        productNameId: selectedOption.value,
        productTypeId: null,
        procurementProductTypes: [],
      }));
    } else {
      setForm(prev => ({
        ...prev,
        productNameId: null,
        productTypeId: null,
        procurementProductTypes: [],
      }));
      setUnitSizeType('');
    }
  };

  // The procurement's product types; the first one is the primary type that
  // productTypeId (and the procurement's own composition fields) mirror.
  const applyProductTypeEntries = entries => {
    setForm(prev => ({
      ...prev,
      procurementProductTypes: entries,
      productTypeId: entries[0]?.productTypeId ?? null,
      productType: entries[0]?.productTypeName ?? null,
      ...(entries[0] ? primaryCompositionFields(entries[0]) : {}),
    }));
    setUnitSizeType(entries[0]?.uom ?? '');
  };

  const toProductTypeEntry = option =>
    productTypeEntries.find(entry => entry.productTypeId === option.value) ||
    emptyProductTypeEntry(option.value, option);

  // LPG: several product types. A type that a cargo already buys cannot be
  // removed until that cargo is changed or deleted.
  const handleProductTypesChange = selectedOptions => {
    const options = selectedOptions || [];
    const keptIds = options.map(option => option.value);
    const blocked = productTypeEntries.find(
      entry =>
        !keptIds.includes(entry.productTypeId) &&
        qtyDeliveryData.items.some(
          item => deliveryProductTypeOf(item) === entry.productTypeId
        )
    );
    if (blocked) {
      notify.warning(
        `${blocked.productTypeName || 'This product type'} is used by a cargo. Change or delete that cargo first.`
      );
      return;
    }
    applyProductTypeEntries(options.map(toProductTypeEntry));
  };

  const handleProductTypeChange = selectedOption => {
    applyProductTypeEntries(
      selectedOption ? [toProductTypeEntry(selectedOption)] : []
    );
  };

  // Edits one product type's composition. The composition components work on
  // a flat object, so they get the entry and a setForm that writes it back.
  const updateProductTypeEntry = (index, updater) => {
    setForm(prev => {
      const entries = [...(prev.procurementProductTypes || [])];
      if (!entries[index]) return prev;
      const current = {
        ...entries[index],
        isProcurementCompleted: prev.isProcurementCompleted,
      };
      const next = typeof updater === 'function' ? updater(current) : updater;
      // eslint-disable-next-line no-unused-vars
      const { isProcurementCompleted: _locked, ...entry } = next;
      entries[index] = { ...entries[index], ...entry };
      return {
        ...prev,
        procurementProductTypes: entries,
        ...(index === 0 ? primaryCompositionFields(entries[0]) : {}),
      };
    });
  };

  const handleSelectChange = (e, field) => {
    const value = e ? e.value : '';
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const [deliveryformData, setDeliveryFormData] = useState({
    procurementId: null,
    procurement: null,
    productTypeId: null,
    deliveryUnit: null,
    isDyed: true,
    isUndyed: true,
    frequency: null,
    ratioLpgId: null,
    ratioLpg: null,
    pricingPeriod: null,
    indexPriceId: null,
    indexPrice: null,
    otherIndexPriceId: null,
    otherIndexPrice: null,
    isCorrelationBetweenTwoIndex: null,
    startDatePublishIndex: null,
    endDatePublishIndex: null,
    schemaBL: null,
    infoPrompt: null,
    priceAmount: null,
    notesForPricing: null,
    id: null,
    cargoType: null,
  });

  // Product type a delivery term buys; terms saved before multi product type
  // belong to the primary type.
  const deliveryProductTypeOf = item =>
    item.productTypeId || form.productTypeId || null;

  // LPG keeps Frequency on the composition: per product type, and per cargo
  // type for the mixed types. Other products keep it on the delivery term.
  const deliveryFrequencyOf = item => {
    let value = item.frequency;
    if (isLpgForm) {
      const typeId = deliveryProductTypeOf(item);
      const entry = productTypeEntryFor(form, typeId);
      if (isMixProductType(typeId)) {
        value = frequencyFor(entry || form, item.cargoType);
      } else {
        value = entry ? entry.frequency : form.refButaneFrequency;
      }
    }
    return value === '' || value == null ? null : value;
  };

  // The open delivery term's product type and its composition.
  const deliveryEntry =
    productTypeEntryFor(form, deliveryformData.productTypeId) || form;
  // Only the mixed LPG types (Refrigerated / Pressurized Propane & Butane)
  // split a cargo by Cargo Type.
  const isDeliveryMixType =
    isLpgForm &&
    isMixProductType(deliveryformData.productTypeId || form.productTypeId);
  const cargoTypeOptions = CargoType[0].options.filter(opt => {
    if (opt.value === 1) return deliveryEntry.isFullCargoVLGC;
    if (opt.value === 2) return deliveryEntry.isHalfCargoVLGC;
    if (opt.value === 3) return deliveryEntry.isSemiRefrigerated;
    return true;
  });
  const productTypeLabel = entry =>
    entry.productTypeName ||
    productTypeData.items?.find(option => option.value === entry.productTypeId)
      ?.label ||
    'Product type';
  const deliveryProductTypeOptions = productTypeEntries.map(entry => ({
    value: entry.productTypeId,
    label: productTypeLabel(entry),
  }));

  const validateDeliveryProductType = () => {
    if (!isLpgForm || deliveryformData.productTypeId) return true;
    setErrors(prev => ({
      ...prev,
      deliveryProductTypeId: 'Please select the product type of this cargo',
    }));
    notify.warning(MESSAGES.mandatory);
    focusDeliveryField('deliveryProductTypeId');
    return false;
  };

  // Picking the cargo's product type decides which composition the quantity
  // options come from; mixed LPG types also need a cargo type first.
  const handleDeliveryProductTypeChange = option => {
    const productTypeId = option ? option.value : null;
    setDeliveryFormData(prev => ({ ...prev, productTypeId, cargoType: null }));
    setErrors(prev => ({
      ...prev,
      deliveryProductTypeId: null,
      cargoType: null,
    }));
    if (!productTypeId || isMixProductType(productTypeId)) {
      setQtyDeliveryDataTotalQty({ items: [] });
      setEditingQtyId(null);
      setQtyRowErrors({});
    } else {
      generateQtyDeliveryTotalQtyByCargoType(null, productTypeId);
    }
  };

  const handleDeliveryOpenModal = (deliveryid = null) => {
    fetchDeliveryDataDetail(deliveryid);
    //fetchDeliveryDataDetailQuantity(deliveryid);
    fetchDeliveryDataDetailIncoterm(deliveryid);
    deliverymodal();
  };

  // Quantity options of a delivery term, from the composition of the product
  // type it buys (and its cargo type for LPG products).
  const generateQtyDeliveryTotalQtyByCargoType = useCallback(
    (cargoType, productTypeId) => {
      setQtyDeliveryDataTotalQty({ items: [] });
      setEditingQtyId(null);
      setQtyRowErrors({});

      const typeId = productTypeId || form.productTypeId;
      const source = productTypeEntryFor(form, typeId) || form;

      if (isMixProductType(typeId)) {
        let butaneMin, propaneMin, butaneMax, propaneMax;
        if (cargoType === 1) {
          butaneMin = source.fullCargoVLGCRefrigrateButaneMin;
          propaneMin = source.fullCargoVLGCRefrigratePropaneMin;
          butaneMax = source.fullCargoVLGCRefrigrateButaneMax;
          propaneMax = source.fullCargoVLGCRefrigratePropaneMax;
        } else if (cargoType === 2) {
          butaneMin = source.halfCargoVLGCRefrigrateButaneMin;
          propaneMin = source.halfCargoVLGCRefrigratePropaneMin;
          butaneMax = source.halfCargoVLGCRefrigrateButaneMax;
          propaneMax = source.halfCargoVLGCRefrigratePropaneMax;
        } else if (cargoType === 3) {
          butaneMin = source.semiRefrigeratedRefrigrateButaneMin;
          propaneMin = source.semiRefrigeratedRefrigratePropaneMin;
          butaneMax = source.semiRefrigeratedRefrigrateButaneMax;
          propaneMax = source.semiRefrigeratedRefrigratePropaneMax;
        }

        // if (cargoType === 3) {
        //   setQtyDeliveryDataTotalQty({ items: [] });
        //   return;
        // }

        if (
          butaneMin != null &&
          propaneMin != null &&
          butaneMax != null &&
          propaneMax != null
        ) {
          // Min total, Min total + step, ... up to Max total; the step is set
          // per cargo type in the Composition section.
          const minTotal = Number(butaneMin) + Number(propaneMin);
          const maxTotal = Number(butaneMax) + Number(propaneMax);
          const step = quantityStepFor(source, cargoType);
          const items = [];
          for (
            let i = 0;
            minTotal + i * step <= maxTotal && i < MAX_QUANTITY_OPTIONS;
            i += 1
          ) {
            items.push({
              quantity: Math.round((minTotal + i * step) * 10000) / 10000,
              id: crypto.randomUUID(),
            });
          }
          setQtyDeliveryDataTotalQty({ items: sortQtyItemsAsc(items) });
        }
      } else {
        const compositionMin = source.compositionMin;
        const compositionMax = source.compositionMax;
        if (compositionMin != null && compositionMax != null) {
          // Min, Min + interval, ... up to Max; the interval is set on the
          // product type's Composition.
          const min = Number(compositionMin);
          const max = Number(compositionMax);
          const step = compositionQuantityStepFor(source);
          const items = [];
          for (
            let i = 0;
            min + i * step <= max && i < MAX_QUANTITY_OPTIONS;
            i += 1
          ) {
            items.push({
              quantity: Math.round((min + i * step) * 10000) / 10000,
              id: crypto.randomUUID(),
            });
          }
          setQtyDeliveryDataTotalQty({ items: sortQtyItemsAsc(items) });
        }
      }
    },
    [form, setQtyDeliveryDataTotalQty]
  );

  // Batas min/max quantity untuk delivery yang sedang aktif, dari komposisi
  // product type (dan cargo type untuk LPG mix) yang dipilih.
  const quantityBounds = quantityBoundsFor(
    form,
    deliveryformData.productTypeId,
    deliveryformData.cargoType
  );
  const quantityRangeMessage = bounds =>
    `Quantity must be between ${bounds.min} and ${bounds.max}.`;

  const handleDeliveryOpenModalNew = (_deliveryid = null) => {
    const presetProductTypeId = !isLpgForm
      ? form.productTypeId || null
      : productTypeEntries.length === 1
        ? productTypeEntries[0].productTypeId
        : null;
    setDeliveryFormData({
      procurementId: null,
      procurement: null,
      deliveryUnit: null,
      isDyed: true,
      isUndyed: true,
      frequency: null,
      ratioLpgId: null,
      ratioLpg: null,
      pricingPeriod: null,
      indexPriceId: null,
      indexPrice: null,
      otherIndexPriceId: null,
      otherIndexPrice: null,
      isCorrelationBetweenTwoIndex: null,
      startDatePublishIndex: null,
      endDatePublishIndex: null,
      schemaBL: null,
      infoPrompt: null,
      priceAmount: null,
      notesForPricing: null,
      id: null,
      cargoType: null,
      productTypeId: presetProductTypeId,
    });
    setQtyDeliveryDataIncoterm({ items: [] });
    setEditingQtyId(null);
    // Several LPG product types: the quantities wait for the product type
    // (and the cargo type for mixed types) to be chosen.
    if (!presetProductTypeId || isMixProductType(presetProductTypeId)) {
      setQtyDeliveryDataTotalQty({ items: [] });
    } else {
      generateQtyDeliveryTotalQtyByCargoType(
        form.cargoType,
        presetProductTypeId
      );
    }
    deliverymodal();
  };

  const toPublishIndexDate = value => {
    if (!value) return null;
    if (String(value).startsWith('1970-01-01')) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getTime() === 0) return null;
    return format(value, 'dd/MM/yyyy');
  };

  const fetchDeliveryDataDetail = async deliveryid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-delivery/${deliveryid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response;
      if (!data?.id) return;
      setDeliveryFormData({
        procurementId: data.procurementId || null,
        procurement: data.procurement || null,
        deliveryUnit: data.deliveryUnit || null,
        isDyed: data.isDyed !== false,
        isUndyed: data.isUndyed !== false,
        frequency: data.frequency || null,
        ratioLpgId: data.ratioLpgId || null,
        ratioLpg: data.ratioLpg || null,
        pricingPeriod: data.pricingPeriod || null,
        indexPriceId: data.indexPriceId || null,
        indexPrice: data.indexPrice || null,
        otherIndexPriceId: data.otherIndexPriceId || null,
        otherIndexPrice: data.otherIndexPrice || null,
        isCorrelationBetweenTwoIndex: data.isCorrelationBetweenTwoIndex || null,
        startDatePublishIndex: toPublishIndexDate(data.startDatePublishIndex),
        endDatePublishIndex: toPublishIndexDate(data.endDatePublishIndex),
        schemaBL: data.schemaBL || null,
        infoPrompt: data.infoPrompt || null,
        priceAmount: data.priceAmount || null,
        notesForPricing: data.notesForPricing || null,
        id: data.id || null,
        cargoType: data.cargoType || null,
        productTypeId: data.productTypeId || form.productTypeId || null,
      });
      fetchDeliveryDataDetailTotalQty(deliveryid);
    } catch (error) {
      logger.error('Error fetching data from Invoice API:', error);
    }
  };

  const selectedFieldsDeliveryFormData = {
    procurementId: form.id,
    productTypeId: deliveryformData.productTypeId || form.productTypeId || null,
    ratioLpgId: deliveryformData.ratioLpgId,
    deliveryUnit: deliveryformData.deliveryUnit,
    // Only gasoline asks for the alpha variants; other products keep the
    // stored default.
    isDyed: isGasolineForm ? deliveryformData.isDyed !== false : null,
    isUndyed: isGasolineForm ? deliveryformData.isUndyed !== false : null,
    frequency: deliveryformData.frequency,
    // Left empty until the user picks one, so a delivery saved through Add
    // Incoterm is not stored as Fixed Date without its publish index dates.
    pricingPeriod: deliveryformData?.pricingPeriod || null,
    indexPriceId: deliveryformData.indexPriceId,
    otherIndexPriceId: deliveryformData.otherIndexPriceId,
    isCorrelationBetweenTwoIndex: deliveryformData.isCorrelationBetweenTwoIndex,
    // startDatePublishIndex: deliveryformData?.startDatePublishIndex
    //   ? convertToInputDateFormat(deliveryformData.startDatePublishIndex)
    //   : null,
    startDatePublishIndex: getSafeInputDate(
      deliveryformData?.startDatePublishIndex,
      convertToInputDateFormat
    ),
    // endDatePublishIndex: convertToInputDateFormat(
    //   deliveryformData.endDatePublishIndex
    // ),
    endDatePublishIndex: getSafeInputDate(
      deliveryformData?.endDatePublishIndex,
      convertToInputDateFormat
    ),
    schemaBL: deliveryformData.schemaBL,
    infoPrompt: deliveryformData?.infoPrompt ?? 1,
    priceAmount: deliveryformData.priceAmount,
    notesForPricing: deliveryformData.notesForPricing,
    cargoType: isDeliveryMixType ? deliveryformData.cargoType : null,
    quantityInformations: qtyDeliveryDataTotalQty.items,
  };

  const handleDeleteQtyDelivery = async (id = null, subject) => {
    if (!(await confirmDelete('delivery term', subject))) return;
    await handleDelDataDelivery(id);
    // Refresh form.portRestriction agar group header & LocationIds jetty aktual,
    // lalu bump key supaya effect fetch ulang port restriction procurement.
    await onRefreshForm?.();
    setJettyRefreshKey(prev => prev + 1);
  };

  const handleDeleteIncotermDeliveryDetail = async (
    id = null,
    _deliveryid = null,
    _section = null,
    subject
  ) => {
    if (!(await confirmDelete('incoterm', subject))) return;
    await handleDelDataDeliveryIncotermDetail(id);
  };

  useEffect(() => {
    const newTabs = {};
    qtyDeliveryData.items.forEach(item => {
      newTabs[item.id] = `${item.id}1`;
    });
    setActiveTabs(newTabs);
  }, [qtyDeliveryData.items]);

  const [deliveryquantityformData, setDeliveryQuantityFormData] = useState({
    quantityDeliveryTermId: null,
    quantity: null,
    quantityLocation1: null,
    quantityLocation2: null,
    id: null,
  });

  const selectedFieldsDeliveryQuantityUpdData = {
    quantityDeliveryTermId: deliveryformData.id,
    quantity: deliveryquantityformData.quantity,
    quantityLocation1: deliveryquantityformData.quantityLocation1,
    quantityLocation2: deliveryquantityformData.quantityLocation2,
  };

  const fetchDeliveryDataDetailIncoterm = async deliveryid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-incoterm?QuantityDeliveryTermId=${deliveryid}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const items = response.items.map(item => ({
        incotermId: item.incotermId,
        incoterm: item.incoterm?.name ?? null,
        incotermPortType: item.incotermPortType,
        incotermLocation: item.incotermLocation?.location?.name ?? null,
        startDate: convertToDisplayDateFormatddmmmyyyy(item.startDate),
        endDate: convertToDisplayDateFormatddmmmyyyy(item.endDate),
        incotermLocation2: item.incotermLocation2?.location?.name ?? null,
        startDate2: convertToDisplayDateFormatddmmmyyyy(item.startDate2),
        endDate2: convertToDisplayDateFormatddmmmyyyy(item.endDate2),
        multiportOption: item.multiportOption,
        incotermFOBLocations: Array.isArray(item?.incotermFOBLocations)
          ? item.incotermFOBLocations
          : item?.incotermFOBLocations
            ? [item.incotermFOBLocations]
            : null,
        incotermLocation3: item.incotermLocation3?.location?.name ?? null,
        startDate3: convertToDisplayDateFormatddmmmyyyy(item.startDate3),
        endDate3: convertToDisplayDateFormatddmmmyyyy(item.endDate3),
        timelineType: item.timelineType,
        incotermRouteId: item.incotermRoute?.incotermId,
        id: item.id,
        routes: item.incotermRoute?.name ?? null,
      }));

      setQtyDeliveryDataIncoterm({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  const handleUpdDataDeliveryQuantity = async deliveryqtyid => {
    try {
      setIsLoading(true);
      await axios.put(
        `${api.API_URL}/quantity-information/${deliveryqtyid}`,
        selectedFieldsDeliveryQuantityUpdData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
      //fetchDeliveryDataDetailQuantity(deliveryformData.id);
    } catch (error) {
      logger.error('Error handleUpdDataDeliveryQuantity: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      LoadDefault();
    }
  };

  // Quantity rows only get sorted once the submit succeeds: sorting on every
  // keystroke reorders the list (and the row being edited) mid-entry.
  const sortQtyItemsAfterSubmit = () => {
    setQtyDeliveryDataTotalQty(prev => ({
      items: sortQtyItemsAsc(prev.items),
    }));
  };

  const handleAddQuantityRow = () => {
    const newId = crypto.randomUUID();
    setQtyDeliveryDataTotalQty(prev => ({
      items: [...prev.items, { quantity: '', id: newId }],
    }));
    setEditingQtyId(newId);
    setErrors(prev => ({ ...prev, quantity: null }));
  };

  const handleQuantityItemChange = (id, value) => {
    setQtyDeliveryDataTotalQty(prev => ({
      items: prev.items.map(item =>
        String(item.id) === String(id)
          ? { ...item, quantity: value === '' ? '' : Number(value) }
          : item
      ),
    }));

    // Inline validation: the value must stay within the min-max bounds.
    if (!quantityBounds || value === '' || value == null) {
      setQtyRowErrors(prev => ({ ...prev, [id]: null }));
      return;
    }
    const parsed = Number(value);
    if (
      Number.isNaN(parsed) ||
      parsed < quantityBounds.min ||
      parsed > quantityBounds.max
    ) {
      setQtyRowErrors(prev => ({
        ...prev,
        [id]: quantityRangeMessage(quantityBounds),
      }));
    } else {
      setQtyRowErrors(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleEditQuantityRow = id => {
    setEditingQtyId(id);
  };

  const handleDeleteQuantityItem = id => {
    setQtyDeliveryDataTotalQty(prev => ({
      items: prev.items.filter(item => String(item.id) !== String(id)),
    }));
    setQtyRowErrors(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setEditingQtyId(prev =>
      prev != null && String(prev) === String(id) ? null : prev
    );
  };

  const handleDelDataDelivery = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/quantity-delivery/${id}`, {
        withXSRFToken: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Data deleted successfully.');
    } catch (error) {
      logger.error('Error handleDelDataDelivery: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      LoadDefault();
    }
  };

  const handleSelectChangeIncoterm = (e, field) => {
    const value = e ? e.value : '';
    setIncotermErrors(prev => ({ ...prev, [field]: null }));

    setDeliveryIncotermFormData(prev => {
      const newData = { ...prev, [field]: value };

      if (field === 'incotermId') {
        newData.timelineType =
          value === 'e0173dfd-e109-4054-8640-23d95befd326' ? 'ALD' : 'ADD';
        newData.incotermRouteId = '';
        newData.incotermLocationId = '';
        newData.incotermLocation2Id = '';
        newData.incotermLocation3Id = '';
        //newData.incotermFOB = [];
      }

      // if (field === 'incotermRouteId') {
      //   newData.incotermFOB = [];
      // }

      if (field === 'incotermPortType') {
        if (value === 'SinglePort') {
          newData.incotermLocation2Id = null;
          newData.incotermLocation3Id = null;
        }
        if (value === '2Port') {
          newData.incotermLocation3Id = null;
        }
      }

      return newData;
    });

    if (field === 'incotermMultiPortType') {
      const setter =
        deliveryincotermformData.id === null
          ? setQtyDeliveryDataTotalQty
          : setQtyDeliveryDataQuantity;

      setter(prevQty => {
        const updatedItems = prevQty.items.map(item => {
          const qty = item.quantity;

          if (value === '2Port') {
            const location1 = Math.floor(qty / 2);
            const location2 = qty - location1;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: 0,
            };
          }

          if (value === '3Port') {
            const location1 = Math.floor(qty / 3);
            const location2 = Math.floor(qty / 3);
            const location3 = qty - location1 - location2;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          return item;
        });

        return { ...prevQty, items: updatedItems };
      });
    }
  };

  const handleDeliveryDateChangeIncoterm = e => {
    const { name, value } = e.target;
    setIncotermErrors(prev => ({ ...prev, [name]: null }));
    setDeliveryIncotermFormData(prev => ({
      ...prev,
      [name]: convertToDisplayDateFormat(value),
    }));
  };

  const handleDateChange =
    (fieldName, resetError = true) =>
    selectedDates => {
      setForm(prevForm => ({
        ...prevForm,
        [fieldName]: selectedDates[0] || '',
      }));

      if (resetError) {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: null,
        }));
      }
    };

  const handleDeliveryNumberChangeIncoterm = fieldName => e => {
    const input = e.target.value;
    if (isNumericOnly(input)) {
      setDeliveryIncotermFormData(prev => ({
        ...prev,
        [fieldName]: input === '' ? null : parseInt(input),
      }));
    }
  };

  const handleDeliveryTextChangeIncoterm = field => event => {
    setDeliveryIncotermFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const togglemodalquantityincoterm = () => {
    setModalQuantityIncoterm(!modalquantityincoterm);
  };

  const getRouteIdByIncoterm = (route = null, incotermId = null) => {
    if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd327') {
      return 'e0173dfd-e109-4054-8640-23d95befd328';
    } else if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd328') {
      return 'e0173dfd-e109-4054-8640-23d95befd329';
    } else if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd329') {
      return 'e0173dfd-e109-4054-8640-23d95befd330';
    } else if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd326') {
      return 'e0173dfd-e109-4054-8640-23d95befd326';
    } else if (route === 'e0173dfd-e109-4054-8640-23d95befd326') {
      return 'e0173dfd-e109-4054-8640-23d95befd326';
    } else if (route === 'e0173dfd-e109-4054-8640-23d95befd327') {
      return 'e0173dfd-e109-4054-8640-23d95befd327';
    } else {
      return incotermId; // default
    }
  };

  const fetchIncotermLocation = useCallback(
    async (route = null, routeid = null) => {
      // const strquery =
      //   id === null ? `IncotermRouteId=${routeid}` : `Description=${id}`;
      //const producttype = form.productNameId === "4588b5e6-7309-49d0-9107-59cbb96b6bd8" ? true : null;
      const isLpgCase = LPG_PRODUCT.includes(form.productNameId);
      const vars = getRouteIdByIncoterm(route, routeid);
      try {
        const response = await axios.get(
          `${api.API_URL}/incoterm/get-incoterm-location?IncotermRouteId=${vars}&PageNumber=1&PageSize=50&IsLpgCase=${isLpgCase}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setIncoTermLocationData({ items });
      } catch (error) {
        logger.error('Error:', error);
      } finally {
        //setIsLoadingIncotermLoc(false);
      }
    },
    [form.productNameId, token]
  );

  const handleQuantityChange = (index, field, value) => {
    setIncotermErrors(prev => ({ ...prev, [`${field}_${index}`]: null }));
    // Tentukan setter berdasarkan kondisi
    const setter =
      deliveryincotermformData.id === null
        ? setQtyDeliveryDataTotalQty
        : setQtyDeliveryDataQuantity;

    // Gunakan setter dengan functional update
    setter(prev => {
      // Pastikan prev memiliki properti items (array)
      const updatedItems = prev.items.map((item, i) => {
        if (i === index) {
          const val = value ? parseInt(value, 10) : 0;
          const quantityTotal = item.quantity;

          if (field === 'quantityLocation1') {
            let location1 = val;
            if (location1 > quantityTotal) {
              notify.warning(
                `Quantity Location1 cannot be more than ${quantityTotal}. Enter a lower value.`
              );
              return item;
            }
            const remainder = quantityTotal - location1;

            if (deliveryincotermformData.incotermMultiPortType === '2Port') {
              const rest = { ...item };
              delete rest.quantityLocation3;
              return {
                ...rest,
                quantityLocation1: location1,
                quantityLocation2: remainder,
              };
            }

            const location2 = Math.floor(remainder / 2);
            const location3 = remainder - location2;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          if (field === 'quantityLocation2') {
            let location2 = val;
            if (location2 > quantityTotal) {
              notify.warning(
                `Total Quantity cannot be more than ${quantityTotal}. Enter a lower value.`
              );
              return item;
            }
            const remainder = quantityTotal - location2;

            if (deliveryincotermformData.incotermMultiPortType === '2Port') {
              const rest = { ...item };
              delete rest.quantityLocation3;
              return {
                ...rest,
                quantityLocation1: remainder,
                quantityLocation2: location2,
              };
            }

            const location1 = Math.floor(remainder / 2);
            const location3 = remainder - location1;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          if (field === 'quantityLocation3') {
            if (deliveryincotermformData.incotermMultiPortType === '2Port') {
              return item;
            }
            let location3 = val;
            if (location3 > quantityTotal) {
              notify.warning(
                `Total Quantity cannot be more than ${quantityTotal}. Enter a lower value.`
              );
              return item;
            }
            const remainder = quantityTotal - location3;
            const location1 = Math.floor(remainder / 2);
            const location2 = remainder - location1;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          return item;
        }
        return item;
      });

      return { ...prev, items: updatedItems };
    });
  };

  // const [deliveryincotermformData, setDeliveryIncotermFormData] = useState({});
  const [deliveryincotermformData, setDeliveryIncotermFormData] = useState({
    quantityDeliveryTermId: null,
    incotermId: null,
    incotermPortType: null,
    incotermRouteId: null,
    incotermLocationId: null,
    incotermLocationName: '',
    incotermLocation2Name: '',
    incotermLocation2Id: null,
    incotermLocation3Name: '',
    incotermLocation3Id: null,
    incotermFOB: null,
    timelineType: null,
    startDate: '',
    endDate: '',
    timelineType2: null,
    startDate2: '',
    endDate2: '',
    timelineType3: null,
    startDate3: '',
    endDate3: '',
    alpha: null,
    loc1Draft1: '',
    loc1Draft2: '',
    loc1Loa1: '',
    loc1Loa2: '',
    loc1Dwt1: '',
    loc1Dwt2: '',
    loc1NotesForSTS: '',
    incotermMultiPortType: null,
    loc2Draft1: '',
    loc2Draft2: '',
    loc2Loa1: '',
    loc2Loa2: '',
    loc2Dwt1: '',
    loc2Dwt2: '',
    loc2NotesForSTS: '',
    loc3Draft1: '',
    loc3Draft2: '',
    loc3Loa1: '',
    loc3Loa2: '',
    loc3Dwt1: '',
    loc3Dwt2: '',
    loc3NotesForSTS: '',
    additionalInformation: '',
    quantityLocation1: '',
    quantityLocation2: '',
    quantityLocation3: '',
    id: null,
  });
  useEffect(() => {
    if (deliveryincotermformData.incotermRouteId) {
      fetchIncotermLocation(deliveryincotermformData.incotermRouteId, null);
    } else if (deliveryincotermformData.incotermId) {
      fetchIncotermLocation(null, deliveryincotermformData.incotermId);
    }
  }, [
    deliveryincotermformData.incotermId,
    deliveryincotermformData.incotermRouteId,
    fetchIncotermLocation,
  ]);

  const handleIncotermOpenModalNew = async deliveryid => {
    setDeliveryIncotermFormData({
      quantityDeliveryTermId: deliveryid ?? deliveryformData.id,
      incotermId: null,
      incotermPortType: null,
      incotermRouteId: null,
      incotermLocationId: null,
      incotermLocation2Id: null,
      incotermLocation3Id: null,
      incotermFOB: null,
      timelineType: null,
      startDate: null,
      endDate: null,
      timelineType2: null,
      startDate2: null,
      endDate2: null,
      timelineType3: null,
      startDate3: null,
      endDate3: null,

      alpha: null,
      loc1Draft1: null,
      loc1Draft2: null,
      loc1Loa1: null,
      loc1Loa2: null,
      loc1Dwt1: null,
      loc1Dwt2: null,
      loc1NotesForSTS: null,
      loc2Draft1: null,
      loc2Draft2: null,
      loc2Loa1: null,
      loc2Loa2: null,
      loc2Dwt1: null,
      loc2Dwt2: null,
      loc2NotesForSTS: null,

      loc3Draft1: null,
      loc3Draft2: null,
      loc3Loa1: null,
      loc3Loa2: null,
      loc3Dwt1: null,
      loc3Dwt2: null,
      loc3NotesForSTS: null,

      additionalInformation: null,
      id: null,
      quantity: null,
    });
    fetchDeliveryDataDetailTotalQty(deliveryid);
    togglemodalincoterm();
  };

  const selectedFieldsDeliveryIncotermFormData = {
    quantityDeliveryTermId: deliveryformData.id,
    incotermId: deliveryincotermformData?.incotermId ?? null,
    incotermPortType: deliveryincotermformData?.incotermPortType ?? null,
    incotermRouteId: deliveryincotermformData?.incotermRouteId ?? null,
    incotermLocationId: deliveryincotermformData?.incotermLocationId ?? null,
    incotermLocation2Id: deliveryincotermformData?.incotermLocation2Id ?? null,
    incotermLocation3Id: deliveryincotermformData?.incotermLocation3Id ?? null,

    incotermFOB: Array.isArray(deliveryincotermformData?.incotermFOB)
      ? deliveryincotermformData.incotermFOB
      : deliveryincotermformData?.incotermFOB
        ? [deliveryincotermformData.incotermFOB]
        : null,

    timelineType: deliveryincotermformData?.timelineType ?? null,
    startDate:
      convertToInputDateFormat(deliveryincotermformData?.startDate) ?? null,
    endDate:
      convertToInputDateFormat(deliveryincotermformData?.endDate) ?? null,
    timelineType2: deliveryincotermformData?.timelineType2 ?? null,
    startDate2:
      convertToInputDateFormat(deliveryincotermformData?.startDate2) ?? null,
    endDate2:
      convertToInputDateFormat(deliveryincotermformData?.endDate2) ?? null,

    timelineType3: deliveryincotermformData?.timelineType3 ?? null,
    startDate3:
      convertToInputDateFormat(deliveryincotermformData?.startDate3) ?? null,
    endDate3:
      convertToInputDateFormat(deliveryincotermformData?.endDate3) ?? null,

    alpha: deliveryincotermformData?.alpha ?? null,
    loc1Draft1: deliveryincotermformData?.loc1Draft1 ?? null,
    loc1Draft2: deliveryincotermformData?.loc1Draft2 ?? null,
    loc1Loa1: deliveryincotermformData?.loc1Loa1 ?? null,
    loc1Loa2: deliveryincotermformData?.loc1Loa2 ?? null,
    loc1Dwt1: deliveryincotermformData?.loc1Dwt1 ?? null,
    loc1Dwt2: deliveryincotermformData?.loc1Dwt2 ?? null,
    loc1NotesForSTS: deliveryincotermformData?.loc1NotesForSTS ?? null,
    loc2Draft1: deliveryincotermformData?.loc2Draft1 ?? null,
    loc2Draft2: deliveryincotermformData?.loc2Draft2 ?? null,
    loc2Loa1: deliveryincotermformData?.loc2Loa1 ?? null,
    loc2Loa2: deliveryincotermformData?.loc2Loa2 ?? null,
    loc2Dwt1: deliveryincotermformData?.loc2Dwt1 ?? null,
    loc2Dwt2: deliveryincotermformData?.loc2Dwt2 ?? null,
    loc2NotesForSTS: deliveryincotermformData?.loc2NotesForSTS ?? null,

    loc3Draft1: deliveryincotermformData?.loc3Draft1 ?? null,
    loc3Draft2: deliveryincotermformData?.loc3Draft2 ?? null,
    loc3Loa1: deliveryincotermformData?.loc3Loa1 ?? null,
    loc3Loa2: deliveryincotermformData?.loc3Loa2 ?? null,
    loc3Dwt1: deliveryincotermformData?.loc3Dwt1 ?? null,
    loc3Dwt2: deliveryincotermformData?.loc3Dwt2 ?? null,
    loc3NotesForSTS: deliveryincotermformData?.loc3NotesForSTS ?? null,
    multiportOption: deliveryincotermformData?.incotermMultiPortType ?? null,

    additionalInformation:
      deliveryincotermformData?.additionalInformation ?? null,

    quantity:
      deliveryincotermformData.id === null
        ? qtyDeliveryDataTotalQty.items
        : qtyDeliveryDataQuantity.items,
  };

  const parseIncotermDate = d => {
    if (!d) return null;
    const parts = d.split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(Number);
    return new Date(year, month - 1, day);
  };

  const validateIncoterm = () => {
    const tempErrors = {};
    if (!deliveryincotermformData.incotermId) {
      tempErrors.incotermId = 'Please select Incoterm Type';
    }
    if (
      deliveryincotermformData.incotermId !==
        'e0173dfd-e109-4054-8640-23d95befd326' &&
      !deliveryincotermformData.incotermPortType
    ) {
      tempErrors.incotermPortType = 'Please select Port(s)';
    }
    if (
      deliveryincotermformData.incotermId ===
        'e0173dfd-e109-4054-8640-23d95befd326' &&
      !deliveryincotermformData.incotermRouteId
    ) {
      tempErrors.incotermRouteId = 'Please select Route';
    }

    const isALD =
      deliveryincotermformData.incotermId ===
      'e0173dfd-e109-4054-8640-23d95befd326';
    const isMultiPort =
      deliveryincotermformData.incotermPortType === 'MultiPort';
    const is2Port = deliveryincotermformData.incotermMultiPortType === '2Port';
    const is3Port = deliveryincotermformData.incotermMultiPortType === '3Port';

    const needsLocation1 = is2Port || is3Port;
    const needsLocation2 = is2Port || is3Port;
    const needsLocation3 = is3Port;

    const qtyItems =
      deliveryincotermformData.id === null
        ? qtyDeliveryDataTotalQty.items
        : qtyDeliveryDataQuantity.items;

    if (needsLocation1) {
      qtyItems.forEach((item, idx) => {
        if (!item.quantityLocation1 && item.quantityLocation1 !== 0) {
          tempErrors[`quantityLocation1_${idx}`] =
            `Row ${idx + 1}: Location 1 quantity is required`;
        }
      });
    }
    if (needsLocation2) {
      qtyItems.forEach((item, idx) => {
        if (!item.quantityLocation2 && item.quantityLocation2 !== 0) {
          tempErrors[`quantityLocation2_${idx}`] =
            `Row ${idx + 1}: Location 2 quantity is required`;
        }
      });
    }
    if (needsLocation3) {
      qtyItems.forEach((item, idx) => {
        if (!item.quantityLocation3 && item.quantityLocation3 !== 0) {
          tempErrors[`quantityLocation3_${idx}`] =
            `Row ${idx + 1}: Location 3 quantity is required`;
        }
      });
    }

    if (!isALD && deliveryincotermformData.incotermId) {
      if (!deliveryincotermformData.incotermLocationId) {
        tempErrors.incotermLocationId = 'Location is required';
      }
    }
    if (
      (is2Port || is3Port) &&
      !isALD &&
      isMultiPort &&
      !deliveryincotermformData.incotermLocation2Id
    ) {
      tempErrors.incotermLocation2Id = 'Location 2 is required';
    }
    if (isALD && isMultiPort) {
      const loc2 = deliveryincotermformData.incotermLocation2Id;
      const isEmpty = Array.isArray(loc2) ? loc2.length === 0 : !loc2;
      if (isEmpty) {
        tempErrors.incotermLocation2Id = 'Location 2 is required';
      }
    }

    if (
      is3Port &&
      !isALD &&
      isMultiPort &&
      !deliveryincotermformData.incotermLocation3Id
    ) {
      tempErrors.incotermLocation3Id = 'Location 3 is required';
    }

    if (!deliveryincotermformData.startDate) {
      tempErrors.startDate = 'Date From is required';
    }
    if (!deliveryincotermformData.endDate) {
      tempErrors.endDate = 'Date To is required';
    }
    if (needsLocation1) {
      if (!deliveryincotermformData.startDate2) {
        tempErrors.startDate2 = 'Date From is required';
      }
      if (!deliveryincotermformData.endDate2) {
        tempErrors.endDate2 = 'Date To is required';
      }
    }
    if (needsLocation3) {
      if (!deliveryincotermformData.startDate3) {
        tempErrors.startDate3 = 'Date From is required';
      }
      if (!deliveryincotermformData.endDate3) {
        tempErrors.endDate3 = 'Date To is required';
      }
    }

    const s1 = parseIncotermDate(deliveryincotermformData.startDate);
    const e1 = parseIncotermDate(deliveryincotermformData.endDate);
    if (s1 && e1 && s1 >= e1) {
      tempErrors.endDate = 'Date To must be after Date From';
    }
    const s2 = parseIncotermDate(deliveryincotermformData.startDate2);
    const e2 = parseIncotermDate(deliveryincotermformData.endDate2);
    if (s2 && e2 && s2 >= e2) {
      tempErrors.endDate2 = 'Date To must be after Date From';
    }
    const s3 = parseIncotermDate(deliveryincotermformData.startDate3);
    const e3 = parseIncotermDate(deliveryincotermformData.endDate3);
    if (s3 && e3 && s3 >= e3) {
      tempErrors.endDate3 = 'Date To must be after Date From';
    }

    // Multiport discharge windows are anchored on Port 1: Port 2 and Port 3
    // each start after Port 1's Date From. Windows may overlap, and Port 2
    // and Port 3 are not ordered against each other.
    const afterPort1 = (key, start) => {
      if (s1 && start && start <= s1) {
        tempErrors[key] ??= 'Date From must be after Port 1 Date From';
      }
    };
    if (isMultiPort && (is2Port || is3Port)) afterPort1('startDate2', s2);
    if (isMultiPort && is3Port) afterPort1('startDate3', s3);

    if (Object.keys(tempErrors).length !== 0) {
      notify.warning(MESSAGES.mandatory);
    }
    setIncotermErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const rejectIncotermLocationLimit = () => {
    notify.warning(
      'The maximum number of locations has been reached. Remove one before adding another.'
    );
    return false;
  };

  // Batas 3 lokasi, dengan aturan berbeda per jenis incoterm.
  const validateIncotermLocationLimit = () => {
    // Form FOB: jumlah lokasi yang dipilih sendiri tidak dibatasi, tapi tetap
    // ditolak kalau lokasi non-FOB yang sudah tersimpan melebihi 3.
    if (deliveryincotermformData.incotermId === FOB_INCOTERM_ID) {
      if (incotermLocationTotals.location <= MAX_INCOTERM_LOCATIONS)
        return true;
      return rejectIncotermLocationLimit();
    }

    // Form non-FOB: hanya diperiksa saat lokasi FOB tersimpan lebih dari satu.
    if (incotermLocationTotals.fob <= 1) return true;

    const totalLocations =
      incotermLocationTotals.location +
      countFormIncotermLocations(deliveryincotermformData);

    if (totalLocations <= MAX_INCOTERM_LOCATIONS) return true;

    return rejectIncotermLocationLimit();
  };

  const handleAddDataDeliveryIncoterm = () => {
    if (!validateIncoterm()) return;
    if (!validateIncotermLocationLimit()) return;
    setModalIncoterm(!modalincoterm);
    return handleAddedDataDeliveryIncoterm();
  };

  const handleAddedDataDeliveryIncoterm = async _e => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${api.API_URL}/quantity-incoterm`,
        selectedFieldsDeliveryIncotermFormData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchDeliveryDataDetail(response.id);
      notify.success(MESSAGES.saved);
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      fetchDataDeliveryIncoterm(response.id);
      //quantityTableRef.current?.refresh(deliveryformData.id);
    } catch (error) {
      notify.apiError(error, 'save the delivery incoterm');

      setError(error.message);
    } finally {
      setIsLoading(false);
      handleSaveDraftProc(); // tetap jalan
      LoadDefault();
    }
  };

  const handleUpdDataDeliveryIncoterm = async _e => {
    try {
      setIsLoading(true);
      await axios.put(
        `${api.API_URL}/quantity-incoterm/${deliveryincotermformData.id}`,
        selectedFieldsDeliveryIncotermFormData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      fetchDataDeliveryIncoterm(deliveryincotermformData.id);
    } catch (error) {
      logger.error('Error handleUpdDataDeliveryIncoterm: ', error);

      setError(error.message);
    } finally {
      setIsLoading(false);
      handleSaveDraftProc();
      LoadDefault();
    }
  };

  const fetchDataDeliveryIncoterm = async Id => {
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-incoterm/${Id}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response;
      if (!data) {
        logger.warn('Data tidak ditemukan dari API');
        return null;
      }

      setDeliveryIncotermFormData({
        quantityDeliveryTermId: data.quantityDeliveryTermId || null,
        incotermId: data.incoterm.id || null,
        incotermPortType: data.incotermPortType || null,
        incotermRouteId: data.incotermRoute?.id || null,

        incotermLocationId: data.incotermLocation?.id || null,
        incotermLocationName: data.incotermLocation?.name || null,
        incotermLocation2Name: data.incotermLocation2?.name || null,
        incotermLocation2Id: data.incotermLocation2?.id || null,
        incotermLocation3Name: data.incotermLocation3?.name || null,
        incotermLocation3Id: data.incotermLocation3?.id || null,

        incotermFOB: Array.isArray(data.incotermFOBLocations)
          ? data.incotermFOBLocations.map(loc => loc.id)
          : null,
        timelineType: data.timelineType || null,
        startDate: convertDisplayddMMyyyy(data.startDate) || null,
        endDate: convertDisplayddMMyyyy(data.endDate) || null,
        timelineType2: data.timelineType2 || null,
        startDate2: convertDisplayddMMyyyy(data.startDate2) || null,
        endDate2: convertDisplayddMMyyyy(data.endDate2) || null,
        timelineType3: data.timelineType3 || null,
        startDate3: convertDisplayddMMyyyy(data.startDate3) || null,
        endDate3: convertDisplayddMMyyyy(data.endDate3) || null,
        alpha: data.alpha || null,
        loc1Draft1: data.loc1Draft1 || null,
        loc1Draft2: data.loc1Draft2 || null,
        loc1Loa1: data.loc1Loa1 || null,
        loc1Loa2: data.loc1Loa2 || null,
        loc1Dwt1: data.loc1Dwt1 || null,
        loc1Dwt2: data.loc1Dwt2 || null,
        loc1NotesForSTS: data.loc1NotesForSTS || null,
        incotermMultiPortType: data.multiportOption || null,

        loc2Draft1: data.loc2Draft1 || null,
        loc2Draft2: data.loc2Draft2 || null,
        loc2Loa1: data.loc2Loa1 || null,
        loc2Loa2: data.loc2Loa2 || null,
        loc2Dwt1: data.loc2Dwt1 || null,
        loc2Dwt2: data.loc2Dwt2 || null,
        loc2NotesForSTS: data.loc2NotesForSTS || null,

        loc3Draft1: data.loc3Draft1 || null,
        loc3Draft2: data.loc3Draft2 || null,
        loc3Loa1: data.loc3Loa1 || null,
        loc3Loa2: data.loc3Loa2 || null,
        loc3Dwt1: data.loc3Dwt1 || null,
        loc3Dwt2: data.loc3Dwt2 || null,
        loc3NotesForSTS: data.loc3NotesForSTS || null,
        additionalInformation: data.additionalInformation || null,
        quantityLocation1:
          data.quantityInformationDetail?.[0]?.quantityLocation1 ?? null,
        quantityLocation2:
          data.quantityInformationDetail?.[0]?.quantityLocation2 ?? null,
        quantityLocation3:
          data.quantityInformationDetail?.[0]?.quantityLocation3 ?? null,
        id: data.id || null,
      });

      if (deliveryincotermformData.id === null) {
        const quantitiesArray = {
          items: sortQtyItemsAsc(
            data.quantityInformationDetail.map(item => ({
              quantityInformationId: item.quantityInformationId,
              quantity: item.qty,
              quantityLocation1: item.quantityLocation1 ?? '',
              quantityLocation2: item.quantityLocation2 ?? '',
              quantityLocation3: item.quantityLocation3 ?? '',
              id: item.quantityInformationId,
            }))
          ),
        };
        setQtyDeliveryDataTotalQty(quantitiesArray);
        setEditingQtyId(null);
      } else {
        const quantityItems = data.quantityInformationDetail
          .map(item => ({
            quantityInformationId: item.quantityInformationId ?? null,
            id: item.id ?? null,
            quantity: item.qty ?? null,
            quantityLocation1: item.quantityLocation1 ?? null,
            quantityLocation2: item.quantityLocation2 ?? null,
            quantityLocation3: item.quantityLocation3 ?? null,
          }))
          .sort((a, b) => a.quantity - b.quantity);

        logger.log('hasil aman', quantityItems);
        setQtyDeliveryDataQuantity({ items: quantityItems });
      }
      return data;
    } catch (error) {
      logger.error('Error fetching data from Invoice API:', error);
      return null;
    }
  };

  const handleDelDataDeliveryIncotermDetail = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/quantity-incoterm/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Data deleted successfully.');
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
    } catch (error) {
      logger.error('Error handleDelDataDeliveryIncotermDetail: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      LoadDefault();
    }
  };

  const handleIncotermOpenModal = async (
    Id = null,
    _IncotermId = null,
    _Section = null
  ) => {
    await fetchDataDeliveryIncoterm(Id);
    togglemodalincoterm();
  };

  const handleViewOrigin = (locations, routes = 'Origin') => {
    setViewOriginLocations(locations || []);
    setViewOriginRoutes(routes);
    setViewOriginModal(true);
  };

  const handleClauseCheckBoxChange = e => {
    const { name, checked } = e.target;
    const itemId = name.replace('isShowToDMUT_', '');
    const originalItem =
      dataCause?.items?.find(i => String(i.id) === String(itemId)) || {};
    const val = checked ? 1 : 0;
    setDataCause(dataCause => {
      const updatedItem = {
        ...originalItem,
        id: String(itemId),
        clauseDetailId: originalItem.clauseDetailId ?? null,
        clauseDescription:
          dataCause.procurementClauseDetails?.find(
            d => String(d.id) === String(itemId)
          )?.clauseDescription ??
          originalItem.clauseDescription ??
          '',
        isShowToDMUT: val,
      };
      const prev = dataCause.procurementClauseDetails ?? dataCause?.items ?? [];
      const idx = prev.findIndex(i => String(i.id) === String(itemId));
      const procurementClauseDetails =
        idx >= 0
          ? prev.map((i, n) => (n === idx ? { ...i, ...updatedItem } : i))
          : [...prev, updatedItem];
      const rest = { ...dataCause };
      delete rest[itemId];
      delete rest[name];
      return {
        ...rest,
        procurementClauseDetails,
      };
    });
  };

  // Active/Inactive applies to this procurement only and is saved with the
  // other clause edits (Save Draft / Complete). Inactive clauses drop out of
  // the list; "Add clause index" brings them back.
  const handleClauseActiveChange = async (item, isActive) => {
    if (
      !isActive &&
      !(await confirmAction({
        tone: 'warning',
        title: 'Inactivate this clause?',
        message:
          'The clause is removed from the Requirement tab and suppliers will not see it. You can activate it again with "Add clause index". Save the draft to keep the change.',
        subject: item.clauseTitle,
        confirmLabel: 'Inactivate clause',
      }))
    ) {
      return;
    }
    setDataCause(prevCause => {
      const prev = prevCause.procurementClauseDetails ?? prevCause?.items ?? [];
      const idx = prev.findIndex(i => String(i.id) === String(item.id));
      const base = idx >= 0 ? prev[idx] : item;
      const updatedItem = {
        ...base,
        id: String(item.id),
        clauseDetailId: base.clauseDetailId ?? null,
        clauseDescription: base.clauseDescription ?? '',
        isShowToDMUT: base.isShowToDMUT ?? null,
        isActive,
      };
      return {
        ...prevCause,
        procurementClauseDetails:
          idx >= 0
            ? prev.map((i, n) => (n === idx ? updatedItem : i))
            : [...prev, updatedItem],
      };
    });
    if (!isActive) {
      setErrors(prev => ({ ...prev, [item.id]: '' }));
    }
  };

  // Inactive clauses picked in "Add clause index"; saved with Save Draft like
  // the switch itself.
  const handleClausesReactivated = items => {
    items.forEach(item => handleClauseActiveChange(item, true));
    setExpandedClauses(prev => ({
      ...prev,
      ...Object.fromEntries(items.map(item => [item.id, true])),
    }));
  };

  const [addClauseOpen, setAddClauseOpen] = useState(false);
  const [templateModalClause, setTemplateModalClause] = useState(null);

  // Rows come back already saved by POST /procurement/{id}/clause-detail.
  const handleClausesAdded = addedRows => {
    const added = addedRows.filter(Boolean).map(toClauseItem);
    if (added.length === 0) return;
    setDataCause(prevCause => ({
      ...prevCause,
      items: [...(prevCause.items || []), ...added].sort(
        (a, b) => (a.ordreNumber ?? 0) - (b.ordreNumber ?? 0)
      ),
    }));
    setExpandedClauses(prev => ({
      ...prev,
      ...Object.fromEntries(added.map(item => [item.id, true])),
    }));
    added.forEach(item => {
      if (item.clauseDetailId) {
        fetchTemplateByClauseDetailId(item.clauseDetailId);
      }
    });
  };

  const handleClauseTemplateSaved = template => {
    const clauseDetailId = templateModalClause?.clauseDetailId;
    if (!template?.id || !clauseDetailId) return;
    setTemplatesByClauseDetailId(prev => ({
      ...prev,
      [clauseDetailId]: [...(prev[clauseDetailId] || []), template],
    }));
  };

  const fetchDeliveryDataDetailTotalQty = async id => {
    try {
      const response = await fetch(
        `${api.API_URL}/quantity-information?QuantityDeliveryTermId=${id}&SortBy=Quantity&IsDescending=false`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      const items = result.data.items.map(item => ({
        quantityDeliveryTermId: item.quantityDeliveryTermId,
        quantityInformationId: item.id,
        quantity: item.quantity,
        quantityIncotermId: item.quantityIncotermId,
        ids: item.id,
        quantityLocation1: '',
        quantityLocation2: '',
        quantityLocation3: '',
        id: item.id || crypto.randomUUID(),
        // Math.random().toString(36).substr(2, 9), // fallback ID
      }));
      setQtyDeliveryDataTotalQty({ items: sortQtyItemsAsc(items) });
      setEditingQtyId(null);
      setQtyRowErrors({});
    } catch (error) {
      // Handle error silently or add logging as needed
      logger.error('Error fetchDeliveryDataDetailTotalQty: ', error);
    }
  };

  const [activeSection, setActiveSection] = useState('req-sec-contract');
  const [expandedClauses, setExpandedClauses] = useState({});

  const fetchTemplateByClauseDetailId = async clauseDetailId => {
    if (templatesByClauseDetailId[clauseDetailId]) return;
    try {
      const response = await axios.get(
        `${api.API_URL}/template-procurement-document?PageNumber=1&PageSize=50&ClauseDetailId=${clauseDetailId}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTemplatesByClauseDetailId(prev => ({
        ...prev,
        [clauseDetailId]: response.items || [],
      }));
    } catch (error) {
      logger.error('Error fetchTemplateByClauseDetailId:', error);
    }
  };

  const toggleClauseCard = id => {
    setExpandedClauses(prev => {
      const next = !prev[id];
      if (next) {
        const clauseItem = dataCause.items?.find(item => item.id === id);
        if (
          clauseItem?.clauseDetailId &&
          !templatesByClauseDetailId[clauseItem.clauseDetailId]
        ) {
          fetchTemplateByClauseDetailId(clauseItem.clauseDetailId);
        }
      }
      return { ...prev, [id]: next };
    });
  };

  useEffect(() => {
    if (isLoading) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-120px 0px -55% 0px' }
    );
    REQ_SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isLoading]);

  const scrollToSection = id => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- incoterm modal ----
  const isFobIncoterm = deliveryincotermformData.incotermId === FOB_INCOTERM_ID;
  const incotermPortCount =
    deliveryincotermformData.incotermPortType === 'SinglePort'
      ? 1
      : deliveryincotermformData.incotermPortType === 'MultiPort'
        ? deliveryincotermformData.incotermMultiPortType === '3Port'
          ? 3
          : deliveryincotermformData.incotermMultiPortType === '2Port'
            ? 2
            : 0
        : 0;
  const isIncotermSplit =
    !isFobIncoterm &&
    incotermPortCount >= 2 &&
    QTY_SPLIT_INCOTERMS.includes(deliveryincotermformData.incotermId);
  const incotermQtyRows =
    (deliveryincotermformData.id === null
      ? qtyDeliveryDataTotalQty.items
      : qtyDeliveryDataQuantity.items) || [];
  const incotermQtyTotal = incotermQtyRows.reduce(
    (sum, row) => sum + (Number(row.quantity) || 0),
    0
  );
  const incotermErrorList = Object.entries(incotermErrors || {}).filter(
    ([, message]) => Boolean(message)
  );

  // After a save click that fails validation, jump to the first bad field.
  const incotermFocusRef = useRef(false);
  useEffect(() => {
    if (!incotermFocusRef.current) return;
    const keys = Object.entries(incotermErrors || {})
      .filter(([, message]) => Boolean(message))
      .map(([key]) => key);
    if (keys.length === 0) return;
    incotermFocusRef.current = false;
    window.setTimeout(() => focusFirstField(keys), 60);
  }, [incotermErrors]);

  // One control for "how many ports": maps to the two fields the API expects.
  const setIncotermPortSetup = count => {
    if (count === 1) {
      handleSelectChangeIncoterm({ value: 'SinglePort' }, 'incotermPortType');
      setDeliveryIncotermFormData(prev => ({
        ...prev,
        incotermMultiPortType: null,
      }));
      return;
    }
    handleSelectChangeIncoterm({ value: 'MultiPort' }, 'incotermPortType');
    handleSelectChangeIncoterm(
      { value: count === 3 ? '3Port' : '2Port' },
      'incotermMultiPortType'
    );
  };

  // Earliest pickable day, the day after a dd/MM/yyyy date (input format).
  const dayAfterInput = d => {
    const date = parseIncotermDate(d);
    return date ? format(addDays(date, 1), 'yyyy-MM-dd') : undefined;
  };

  const renderIncotermDates = (suffix, label, required) => {
    const startKey = `startDate${suffix}`;
    const endKey = `endDate${suffix}`;
    // Multiport: Port 2 and Port 3 start after Port 1's Date From (see
    // validateIncoterm).
    const anchorKey =
      deliveryincotermformData.incotermPortType === 'MultiPort' &&
      (suffix === '2' || suffix === '3')
        ? 'startDate'
        : null;
    const startMin = anchorKey
      ? dayAfterInput(deliveryincotermformData[anchorKey])
      : undefined;
    const endMin = dayAfterInput(deliveryincotermformData[startKey]);
    return (
      <>
        <Label for={startKey} className='form-label'>
          {label}
          {required && <span className='text-danger'> *</span>}
        </Label>
        <div className='pd-date-range'>
          <div>
            <Input
              type='date'
              id={startKey}
              name={startKey}
              aria-label={`${label} from`}
              className='form-control'
              invalid={Boolean(incotermErrors[startKey])}
              min={startMin}
              value={convertToInputDateFormat(
                deliveryincotermformData[startKey]
              )}
              onChange={handleDeliveryDateChangeIncoterm}
            />
            {incotermErrors[startKey] && (
              <div className='text-danger d-flex align-items-center gap-1 mt-1 fs-12'>
                <i className='ri-error-warning-line'></i>
                <span>{incotermErrors[startKey]}</span>
              </div>
            )}
          </div>
          <span className='pt-2 text-muted fs-12'>to</span>
          <div>
            <Input
              type='date'
              id={endKey}
              name={endKey}
              aria-label={`${label} to`}
              className='form-control'
              invalid={Boolean(incotermErrors[endKey])}
              min={endMin}
              value={convertToInputDateFormat(deliveryincotermformData[endKey])}
              onChange={handleDeliveryDateChangeIncoterm}
            />
            {incotermErrors[endKey] && (
              <div className='text-danger d-flex align-items-center gap-1 mt-1 fs-12'>
                <i className='ri-error-warning-line'></i>
                <span>{incotermErrors[endKey]}</span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ---- grouped layout: what is still empty, which cards are open ----
  const isLocked = form.isProcurementCompleted !== false;
  const isLpgProduct = LPG_PRODUCT.includes(form.productNameId);
  const isTermContract = parseInt(form.contractType, 10) === 1;

  const missingBySection = useMemo(() => {
    const missing = {};
    const add = (sectionId, isEmpty) => {
      if (isEmpty) missing[sectionId] = (missing[sectionId] || 0) + 1;
    };
    add('req-sec-contract', isBlank(form.contractType));
    add('req-sec-contract', isBlank(form.contractPeriodTermStart));
    add(
      'req-sec-contract',
      isTermContract && isBlank(form.contractPeriodTermEnd)
    );
    add('req-sec-product', isBlank(form.productNameId));
    add('req-sec-product', isBlank(form.productTypeId));
    add('req-sec-spec', isBlank(form.productSpecification));
    (dataCause.items || []).forEach(item => {
      if (clauseValue(dataCause, item, 'isActive') === false) return;
      add(
        'req-sec-clause',
        isBlank(clauseValue(dataCause, item, 'clauseDescription'))
      );
    });
    add('req-sec-payment', isBlank(form.descriptionOfPaymentTerms));
    add('req-sec-other', isBlank(form.otherTermsAndCondition));
    add('req-sec-validity', isBlank(form.proposalSubmissionDeadline));
    add('req-sec-validity', isBlank(form.proposalValidityPeriodMax));
    return missing;
  }, [form, dataCause, isTermContract]);

  const missingByGroup = useMemo(
    () =>
      Object.fromEntries(
        REQ_GROUPS.map(group => [
          group.key,
          group.sections.reduce(
            (sum, section) => sum + (missingBySection[section.id] || 0),
            0
          ),
        ])
      ),
    [missingBySection]
  );
  const totalMissing = Object.values(missingByGroup).reduce(
    (sum, count) => sum + count,
    0
  );

  const [openGroups, setOpenGroups] = useState({ contract: true });
  const groupsInitialised = useRef(false);
  useEffect(() => {
    if (groupsInitialised.current || isLoading || !form.id) return;
    groupsInitialised.current = true;
    const next = { contract: true };
    if (!isLocked) {
      REQ_GROUPS.forEach(group => {
        if (missingByGroup[group.key] > 0) next[group.key] = true;
      });
    }
    setOpenGroups(next);
  }, [isLoading, form.id, isLocked, missingByGroup]);

  // A failed save opens every card that holds an error and, right after a
  // save click, puts the cursor in the first field that needs fixing.
  const focusOnErrors = useRef(false);
  useEffect(() => {
    const errorKeys = Object.entries(errors || {})
      .filter(([, message]) => Boolean(message))
      .map(([key]) => key);
    if (errorKeys.length === 0) return;
    setOpenGroups(prev => {
      const next = { ...prev };
      errorKeys.forEach(key => {
        next[errorKeyToGroup(key)] = true;
      });
      return next;
    });
    if (focusOnErrors.current) {
      focusOnErrors.current = false;
      // wait one tick so the cards that were just opened are laid out
      window.setTimeout(
        () => focusFirstField(errorKeys.map(requirementFieldId)),
        60
      );
    }
  }, [errors]);

  const toggleGroup = key =>
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  const allGroupsOpen = REQ_GROUPS.every(group => openGroups[group.key]);
  const setAllGroups = open =>
    setOpenGroups(
      Object.fromEntries(REQ_GROUPS.map(group => [group.key, open]))
    );

  const goToSection = (groupKey, sectionId) => {
    setOpenGroups(prev => ({ ...prev, [groupKey]: true }));
    setActiveSection(sectionId);
    // wait for the card to open before scrolling to the section
    window.requestAnimationFrame(() => scrollToSection(sectionId));
  };

  const formatMonth = value => {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date) ? null : format(date, 'MMM yyyy');
  };
  const labelOf = (items, id) => items?.find(obj => obj.value === id)?.label;
  const portCount = groupedJettyRestrictions.reduce(
    (sum, group) => sum + group.items.length,
    0
  );
  const activeClauses = (dataCause.items || []).filter(
    item => clauseValue(dataCause, item, 'isActive') !== false
  );
  const mandatoryClauseCount = activeClauses.filter(item =>
    Boolean(clauseValue(dataCause, item, 'isShowToDMUT'))
  ).length;
  const inactiveClauses = (dataCause.items || []).filter(
    item => clauseValue(dataCause, item, 'isActive') === false
  );
  const inactiveClauseCount = inactiveClauses.length;
  const deliveryCount = qtyDeliveryData.items.length;

  const groupSummary = {
    contract: [
      form.contractType
        ? `${isTermContract ? 'TERM' : 'SPOT'}${
            formatMonth(form.contractPeriodTermStart)
              ? ` · ${[
                  formatMonth(form.contractPeriodTermStart),
                  isTermContract && formatMonth(form.contractPeriodTermEnd),
                ]
                  .filter(Boolean)
                  .join(' – ')}`
              : ''
          }`
        : 'Contract type not set',
      labelOf(productName.items, form.productNameId),
      labelOf(productTypeData.items, form.productTypeId),
      form.operationalTolerance ? `±${form.operationalTolerance}%` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    delivery: `${deliveryCount} ${
      deliveryCount === 1 ? 'delivery' : 'deliveries'
    } · ${portCount} port ${portCount === 1 ? 'restriction' : 'restrictions'}`,
    terms: [
      `${activeClauses.length} active ${
        activeClauses.length === 1 ? 'clause' : 'clauses'
      }${mandatoryClauseCount ? ` (${mandatoryClauseCount} mandatory)` : ''}${
        inactiveClauseCount ? ` · ${inactiveClauseCount} inactive` : ''
      }`,
      labelOf(lawData.items, form.lawId),
      labelOf(abitrationData.items, form.arbitrationId),
    ]
      .filter(Boolean)
      .join(' · '),
    validity: form.proposalSubmissionDeadline
      ? `Submission deadline ${format(
          new Date(form.proposalSubmissionDeadline),
          'dd MMM yyyy, HH:mm'
        )} WIB`
      : 'Submission deadline not set',
  };

  const groupChip = key => {
    if (key === 'delivery') {
      return deliveryCount > 0 ? (
        <span className='pd-chip pd-chip--info'>
          {deliveryCount} {deliveryCount === 1 ? 'delivery' : 'deliveries'}
        </span>
      ) : (
        <span className='pd-chip pd-chip--warn'>No delivery yet</span>
      );
    }
    if (missingByGroup[key] > 0 && !isLocked) {
      return (
        <span className='pd-chip pd-chip--warn'>
          {missingByGroup[key]} to fill
        </span>
      );
    }
    return <span className='pd-chip pd-chip--ok'>Complete</span>;
  };

  const groupProps = key => {
    const group = REQ_GROUPS.find(g => g.key === key);
    return {
      id: `req-grp-${key}`,
      icon: group.icon,
      title: group.title,
      summary: groupSummary[key],
      chip: groupChip(key),
      isOpen: Boolean(openGroups[key]),
      onToggle: () => toggleGroup(key),
    };
  };

  // Opens a delivery term on its Pricing tab with the missing fields marked,
  // then puts the cursor in the first one. The fields depend on the delivery's
  // Pricing Period, which arrives with the detail request, so retry briefly.
  const openDeliveryPricing = (deliveryId, fieldErrors) => {
    if (!validatedeliveryModal()) return;
    fetchDeliveryDataDetail(deliveryId);
    fetchDeliveryDataDetailIncoterm(deliveryId);
    setactiveTab('2');
    setModal(true);
    setErrors(prev => ({ ...prev, ...fieldErrors }));
    const firstKey = Object.keys(fieldErrors)[0];
    let attempts = 0;
    const tryFocus = () => {
      attempts += 1;
      if (!focusFieldById(firstKey) && attempts < 10) {
        window.setTimeout(tryFocus, 150);
      }
    };
    window.setTimeout(tryFocus, 300);
  };

  // Completing the step needs every delivery term's pricing filled in.
  const validateDeliveryPricing = () => {
    const incomplete = qtyDeliveryData.items
      .map((item, index) => ({
        item,
        index,
        missing: deliveryPricingErrors(item, {
          isMonthlyIndexPrice,
        }),
      }))
      .filter(entry => Object.keys(entry.missing).length > 0);
    if (incomplete.length === 0) return true;

    setOpenGroups(prev => ({ ...prev, delivery: true }));
    notify.warning(
      <div>
        Complete the pricing of these delivery terms (click one to open it):
        <ul className='mb-0 mt-1' style={{ paddingLeft: '1.25rem' }}>
          {incomplete.map(({ item, index, missing }) => (
            <li key={item.id}>
              <button
                type='button'
                className='btn btn-link p-0 text-start align-baseline'
                style={{ fontSize: 'inherit' }}
                onClick={() => openDeliveryPricing(item.id, missing)}
              >
                Delivery {index + 1}
                {item.deliveryUnit ? ` (${item.deliveryUnit})` : ''}:{' '}
                {Object.values(missing).join(' ')}
              </button>
            </li>
          ))}
        </ul>
      </div>,
      { className: 'hy-toast hy-toast--warning hy-toast--wide' }
    );
    openDeliveryPricing(incomplete[0].item.id, incomplete[0].missing);
    return false;
  };

  const handleCompleteAndContinue = async () => {
    // Already completed: save the edits as a draft and move on, without
    // asking to complete the step a second time.
    if (form.isProcurementRequirementCompleted === true) {
      const saving = handleSaveDraftClick();
      if (!saving) return;
      await saving;
      goNext?.();
      return;
    }
    if (!validateProposalDates()) return;
    if (!validateDeliveryPricing()) return;
    focusOnErrors.current = true;
    const completed = await handleSaveCompleteProc();
    if (completed) {
      focusOnErrors.current = false;
      goNext?.();
    }
  };

  const handleSaveDraftClick = () => {
    if (!validateProposalDates()) return;
    focusOnErrors.current = true;
    return handleSaveDraftProc();
  };

  return (
    <React.Fragment>
      <Row>
        <div className='pd-step-head'>
          <div>
            <h5>
              <i className='ri-file-list-3-line me-2'></i>
              Requirement
            </h5>
            <p>
              Fill in the four cards below. Fields marked as missing are needed
              before this step can be completed.
            </p>
          </div>
          <ActionButton
            variant='tertiary'
            size='sm'
            icon={
              allGroupsOpen
                ? 'ri-contract-up-down-line'
                : 'ri-expand-up-down-line'
            }
            onClick={() => setAllGroups(!allGroupsOpen)}
          >
            {allGroupsOpen ? 'Collapse all' : 'Expand all'}
          </ActionButton>
        </div>

        <div className='pd-req'>
          <nav
            className='pd-req-nav d-none d-lg-block'
            aria-label='Requirement sections'
          >
            <div className='pd-req-nav__title'>Sections</div>
            {REQ_GROUPS.map(group => {
              const sections = group.sections.filter(
                section => !section.lpgOnly || isLpgProduct
              );
              const doneCount = sections.filter(
                section => isLocked || !missingBySection[section.id]
              ).length;
              const groupDone = doneCount === sections.length;
              return (
                <div className='pd-req-nav__block' key={group.key}>
                  <button
                    type='button'
                    className='pd-req-nav__group'
                    onClick={() =>
                      goToSection(group.key, `req-grp-${group.key}`)
                    }
                  >
                    <span className='pd-req-nav__group-icon' aria-hidden='true'>
                      <i className={group.icon}></i>
                    </span>
                    <span className='pd-req-nav__group-title'>
                      {group.title}
                    </span>
                    <span
                      className={`pd-req-nav__count${
                        groupDone ? ' pd-req-nav__count--ok' : ''
                      }`}
                      aria-label={`${doneCount} of ${sections.length} sections filled`}
                    >
                      {doneCount}/{sections.length}
                    </span>
                  </button>
                  <div className='pd-req-nav__items'>
                    {sections.map(section => {
                      const missing = !isLocked && missingBySection[section.id];
                      return (
                        <button
                          key={section.id}
                          type='button'
                          className={classnames('pd-req-nav__link', {
                            active: activeSection === section.id,
                          })}
                          onClick={() => goToSection(group.key, section.id)}
                        >
                          <span>{section.label}</span>
                          <span
                            className={`pd-dot ${missing ? 'pd-dot--warn' : 'pd-dot--ok'}`}
                            aria-label={
                              missing ? `${missing} field(s) to fill` : 'Filled'
                            }
                          >
                            <i
                              className={
                                missing
                                  ? 'ri-error-warning-line'
                                  : 'ri-check-line'
                              }
                              aria-hidden='true'
                            ></i>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
          <div className='pd-req__main'>
            <RequirementGroup {...groupProps('contract')}>
              {isLoading ? (
                <SkeletonForm fields={6} />
              ) : error ? (
                <Col lg={12} className='text-center'>
                  <div className='text-center'>
                    <div className='alert alert-danger' role='alert'>
                      {error}
                    </div>
                  </div>
                </Col>
              ) : (
                <>
                  <Row>
                    <Col lg={12}>
                      <div>
                        <p
                          id='req-sec-contract'
                          className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                        >
                          Contract
                        </p>

                        <p className='text-muted mb-2 fs-12 '>
                          Choose the contract type and set its period.
                        </p>
                      </div>
                    </Col>

                    {form.isProcurementCompleted === false ? (
                      <>
                        <Col lg={2}>
                          <Label className='form-label'>Contract Type</Label>
                          <div className='form-check card-radio'>
                            <Input
                              id='contractType01'
                              name='contractType'
                              type='radio'
                              value='1'
                              className='form-check-input'
                              onChange={e => {
                                handleTypeChange(e);
                                setErrors(prev => ({
                                  ...prev,
                                  contractType: null,
                                }));
                              }}
                              checked={parseInt(form.contractType, 10) === 1}
                            />

                            <Label
                              className='form-check-label'
                              for='contractType01'
                            >
                              <span className='fs-16  me-2'>
                                <i className='ri-file-list-2-line align-middle'></i>
                              </span>
                              <span className='fs-15 mb-1 text-wrap '>
                                TERM
                              </span>
                            </Label>
                          </div>
                          {errors.contractType && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='ri-error-warning-line'></i>
                              <span>{errors.contractType}</span>
                            </div>
                          )}
                        </Col>
                        <Col lg={2}>
                          <Label className='form-label'></Label>
                          <div className='form-check card-radio mt-1'>
                            <Input
                              id='contractType02'
                              name='contractType'
                              type='radio'
                              value='2'
                              className='form-check-input'
                              onChange={e => {
                                handleTypeChange(e);
                                setErrors(prev => ({
                                  ...prev,
                                  contractType: null,
                                  contractPeriodTermEnd: null,
                                }));
                              }}
                              checked={parseInt(form.contractType, 10) === 2}
                            />
                            <Label
                              className='form-check-label'
                              for='contractType02'
                            >
                              <span className='fs-16  me-2'>
                                <i className='ri-file-list-2-line align-middle'></i>
                              </span>
                              <span className='fs-15 mb-1 text-wrap '>
                                SPOT
                              </span>
                            </Label>
                          </div>
                        </Col>
                      </>
                    ) : (
                      <Col lg={4}>
                        <Label for='contractType' className='form-label'>
                          Contract Type
                        </Label>

                        <Input
                          type='text'
                          className='form-control bg-light border-1 text-center  fw-semibold fs-14'
                          id='contractType'
                          name='contractType'
                          readOnly
                          disabled={true}
                          value={
                            parseInt(form?.contractType ?? 0, 10) === 1
                              ? 'TERM'
                              : 'SPOT'
                          }
                        />
                      </Col>
                    )}

                    <Col lg={2}>
                      <div className='mb-2'>
                        <Label
                          for='contractPeriodTermStart'
                          className='form-label'
                        >
                          {!isendDate ? 'Start Period' : 'Period'}{' '}
                        </Label>
                        {form.isProcurementCompleted === true ? (
                          <Flatpickr
                            key='contractPeriodTermStartreadonly'
                            value={
                              form.contractPeriodTermStart
                                ? new Date(form.contractPeriodTermStart)
                                : null
                            }
                            options={{
                              altInput: true, // tampilkan format terpisah
                              altFormat: 'M Y', // format tampilan: Mar 2025
                              dateFormat: 'Y-m', // format simpan: 2025-03
                              locale: Indonesian,
                              clickOpens: !isendDate,
                              allowInput: !isendDate,
                            }}
                            //dateFormat='MMMM yyyy'
                            className='form-control bg-light border-1'
                            id='contractPeriodTermEndRo'
                            name='contractPeriodTermEndRo'
                            locale='id'
                            readOnly
                            disabled={true}
                          />
                        ) : (
                          <Flatpickr
                            key='contractPeriodTermStarteditable'
                            value={
                              form.contractPeriodTermStart
                                ? new Date(form.contractPeriodTermStart)
                                : null
                            }
                            options={{
                              altInput: true,
                              altFormat: 'M Y',
                              dateFormat: 'Y-m',
                              locale: Indonesian,
                              plugins: [
                                new monthSelectPlugin({
                                  shorthand: false,
                                  dateFormat: 'Y-m',
                                  altFormat: 'F Y',
                                  theme: 'light',
                                }),
                              ],
                            }}
                            onChange={([date]) => {
                              if (date instanceof Date && !isNaN(date)) {
                                const endOfMonth = new Date(
                                  date.getFullYear(),
                                  date.getMonth() + 1,
                                  0
                                );
                                setForm(form => ({
                                  ...form,
                                  contractPeriodTermStart: endOfMonth,
                                }));
                                setErrors(prev => ({
                                  ...prev,
                                  contractPeriodTermStart: null,
                                }));
                              } else {
                                setForm(form => ({
                                  ...form,
                                  contractPeriodTermStart: null,
                                }));
                              }
                            }}
                            className='form-control'
                            placeholder='Select month'
                            id='contractPeriodTermStart'
                            name='contractPeriodTermStart'
                            locale='id'
                          />
                        )}
                        {errors.contractPeriodTermStart && (
                          <div className='text-danger d-flex align-items-center gap-1'>
                            <i className='ri-error-warning-line'></i>
                            <span>{errors.contractPeriodTermStart}</span>
                          </div>
                        )}{' '}
                      </div>
                    </Col>

                    <Col lg={2}>
                      {!isendDate && (
                        <div className='mb-2'>
                          <Label
                            for='contractPeriodTermEnd'
                            className='form-label'
                          >
                            {' '}
                            End Period{' '}
                          </Label>
                          {form.isProcurementCompleted === true ? (
                            <Flatpickr
                              key='contractPeriodTermEndreadonly'
                              value={
                                form.contractPeriodTermEnd
                                  ? new Date(form.contractPeriodTermEnd)
                                  : null
                              }
                              options={{
                                altInput: true, // tampilkan format terpisah
                                altFormat: 'M Y', // format tampilan: Mar 2025
                                dateFormat: 'Y-m', // format simpan: 2025-03
                                locale: Indonesian,
                                clickOpens: !isendDate,
                                allowInput: !isendDate,
                              }}
                              //dateFormat='MMMM yyyy'
                              className='form-control bg-light border-1'
                              id='contractPeriodTermEnd'
                              name='contractPeriodTermEnd'
                              locale='id'
                              disabled={true}
                            />
                          ) : (
                            <Flatpickr
                              key='contractPeriodTermEndeditable'
                              value={
                                form.contractPeriodTermEnd
                                  ? new Date(form.contractPeriodTermEnd)
                                  : null
                              }
                              options={{
                                altInput: true, // tampilkan format terpisah
                                altFormat: 'M Y', // format tampilan: Mar 2025
                                dateFormat: 'Y-m', // format simpan: 2025-03
                                locale: Indonesian,
                                clickOpens: !isendDate,
                                allowInput: !isendDate,
                                plugins: [
                                  new monthSelectPlugin({
                                    shorthand: false,
                                    dateFormat: 'Y-m', // Format yang disimpan (misalnya: 2025-02)
                                    altFormat: 'F Y', // Format yang ditampilkan (misalnya: Februari 2025)
                                    theme: 'light',
                                  }),
                                ],
                              }}
                              onChange={([date]) => {
                                if (isendDate) return;
                                if (date instanceof Date && !isNaN(date)) {
                                  const endOfMonth = new Date(
                                    date.getFullYear(),
                                    date.getMonth() + 1,
                                    0
                                  );
                                  setForm(form => ({
                                    ...form,
                                    contractPeriodTermEnd: endOfMonth,
                                  }));
                                  setErrors(prev => ({
                                    ...prev,
                                    contractPeriodTermEnd: null,
                                  }));
                                } else {
                                  setForm(form => ({
                                    ...form,
                                    contractPeriodTermEnd: null,
                                  }));
                                }
                              }}
                              className='form-control'
                              placeholder='Select month'
                              id='contractPeriodTermEnd'
                              name='contractPeriodTermEnd'
                              locale='id'
                              //readOnly
                              disabled={isendDate}
                            />
                          )}
                          {errors.contractPeriodTermEnd && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='ri-error-warning-line'></i>
                              <span>{errors.contractPeriodTermEnd}</span>
                            </div>
                          )}{' '}
                        </div>
                      )}
                    </Col>
                  </Row>
                </>
              )}

              <Row>
                <Col sm={12}>
                  <div className='pd-divider'></div>

                  <p
                    id='req-sec-product'
                    className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                  >
                    Product Specification / Quantity
                  </p>
                  <p className='text-muted mb-2 fs-12 '>
                    Select the product, its type, tolerance, and detailed
                    specification.
                  </p>
                </Col>

                <Col sm={6} md={4}>
                  <div className='mb-2'>
                    <p className='form-label '>Product Name </p>
                    <Select
                      name='productNameId'
                      id='productNameId'
                      options={productName.items}
                      isClearable
                      placeholder='Select Product Name'
                      value={
                        productName.items?.find(
                          obj => obj.value === form.productNameId
                        ) || null
                      }
                      onChange={e => {
                        handleProductNameChange(e);
                        setErrors(prev => ({
                          ...prev,
                          productNameId: null,
                        }));
                      }}
                      isDisabled={
                        form.isProcurementCompleted ||
                        qtyDeliveryData.items.length > 0
                      }
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                    {errors.productNameId && (
                      <div className='text-danger d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line'></i>
                        <span>{errors.productNameId}</span>
                      </div>
                    )}{' '}
                  </div>
                </Col>

                <Col sm={6} md={3}>
                  <div className='mb-2'>
                    <p className='form-label '>
                      {isLpgForm ? 'Product Types' : 'Product Type'}
                    </p>
                    <Select
                      name='productTypeId'
                      id='productTypeId'
                      options={productTypeData.items}
                      isMulti={isLpgForm}
                      closeMenuOnSelect={!isLpgForm}
                      placeholder={
                        !form.productNameId
                          ? 'Select a product name first'
                          : isLpgForm
                            ? 'Select one or more'
                            : 'Please Select'
                      }
                      value={
                        isLpgForm
                          ? productTypeEntries.map(entry => ({
                              value: entry.productTypeId,
                              label: productTypeLabel(entry),
                            }))
                          : productTypeData.items?.find(
                              obj => obj.value === form.productTypeId
                            ) || null
                      }
                      onChange={e => {
                        if (isLpgForm) handleProductTypesChange(e);
                        else handleProductTypeChange(e);
                        setErrors(prev => ({
                          ...prev,
                          productTypeId: null,
                        }));
                      }}
                      isDisabled={
                        form.isProcurementCompleted ||
                        (!isLpgForm && qtyDeliveryData.items.length > 0)
                      }
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                    {errors.productTypeId && (
                      <div className='text-danger d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line'></i>
                        <span>{errors.productTypeId}</span>
                      </div>
                    )}{' '}
                  </div>
                </Col>

                <Col sm={12} md={5}>
                  <div className='mb-2'>
                    <Label for='operationalTolerance' className='form-label'>
                      Operational Tolerance (±)
                    </Label>
                    <div className='pd-tolerance'>
                      <div className='input-group'>
                        <Input
                          placeholder='e.g. 5'
                          type='number'
                          id='operationalTolerance'
                          name='operationalTolerance'
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-1'
                          }
                          value={form.operationalTolerance || ''}
                          onChange={handleNumberChange('operationalTolerance')}
                          readOnly={form.isProcurementCompleted !== false}
                        />
                        <span className='input-group-text'>%</span>
                      </div>
                      <Select
                        id='partyType'
                        name='partyType'
                        aria-label='Operational tolerance option'
                        options={PartyType}
                        placeholder='Buyer / Seller option'
                        isClearable
                        value={
                          PartyType[0].options.find(
                            option => option.value === form.partyType
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'partyType');
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              {LPG_PRODUCT.includes(form.productNameId) && (
                <>
                  <Row>
                    <Col sm={12}>
                      <div className='pd-divider'></div>

                      <p
                        id='req-sec-composition'
                        className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                      >
                        Composition
                      </p>
                      <p className='text-muted mb-2 fs-12 '>
                        Define the composition breakdown for each component.
                      </p>
                    </Col>
                  </Row>
                  {productTypeEntries.length === 0 && (
                    <div className='pd-empty'>
                      Select the product types to fill in their composition.
                    </div>
                  )}
                  {/* One composition per product type. The mixed propane &
                      butane types are split per cargo type; the others have
                      one range and a frequency. */}
                  {productTypeEntries.map((entry, index) => {
                    const { comptitle, sectitle } = compositionTitles(
                      entry.productTypeId
                    );
                    const entryForm = {
                      ...entry,
                      isProcurementCompleted: form.isProcurementCompleted,
                    };
                    const setEntryForm = updater =>
                      updateProductTypeEntry(index, updater);
                    const entryDeliveries = {
                      items: qtyDeliveryData.items.filter(
                        item =>
                          deliveryProductTypeOf(item) === entry.productTypeId
                      ),
                    };
                    return (
                      <div
                        key={entry.productTypeId}
                        className='pd-composition-type'
                      >
                        {productTypeEntries.length > 1 && (
                          <div className='pd-composition-type__head'>
                            <span className='pd-composition-type__num'>
                              {index + 1}
                            </span>
                            {productTypeLabel(entry)}
                            {entryDeliveries.items.length > 0 && (
                              <span className='pd-chip pd-chip--info ms-2'>
                                {entryDeliveries.items.length}{' '}
                                {entryDeliveries.items.length === 1
                                  ? 'cargo'
                                  : 'cargoes'}
                              </span>
                            )}
                          </div>
                        )}
                        {isMixProductType(entry.productTypeId) ? (
                          <Row>
                            <Col sm={12}>
                              <CargoTypeComposition
                                form={entryForm}
                                setForm={setEntryForm}
                                comptitle={comptitle}
                                sectitle={sectitle}
                                qtyDeliveryData={entryDeliveries}
                                idPrefix={compositionIdPrefix(index)}
                              />
                            </Col>
                          </Row>
                        ) : (
                          <Row>
                            <Col sm={4}>
                              <Composition
                                form={entryForm}
                                setForm={setEntryForm}
                                comptitle={comptitle}
                                idPrefix={compositionIdPrefix(index)}
                              />
                            </Col>
                          </Row>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              <Row>
                <Col sm={12}>
                  <div className='pd-divider'></div>
                  <p
                    id='req-sec-spec'
                    className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                  >
                    Product Specification
                  </p>
                  {errors.productSpecification && (
                    <div className='text-danger d-flex align-items-center gap-1'>
                      <i className='ri-error-warning-line'></i>
                      <span>{errors.productSpecification}</span>
                    </div>
                  )}{' '}
                  <EditorProvider>
                    <Editor
                      placeholder='Describe the product specification, e.g. composition limits, standards and test methods'
                      id='productSpecification'
                      name='productSpecification'
                      value={form.productSpecification || ''}
                      onChange={e => {
                        handleNoteTextChange(e, 'productSpecification');
                        setErrors(prev => ({
                          ...prev,
                          productSpecification: '',
                        }));
                      }}
                      style={{ height: '200px', overflowY: 'auto' }}
                      className={
                        form.isProcurementCompleted === false
                          ? 'form-control'
                          : 'form-control bg-light border-0'
                      }
                    >
                      {form.isProcurementCompleted === false && (
                        <RichTextToolbar />
                      )}
                    </Editor>
                  </EditorProvider>
                </Col>
              </Row>
            </RequirementGroup>

            <RequirementGroup {...groupProps('delivery')}>
              <Row>
                <Col sm={12}>
                  <div className='d-flex align-items-center '>
                    <div className='flex-grow-1'>
                      <p
                        id='req-sec-delivery'
                        className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                      >
                        Quantity & Delivery Time
                      </p>

                      <p className='text-muted mb-2 fs-12 '>
                        Manage delivery quantities, schedules, and destinations.
                      </p>
                    </div>
                    <div className='flex-shrink-0'>
                      {form.isProcurementCompleted === false && (
                        <ActionButton
                          variant='primary'
                          size='sm'
                          icon='ri-check-double-line'
                          onClick={() => handleDeliveryOpenModalNew()}
                        >
                          Add Optional Delivery
                        </ActionButton>
                      )}
                    </div>
                  </div>
                </Col>

                <Col xl={12}>
                  <div className='mb-1'>
                    {/* <CardBody> */}

                    {isLoading ? (
                      <SkeletonCard lines={3} />
                    ) : error ? (
                      <Col lg={12} className='text-center'>
                        <div className='text-center'>
                          <div className='alert alert-danger' role='alert'>
                            {error}
                          </div>
                        </div>
                      </Col>
                    ) : (
                      <>
                        {qtyDeliveryData.items.length > 0 ? (
                          qtyDeliveryData.items.map((data, index) => (
                            <Col
                              sm={12}
                              className='pd-delivery'
                              key={`${data.id}-${index}`}
                            >
                              {/* <ActionMenu /> */}
                              <Col sm={12}>
                                <div className='pd-delivery__head'>
                                  <div className='pd-delivery__title'>
                                    <span
                                      className='pd-delivery__icon'
                                      aria-hidden='true'
                                    >
                                      <i className='ri-ship-2-line'></i>
                                    </span>
                                    <div>
                                      <strong>
                                        {data.deliveryUnit || 'Delivery'}
                                      </strong>
                                      <small>
                                        Delivery {index + 1}
                                        {productTypeEntries.length > 1 &&
                                        (data.productTypeName ||
                                          deliveryProductTypeOf(data))
                                          ? ` · ${
                                              data.productTypeName ||
                                              productTypeLabel(
                                                productTypeEntryFor(
                                                  form,
                                                  deliveryProductTypeOf(data)
                                                ) || {}
                                              )
                                            }`
                                          : ''}
                                        {deliveryFrequencyOf(data) != null
                                          ? ` · Frequency ${deliveryFrequencyOf(data)}`
                                          : ''}
                                      </small>
                                    </div>
                                  </div>
                                  <div className='d-flex align-items-center gap-2'>
                                    <button
                                      type='button'
                                      className='btn btn-soft-primary btn-sm'
                                      onClick={() =>
                                        handleDeliveryOpenModal(data.id)
                                      }
                                    >
                                      <i
                                        className='ri-align-justify align-middle me-1'
                                        aria-hidden='true'
                                      ></i>
                                      Detail
                                    </button>
                                    {!form.isProcurementCompleted && (
                                      <button
                                        type='button'
                                        className='btn btn-soft-danger btn-sm'
                                        onClick={() =>
                                          handleDeleteQtyDelivery(
                                            data.id,
                                            data.deliveryUnit
                                          )
                                        }
                                      >
                                        <i
                                          className='ri-delete-bin-line align-middle me-1'
                                          aria-hidden='true'
                                        ></i>
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </Col>
                              <Col xxl={12}>
                                <Nav tabs className='nav nav-tabs  mb-3'>
                                  <NavItem>
                                    <NavLink
                                      style={{ cursor: 'pointer' }}
                                      className={classnames({
                                        active:
                                          activeTabs[data.id] === `${data.id}1`,
                                      })}
                                      onClick={() =>
                                        toggle(data.id, `${data.id}1`)
                                      }
                                    >
                                      <label className='text-uppercase'>
                                        {' '}
                                        Quantity Delivery Time
                                      </label>
                                    </NavLink>
                                  </NavItem>

                                  <NavItem>
                                    <NavLink
                                      style={{ cursor: 'pointer' }}
                                      className={classnames({
                                        active:
                                          activeTabs[data.id] === `${data.id}2`,
                                      })}
                                      onClick={() =>
                                        toggle(data.id, `${data.id}2`)
                                      }
                                    >
                                      <label className='text-uppercase'>
                                        {' '}
                                        Pricing
                                      </label>
                                    </NavLink>
                                  </NavItem>
                                </Nav>

                                <TabContent activeTab={activeTabs[data.id]}>
                                  <TabPane tabId={`${data.id}1`} id='home'>
                                    <Row>
                                      <Col lg={3}>
                                        <Row>
                                          <Col lg={12}>
                                            <div className=' mt-2  field-border-brown'>
                                              <Label
                                                for='deliveryUnitreadonly'
                                                className='text-muted'
                                              >
                                                Delivery Unit
                                              </Label>
                                              <p className='form-label fw-semibold fs-12 '>
                                                {data.deliveryUnit || '-'}
                                              </p>
                                            </div>
                                          </Col>
                                          <Col lg={12}>
                                            <div className=' mt-2  field-border-brown'>
                                              <Label
                                                for='frequencyreadonly'
                                                className='text-muted'
                                              >
                                                Frequency
                                              </Label>
                                              <p className='form-label fw-semibold fs-12 '>
                                                {deliveryFrequencyOf(data) ??
                                                  '-'}
                                              </p>
                                            </div>
                                          </Col>
                                          {isGasolineForm && (
                                            <Col lg={12}>
                                              <div className=' mt-2  field-border-brown'>
                                                <p className='text-muted mb-1'>
                                                  Alpha
                                                </p>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {getDyedUndyedLabel(data)}
                                                </p>
                                              </div>
                                            </Col>
                                          )}

                                          {/* <Col lg={6}>
                                              <div className='mb-2'>
                                                <Label
                                                  for='frequencyreadonly'
                                                  className='form-label '
                                                >
                                                  Frequency
                                                </Label>
                                                <Input
                                                  type='text'
                                                  className='form-control bg-light border-1'
                                                  id='frequencyreadonly'
                                                  name='frequencyreadonly'
                                                  readOnly
                                                  value={data.frequency || ''}
                                                />
                                              </div>
                                            </Col> */}
                                        </Row>
                                      </Col>

                                      <QuantityTableFragment
                                        ref={quantityRefs.current[index]}
                                        incoTermTable={incoTermTable}
                                        dataid={data.id}
                                        token={token}
                                        unitSizeType={unitSizeType}
                                      />

                                      <IncotermTableFragment
                                        incoTermTable={incoTermTable}
                                        dataid={data.id}
                                        token={token}
                                        onViewOrigin={handleViewOrigin}
                                      />
                                    </Row>
                                  </TabPane>

                                  <TabPane tabId={`${data.id}2`} id='product'>
                                    <Col sm={12}>
                                      <Row>
                                        <Col md={6}>
                                          <Row>
                                            <Col sm={4}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label className='text-muted'>
                                                  Delivery Unit
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {data.deliveryUnit || '-'}
                                                </p>
                                              </div>
                                            </Col>

                                            <Col sm={4}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label className='text-muted'>
                                                  Pricing Period
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {PricingPeriodData[0].options.find(
                                                    option =>
                                                      option.value ===
                                                      data.pricingPeriod
                                                  )?.label || '-'}
                                                </p>
                                              </div>
                                            </Col>

                                            <Col sm={4}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label className='text-muted'>
                                                  Index Price Reference
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {data.indexPricename || '-'}
                                                </p>
                                              </div>
                                            </Col>

                                            <Col sm={4}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label className='text-muted'>
                                                  Other Index Price
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {data.otherIndexPricename ||
                                                    '-'}
                                                </p>
                                              </div>
                                            </Col>

                                            <Col sm={4}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label className='text-muted'>
                                                  Information
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {InfoPrompt[0].options.find(
                                                    option =>
                                                      option.value ===
                                                      data.infoPrompt
                                                  )?.label || '-'}
                                                </p>
                                              </div>
                                            </Col>

                                            <Col sm={12}>
                                              <div className='mb-2 mt-2'>
                                                <Label
                                                  className='form-check-label'
                                                  for='inlineCheckbox8'
                                                >
                                                  {data.indexPricename &&
                                                  data.otherIndexPricename
                                                    ? '(whichever is lower)'
                                                    : ''}
                                                </Label>
                                              </div>
                                            </Col>
                                            {data.pricingPeriod === 1 &&
                                              isMonthlyIndexPrice(
                                                data.indexPriceId
                                              ) && (
                                                <Col sm={4}>
                                                  <div className='mb-2 mt-2  field-border-brown'>
                                                    <Label className='text-muted'>
                                                      Publish Index Month
                                                    </Label>
                                                    <p className='form-label mb-2 fs-12 '>
                                                      {publishIndexDay(
                                                        data.startDatePublishIndex
                                                      )
                                                        ? format(
                                                            publishIndexDay(
                                                              data.startDatePublishIndex
                                                            ),
                                                            'MMMM yyyy'
                                                          )
                                                        : '-'}
                                                    </p>
                                                  </div>
                                                </Col>
                                              )}
                                            {data.pricingPeriod === 1 &&
                                              !isMonthlyIndexPrice(
                                                data.indexPriceId
                                              ) && (
                                                <>
                                                  <Col sm={4}>
                                                    <div className='mb-2 mt-2  field-border-brown'>
                                                      <Label className='text-muted'>
                                                        Publish Index Period
                                                      </Label>
                                                      <p className='form-label mb-2 fs-12 '>
                                                        {formatDateToDDMMYYYY(
                                                          data.startDatePublishIndex
                                                        ) || '-'}
                                                      </p>
                                                    </div>
                                                  </Col>

                                                  <Col sm={4}>
                                                    <div className='mb-2 mt-2  '>
                                                      <Label className='text-muted'>
                                                        To
                                                      </Label>
                                                      <p className='form-label mb-2 fs-12 '>
                                                        {formatDateToDDMMYYYY(
                                                          data.endDatePublishIndex
                                                        ) || '-'}
                                                      </p>
                                                    </div>
                                                  </Col>
                                                </>
                                              )}
                                            {data.pricingPeriod === 2 && (
                                              <>
                                                <Col sm={4}>
                                                  <div className='mb-2 mt-2  field-border-brown'>
                                                    <Label className='text-muted'>
                                                      Schema B/L Date
                                                    </Label>
                                                    <p className='form-label mb-2 fs-12 '>
                                                      {data.schemaBL || '-'}
                                                    </p>
                                                  </div>
                                                </Col>
                                              </>
                                            )}
                                          </Row>
                                        </Col>

                                        <Col md={6}>
                                          <Label
                                            for='product-name-3'
                                            className='form-label '
                                          >
                                            Detail Information for Pricing
                                          </Label>
                                          <div className='snow-editor '>
                                            <EditorProvider>
                                              <Editor
                                                id='notesForPricingRO'
                                                name='notesForPricingRO'
                                                value={
                                                  data.notesForPricing || ''
                                                }
                                                readOnly
                                                style={{
                                                  height: '165px',
                                                  overflowY: 'auto',
                                                }}
                                              ></Editor>
                                            </EditorProvider>
                                          </div>
                                        </Col>
                                      </Row>
                                    </Col>
                                  </TabPane>
                                </TabContent>
                              </Col>
                            </Col>
                          ))
                        ) : (
                          <div className='pd-empty'>
                            <span className='pd-empty__icon' aria-hidden='true'>
                              <i className='ri-ship-2-line'></i>
                            </span>
                            <strong>No delivery yet</strong>
                            <p>
                              Add a delivery to set its quantity, incoterm and
                              pricing.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    <Row>
                      <Col sm={12}>
                        <div className='pd-divider'></div>
                        <div className='mb-1  '>
                          <div className='d-flex align-items-center'>
                            <div className='flex-grow-1'>
                              <Label
                                id='req-sec-port'
                                for='additionalNotes'
                                className='form-label  text-uppercase fw-semibold'
                              >
                                Port Restriction
                              </Label>

                              <p className='text-muted mb-2 fs-12 '>
                                List port restrictions that apply to this
                                procurement.
                              </p>
                            </div>
                            <div className='flex-shrink-0'>
                              {form.isProcurementCompleted === false && (
                                <ActionButton
                                  variant='primary'
                                  size='sm'
                                  icon='ri-add-line'
                                  disabled={
                                    !form.id || portLocationOptions.length === 0
                                  }
                                  title={
                                    !form.id
                                      ? 'Save the procurement first'
                                      : portLocationOptions.length === 0
                                        ? 'No port available on this procurement'
                                        : undefined
                                  }
                                  onClick={() => setAddPortModal(true)}
                                >
                                  Add Port
                                </ActionButton>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='mb-2'>
                          <CardBody>
                            <div className='table-card overflow-x-auto'>
                              {/* Tabel dibuat manual supaya header MIN/MAX bisa
                                    dikelompokkan di bawah DWT/LOA/DRAFT lewat
                                    colSpan/rowSpan. */}
                              <table className='table table-hover table-centered align-middle table-nowrap mb-0'>
                                <thead className='table-light'>
                                  <tr>
                                    <th
                                      scope='col'
                                      rowSpan={2}
                                      className='align-middle'
                                      style={{ width: '22%' }}
                                    >
                                      TERMINAL
                                    </th>
                                    <th colSpan={2} className='text-center'>
                                      DWT (TONS)
                                    </th>
                                    <th colSpan={2} className='text-center'>
                                      LOA (M)
                                    </th>
                                    <th colSpan={2} className='text-center'>
                                      DRAFT (M)
                                    </th>
                                    <th
                                      scope='col'
                                      rowSpan={2}
                                      className='align-middle text-center'
                                      style={{ width: '10%' }}
                                    >
                                      ACTION
                                    </th>
                                  </tr>
                                  <tr>
                                    <th className='text-center'>MIN</th>
                                    <th className='text-center'>MAX</th>
                                    <th className='text-center'>MIN</th>
                                    <th className='text-center'>MAX</th>
                                    <th className='text-center'>MIN</th>
                                    <th className='text-center'>MAX</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {jettyRestrictionsLoading && (
                                    <SkeletonTable
                                      inline
                                      rows={3}
                                      columns={8}
                                    />
                                  )}
                                  {!jettyRestrictionsLoading &&
                                    groupedJettyRestrictions.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={8}
                                          className='text-center text-muted py-3'
                                        >
                                          There is no data port restriction
                                        </td>
                                      </tr>
                                    )}
                                  {(jettyRestrictionsLoading
                                    ? []
                                    : groupedJettyRestrictions
                                  ).map(group => (
                                    <React.Fragment key={group.key}>
                                      <tr>
                                        <td colSpan={8} className='fw-semibold'>
                                          {group.name}
                                        </td>
                                      </tr>
                                      {group.items.length === 0 && (
                                        <tr>
                                          <td
                                            colSpan={8}
                                            className='text-center text-muted ps-4'
                                          >
                                            No Port data available
                                          </td>
                                        </tr>
                                      )}
                                      {group.items.map(item => (
                                        <tr key={item.id}>
                                          <td className='ps-4'>
                                            {item.name ||
                                              'Jenis Sarana Tambat Not Found'}
                                          </td>
                                          <td className='text-center'>
                                            {formatNumber(item.dwtMin)}
                                          </td>
                                          <td className='text-center'>
                                            {formatNumber(item.dwtMax)}
                                          </td>
                                          <td className='text-center'>
                                            {formatNumber(item.loaMin)}
                                          </td>
                                          <td className='text-center'>
                                            {formatNumber(item.loaMax)}
                                          </td>
                                          <td className='text-center'>
                                            {formatNumber(item.draftMin)}
                                          </td>
                                          <td className='text-center'>
                                            {formatNumber(item.draftMax)}
                                          </td>
                                          <td className='text-center'>
                                            {form.isProcurementCompleted ===
                                              false && (
                                              <div className='d-inline-flex gap-1'>
                                                <ActionButton
                                                  variant='icon'
                                                  size='sm'
                                                  icon='ri-edit-line'
                                                  label='Edit port'
                                                  onClick={() =>
                                                    setEditPortItem(item)
                                                  }
                                                />
                                                <ActionButton
                                                  variant='icon'
                                                  size='sm'
                                                  danger
                                                  // handleRemovePort sudah memakai confirmRemove.
                                                  confirm={false}
                                                  icon='ri-delete-bin-line'
                                                  label='Remove port'
                                                  onClick={() =>
                                                    handleRemovePort(item)
                                                  }
                                                />
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardBody>
                        </div>
                        <AddPortModal
                          isOpen={addPortModal}
                          toggle={() => setAddPortModal(false)}
                          locationOptions={portLocationOptions}
                          procurementId={form.id}
                          selectedJettyIds={jettyRestrictions.map(
                            item => item.locationJettyId
                          )}
                          onSaved={() => setJettyRefreshKey(prev => prev + 1)}
                        />
                        <EditPortModal
                          isOpen={Boolean(editPortItem)}
                          item={editPortItem}
                          toggle={() => setEditPortItem(null)}
                          onSaved={() => setJettyRefreshKey(prev => prev + 1)}
                        />
                      </Col>

                      <Col sm={12}>
                        <div className='pd-divider'></div>

                        <div className='mb-1  '>
                          <Label
                            id='req-sec-port-notes'
                            for='globalNotes'
                            className='form-label mt-2 text-uppercase fw-semibold'
                          >
                            Port Notes
                          </Label>

                          <p className='text-muted mb-2 fs-12 '>
                            Add supporting notes for the port restriction above.
                          </p>
                        </div>
                        <div className='snow-editor '>
                          <EditorProvider>
                            <Editor
                              placeholder='Add notes about the port restrictions above'
                              ref={editorRef}
                              id='globalNotes'
                              name='globalNotes'
                              value={form.globalNotes || ''}
                              onChange={e => {
                                handleNoteTextChange(e, 'globalNotes');
                              }}
                              style={{ height: '200px', overflowY: 'auto' }}
                              className={
                                form.isProcurementCompleted === false
                                  ? 'form-control'
                                  : 'form-control bg-light border-0'
                              }
                            >
                              {form.isProcurementCompleted === false && (
                                <RichTextToolbar highlight />
                              )}
                            </Editor>
                          </EditorProvider>
                        </div>
                      </Col>
                    </Row>

                    {/* </CardBody> */}
                  </div>
                </Col>
              </Row>
            </RequirementGroup>

            <RequirementGroup {...groupProps('terms')}>
              <Row>
                <Col sm={12}>
                  <div className='d-flex align-items-start justify-content-between mb-3'>
                    <div>
                      <Label
                        id='req-sec-clause'
                        for='clauseDetails'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Clause Details
                      </Label>
                      <p className='text-muted mb-0 fs-12 '>
                        Click a clause to expand and edit its wording. Switch a
                        clause off to leave it out of this procurement; use Add
                        clause index to bring it back.
                      </p>
                    </div>
                    <div className='d-flex align-items-center gap-2 flex-wrap justify-content-end'>
                      <span className='badge bg-primary-subtle text-primary'>
                        {activeClauses.length} active
                        {inactiveClauseCount > 0 &&
                          ` · ${inactiveClauseCount} inactive`}
                      </span>
                      {form.isProcurementCompleted === false && (
                        <ActionButton
                          variant='primary'
                          size='sm'
                          icon='ri-add-line'
                          onClick={() => setAddClauseOpen(true)}
                        >
                          Add clause index
                        </ActionButton>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {activeClauses.length === 0 && (
                <div className='pd-empty mb-3'>
                  <span className='pd-empty__icon'>
                    <i className='ri-article-line' aria-hidden='true' />
                  </span>
                  <p>
                    {inactiveClauseCount > 0
                      ? 'All clauses are inactive. Use Add clause index to activate one.'
                      : 'No clause on this procurement yet.'}
                  </p>
                </div>
              )}

              {activeClauses.map((item, index) => {
                const isMandatory = Boolean(
                  clauseValue(dataCause, item, 'isShowToDMUT')
                );
                const isOpen =
                  Boolean(expandedClauses[item.id]) || Boolean(errors[item.id]);

                return (
                  <div className='clause-card' key={`${item.id}-${index}`}>
                    <div className='clause-card-head'>
                      <button
                        type='button'
                        className='clause-card-header'
                        onClick={() => toggleClauseCard(item.id)}
                        aria-expanded={isOpen}
                      >
                        <span className='clause-card-icon'>
                          <i className='ri-article-line'></i>
                        </span>
                        <span className='clause-card-title'>
                          {item.clauseTitle}
                        </span>
                        {isMandatory && (
                          <span className='badge bg-danger-subtle text-danger'>
                            Mandatory for Registered Supplier
                          </span>
                        )}
                        {errors[item.id] && (
                          <span className='badge bg-warning-subtle text-warning'>
                            Needs attention
                          </span>
                        )}
                        <i
                          className={`ri-arrow-down-s-line clause-chevron ${
                            isOpen ? 'open' : ''
                          }`}
                        ></i>
                      </button>
                      <div
                        className='form-check form-switch form-switch-success clause-card-switch'
                        title='Active: shown to suppliers'
                      >
                        <Input
                          className='form-check-input'
                          type='checkbox'
                          role='switch'
                          id={`clauseActive_${item.id}`}
                          checked
                          onChange={e =>
                            handleClauseActiveChange(item, e.target.checked)
                          }
                          disabled={form.isProcurementCompleted !== false}
                          aria-label={`${item.clauseTitle} active`}
                        />
                        <Label
                          className='form-check-label fs-12'
                          for={`clauseActive_${item.id}`}
                        >
                          Active
                        </Label>
                      </div>
                    </div>

                    {isOpen && (
                      <div className='clause-card-body'>
                        <div className='d-flex form-check form-switch form-switch-secondary mb-3'>
                          <div className='flex-shrink-0'>
                            <Input
                              className='form-check-input'
                              type='checkbox'
                              role='switch'
                              id={`isShowToDMUT_${item.id}`}
                              name={`isShowToDMUT_${item.id}`}
                              checked={isMandatory}
                              onChange={e => {
                                handleClauseCheckBoxChange(e);
                              }}
                              disabled={form.isProcurementCompleted !== false}
                            />
                          </div>
                          <Label className='form-label ms-2 mb-0'>
                            Mandatory clause for Registered Supplier to fulfill
                          </Label>
                        </div>
                        {errors[item.id] && (
                          <div className='text-danger d-flex align-items-center gap-1 mb-2'>
                            <i className='ri-error-warning-line'></i>
                            <span>{errors[item.id]}</span>
                          </div>
                        )}
                        <div className='snow-editor '>
                          <EditorProvider>
                            <Editor
                              placeholder='Write the clause wording, or insert a template from the toolbar'
                              id={item.id}
                              name={item.id}
                              value={
                                clauseValue(
                                  dataCause,
                                  item,
                                  'clauseDescription'
                                ) ?? ''
                              }
                              onChange={e => {
                                handleNoteTextChanges(
                                  e,
                                  `clauseDesc_${item.id}`
                                );
                                setErrors(prev => ({
                                  ...prev,
                                  [item.id]: '',
                                }));
                              }}
                              style={{ height: '200px', overflowY: 'auto' }}
                              className={
                                form.isProcurementCompleted === false
                                  ? 'form-control'
                                  : 'form-control bg-light border-0'
                              }
                            >
                              {form.isProcurementCompleted === false && (
                                <RichTextToolbar highlight>
                                  <select
                                    className='form-select form-select-sm pd-rte__template'
                                    aria-label='Insert template'
                                    value={insertOption}
                                    onChange={e => {
                                      handleDropdownInsert(
                                        e.target.value,
                                        item.id
                                      );
                                      const editorEl = document.getElementById(
                                        item.id
                                      );
                                      if (editorEl) {
                                        handleNoteTextChanges(
                                          {
                                            target: {
                                              value: editorEl.innerHTML,
                                            },
                                          },
                                          `clauseDesc_${item.id}`
                                        );
                                      }
                                    }}
                                  >
                                    <option value=''>Insert template…</option>
                                    {(
                                      templatesByClauseDetailId[
                                        item.clauseDetailId
                                      ] || []
                                    ).map(tpl => (
                                      <option key={tpl.id} value={tpl.id}>
                                        {tpl.name}
                                      </option>
                                    ))}
                                  </select>
                                  {item.clauseDetailId && (
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-soft-primary pd-rte__new-template'
                                      title='Save a new answer template for this clause to master data'
                                      onClick={() =>
                                        setTemplateModalClause({
                                          clauseDetailId: item.clauseDetailId,
                                          clauseTitle: item.clauseTitle,
                                          wording:
                                            clauseValue(
                                              dataCause,
                                              item,
                                              'clauseDescription'
                                            ) ?? '',
                                        })
                                      }
                                    >
                                      <i
                                        className='ri-add-line'
                                        aria-hidden='true'
                                      ></i>
                                      New template
                                    </button>
                                  )}
                                </RichTextToolbar>
                              )}
                            </Editor>
                          </EditorProvider>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <AddClauseIndexModal
                isOpen={addClauseOpen}
                toggle={() => setAddClauseOpen(false)}
                procurementId={form.id}
                existingClauseDetailIds={(dataCause.items || []).map(
                  item => item.clauseDetailId
                )}
                inactiveClauses={inactiveClauses}
                onAdded={handleClausesAdded}
                onReactivated={handleClausesReactivated}
              />
              <ClauseTemplateModal
                isOpen={Boolean(templateModalClause)}
                toggle={() => setTemplateModalClause(null)}
                clauseDetailId={templateModalClause?.clauseDetailId}
                clauseTitle={templateModalClause?.clauseTitle}
                initialTemplate={templateModalClause?.wording}
                onSaved={handleClauseTemplateSaved}
              />

              <Row>
                <Col sm={12}>
                  <div className='border-top mt-3 pt-2'>
                    <div className='flex-grow-1'>
                      <Label
                        id='req-sec-payment'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Payment Terms
                      </Label>

                      <p className='text-muted mb-2 fs-12 '>
                        Choose the payment methods and terms that apply.
                      </p>
                    </div>
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2'>
                    <Label for='invoicingQuantityId' className='form-label '>
                      Invoicing Quantity
                    </Label>

                    <Select
                      name='invoicingQuantityId'
                      id='invoicingQuantityId'
                      options={invoiceQuantityData.items}
                      placeholder='Select…'
                      value={
                        invoiceQuantityData.items?.find(
                          obj => obj.value === form.invoicingQuantityId
                        ) || null
                      }
                      onChange={e => {
                        handleSelectChange(e, 'invoicingQuantityId');
                      }}
                      isDisabled={form.isProcurementCompleted}
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2'>
                    <Label
                      for='modeInvoicingQuantityId'
                      className='form-label text-muted'
                    >
                      Mode
                    </Label>

                    <Select
                      name='modeInvoicingQuantityId'
                      id='modeInvoicingQuantityId'
                      options={modelinvoiceQuantityData.items}
                      placeholder='Select…'
                      value={
                        modelinvoiceQuantityData.items?.find(
                          obj => obj.value === form.modeInvoicingQuantityId
                        ) || null
                      }
                      onChange={e => {
                        handleSelectChange(e, 'modeInvoicingQuantityId');
                      }}
                      isDisabled={form.isProcurementCompleted}
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2'>
                    <Label
                      for='paymentCurrencyId'
                      className='form-label text-muted'
                    >
                      Currency
                    </Label>

                    <Select
                      name='paymentCurrencyId'
                      id='paymentCurrencyId'
                      options={currencyName.items}
                      placeholder='Select…'
                      value={
                        currencyName.items?.find(
                          obj => obj.value === form.paymentCurrencyId
                        ) || null
                      }
                      onChange={e => {
                        handleSelectChange(e, 'paymentCurrencyId');
                      }}
                      isDisabled={form.isProcurementCompleted}
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                  </div>
                </Col>

                <Col sm={12}>
                  <Label
                    for='descriptionOfPaymentTerms'
                    className='form-label  text-uppercase fw-semibold'
                  >
                    Description of Payment Mode
                  </Label>
                  {errors.descriptionOfPaymentTerms && (
                    <div className='text-danger d-flex align-items-center gap-1'>
                      <i className='ri-error-warning-line'></i>
                      <span>{errors.descriptionOfPaymentTerms}</span>
                    </div>
                  )}{' '}
                  <EditorProvider>
                    <Editor
                      placeholder='Describe the payment terms, e.g. due date and the documents required'
                      id='descriptionOfPaymentTerms'
                      name='descriptionOfPaymentTerms'
                      value={form.descriptionOfPaymentTerms || ''}
                      //onChange={handleTextChange("descriptionOfPaymentTerms")}
                      onChange={e => {
                        handleNoteTextChange(e, 'descriptionOfPaymentTerms');
                        setErrors(prev => ({
                          ...prev,
                          descriptionOfPaymentTerms: '',
                        }));
                      }}
                      style={{ height: '200px', overflowY: 'auto' }}
                      className={
                        form.isProcurementCompleted === false
                          ? 'form-control'
                          : 'form-control bg-light border-0'
                      }
                    >
                      {form.isProcurementCompleted === false && (
                        <RichTextToolbar highlight />
                      )}
                    </Editor>
                  </EditorProvider>
                </Col>
              </Row>

              <Row>
                <Col sm={12}>
                  <div className='pd-divider'>
                    <div className='flex-grow-1'>
                      <Label
                        id='req-sec-compliance'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Compliance
                      </Label>

                      <p className='text-muted mb-2 fs-12 '>
                        Confirm the compliance requirements for this
                        procurement.
                      </p>
                    </div>
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2'>
                    <Label for='lawId' className='form-label '>
                      Law
                    </Label>
                    <Select
                      name='lawId'
                      id='lawId'
                      options={lawData.items}
                      placeholder='Select…'
                      value={
                        lawData.items?.find(obj => obj.value === form.lawId) ||
                        null
                      }
                      onChange={e => {
                        handleSelectChange(e, 'lawId');
                      }}
                      isDisabled={form.isProcurementCompleted}
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2'>
                    <Label for='arbitrationId' className='form-label '>
                      Arbitration
                    </Label>

                    <Select
                      name='arbitrationId'
                      id='arbitrationId'
                      options={abitrationData.items}
                      placeholder='Select…'
                      value={
                        abitrationData.items?.find(
                          obj => obj.value === form.arbitrationId
                        ) || null
                      }
                      onChange={e => {
                        handleSelectChange(e, 'arbitrationId');
                      }}
                      isDisabled={form.isProcurementCompleted}
                      styles={{
                        singleValue: base => ({
                          ...base,
                          color: '#000',
                        }),
                        input: base => ({
                          ...base,
                          color: '#000',
                        }),
                      }}
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col sm={12}>
                  <div className='pd-divider'>
                    <div className='flex-grow-1'>
                      <Label
                        id='req-sec-other'
                        for='otherTermsAndCondition'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Other Terms & Conditions
                      </Label>
                      {errors.otherTermsAndCondition && (
                        <div className='text-danger d-flex align-items-center gap-1'>
                          <i className='ri-error-warning-line'></i>
                          <span>{errors.otherTermsAndCondition}</span>
                        </div>
                      )}{' '}
                    </div>
                  </div>
                </Col>

                <Col sm={12}>
                  <Card>
                    <div className='snow-editor '>
                      <EditorProvider>
                        <Editor
                          placeholder='Add any other terms and conditions'
                          id='otherTermsAndCondition'
                          name='otherTermsAndCondition'
                          value={form.otherTermsAndCondition || ''}
                          onChange={e => {
                            handleNoteTextChange(e, 'otherTermsAndCondition');
                            setErrors(prev => ({
                              ...prev,
                              otherTermsAndCondition: '',
                            }));
                          }}
                          style={{ height: '200px', overflowY: 'auto' }}
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-0'
                          }
                        >
                          {form.isProcurementCompleted === false && (
                            <RichTextToolbar highlight />
                          )}
                        </Editor>
                      </EditorProvider>
                    </div>
                  </Card>
                </Col>
              </Row>

              <Col sm={12} className='mb-2'>
                <div className='pd-divider'>
                  <div className='flex-grow-1'>
                    <Label
                      id='req-sec-additional'
                      for='additionalNotes'
                      className='form-label  text-uppercase fw-semibold'
                    >
                      Additional Notes
                    </Label>
                  </div>
                </div>
              </Col>

              <Col sm={12}>
                <Card>
                  <div className='snow-editor '>
                    <EditorProvider>
                      <Editor
                        placeholder='Add notes for the suppliers (optional)'
                        id='additionalNotes'
                        name='additionalNotes'
                        value={form.additionalNotes || ''}
                        onChange={e => {
                          handleNoteTextChange(e, 'additionalNotes');
                        }}
                        //onChange={handleTextChange('additionalNotes')}
                        style={{ height: '200px', overflowY: 'auto' }}
                        className={
                          form.isProcurementCompleted === false
                            ? 'form-control'
                            : 'form-control bg-light border-0'
                        }
                      >
                        {form.isProcurementCompleted === false && (
                          <RichTextToolbar highlight />
                        )}
                      </Editor>
                    </EditorProvider>
                  </div>
                </Card>
              </Col>
            </RequirementGroup>

            <RequirementGroup {...groupProps('validity')}>
              <Row>
                <Col sm={12}>
                  <div className='mb-2'>
                    <Label
                      id='req-sec-validity'
                      className='form-label  text-uppercase fw-semibold'
                    >
                      Validity
                    </Label>
                    <p className='text-muted mb-2 fs-12 '>
                      Set how long the submitted proposal remains valid. All
                      times are Jakarta local time (WIB).
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className='g-3'>
                <Col md={6} xl={4}>
                  <div className='mb-2'>
                    <Label
                      for='proposalSubmissionDeadline'
                      className='form-label fw-semibold text-nowrap'
                    >
                      Submission Deadline{' '}
                      <span className='text-muted fw-normal'>(WIB)</span>
                    </Label>
                    <Flatpickr
                      placeholder='Select date and time'
                      id='proposalSubmissionDeadline'
                      name='proposalSubmissionDeadline'
                      className={
                        form.isProcurementCompleted === false
                          ? 'form-control'
                          : 'form-control bg-light border-1'
                      }
                      options={{
                        enableTime: true,
                        dateFormat: 'd/m/Y H:i', // Format tampilan
                        time_24hr: true,
                        minDate: pickerMinDate(
                          minSubmissionDeadline(),
                          form.proposalSubmissionDeadline
                        ),
                      }}
                      value={form.proposalSubmissionDeadline || ''}
                      //onChange={handleDateChange}
                      onChange={handleDateChange(
                        'proposalSubmissionDeadline',
                        true
                      )}
                      disabled={form.isProcurementCompleted ? true : false}
                    />
                    {errors.proposalSubmissionDeadline && (
                      <div className='text-danger d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line'></i>
                        <span>{errors.proposalSubmissionDeadline}</span>
                      </div>
                    )}{' '}
                  </div>
                </Col>

                <Col md={6} xl={4}>
                  <div className='mb-2'>
                    <Label
                      for='proposalValidityPeriodMax'
                      className='form-label fw-semibold text-nowrap'
                    >
                      Validity Period{' '}
                      <span className='text-muted fw-normal'>(WIB)</span>
                    </Label>
                    <Flatpickr
                      placeholder='Select date and time'
                      id='proposalValidityPeriodMax'
                      name='proposalValidityPeriodMax'
                      className={
                        form.isProcurementCompleted === false
                          ? 'form-control'
                          : 'form-control bg-light border-1'
                      }
                      options={{
                        enableTime: true,
                        dateFormat: 'd/m/Y H:i', // Format tampilan
                        time_24hr: true,
                        minDate: pickerMinDate(
                          minValidityPeriod(),
                          form.proposalValidityPeriodMax
                        ),
                      }}
                      value={form.proposalValidityPeriodMax || ''}
                      //onChange={handleDateChange}
                      onChange={handleDateChange(
                        'proposalValidityPeriodMax',
                        true
                      )}
                      disabled={form.isProcurementCompleted ? true : false}
                    />
                    {errors.proposalValidityPeriodMax && (
                      <div className='text-danger d-flex align-items-center gap-1'>
                        <i className='ri-error-warning-line'></i>
                        <span>{errors.proposalValidityPeriodMax}</span>
                      </div>
                    )}{' '}
                  </div>
                </Col>
              </Row>
            </RequirementGroup>

            <StepActionBar
              status={
                isLocked
                  ? 'Read-only after submission'
                  : totalMissing > 0
                    ? `${totalMissing} required ${
                        totalMissing === 1 ? 'field' : 'fields'
                      } still empty`
                    : form.isProcurementRequirementCompleted
                      ? 'Step completed. Changes need Save as Draft.'
                      : 'All required fields are filled'
              }
              statusTone={isLocked ? 'muted' : totalMissing > 0 ? 'warn' : 'ok'}
              statusIcon={
                isLocked
                  ? 'ri-lock-line'
                  : totalMissing > 0
                    ? 'ri-error-warning-line'
                    : 'ri-checkbox-circle-line'
              }
            >
              {isLocked ? (
                <ActionButton
                  variant='primary'
                  icon='ri-arrow-right-line'
                  onClick={() => goNext?.()}
                >
                  Next: Supporting Documents
                </ActionButton>
              ) : (
                <>
                  <ActionButton
                    variant='secondary'
                    icon='ri-draft-line'
                    onClick={handleSaveDraftClick}
                  >
                    Save as Draft
                  </ActionButton>
                  <ActionButton
                    variant='primary'
                    icon='ri-check-double-line'
                    onClick={handleCompleteAndContinue}
                  >
                    {form.isProcurementRequirementCompleted
                      ? 'Save & Continue'
                      : 'Complete & Continue'}
                  </ActionButton>
                </>
              )}
            </StepActionBar>
          </div>
        </div>
      </Row>
      <Modal
        isOpen={deletemodal}
        role='dialog'
        autoFocus={true}
        centered
        id='removeItemModal'
        toggle={toggledeletemodal}
      >
        <ModalHeader
          toggle={() => {
            setDeleteModal(!deletemodal);
          }}
        ></ModalHeader>
        <ModalBody>
          <div className='mt-2 text-center'>
            <div className='mt-4 pt-2 fs-15 mx-4 mx-sm-5'>
              <h4>Are you Sure ?</h4>
              <p className='text-muted mx-4 mb-0'>
                Are you Sure You want to Remove this Address ?
              </p>
            </div>
          </div>
          <div className='d-flex gap-2 justify-content-center mt-4 mb-2'>
            <ActionButton
              variant='secondary'
              icon='ri-close-line'
              className='w-sm'
              onClick={() => {
                setDeleteModal(!deletemodal);
              }}
            >
              Close
            </ActionButton>
            <ActionButton
              variant='destructive'
              icon='ri-delete-bin-line'
              confirm={false}
              className='w-sm'
              onClick={() => {
                setDeleteModal(!deletemodal);
              }}
            >
              Yes, Delete It!
            </ActionButton>
          </div>
        </ModalBody>
      </Modal>
      {/* //---- Modal quantity */}
      {/* modal Add Address */}

      <Modal
        //size="lg"
        isOpen={modal}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={deliverymodal}
        data-bs-backdrop='static'
        //size='xl'
        className='modal-custom-xl'
      >
        <ModalHeader
          toggle={() => {
            setModal(!modal);
          }}
        >
          <span className='modal-title' id='addAddressModalLabel'>
            Quantity And Delivery Time
          </span>
        </ModalHeader>

        <ModalBody>
          <Row className='mb-1' style={{ minHeight: '485px' }}>
            <Col sm={12}>
              <CardBody>
                <div className='flex-grow-1'>
                  <p className='text-muted mb-2 fs-12 '>
                    Manage the delivery schedule and quantity for each parcel.
                  </p>
                </div>

                <Nav tabs className='nav-tabs mb-3'>
                  <NavItem>
                    <NavLink
                      style={{ cursor: 'pointer' }}
                      className={classnames({ active: activeTab === '1' })}
                      onClick={() => {
                        toggles('1');
                      }}
                    >
                      <label className='text-uppercase'>
                        {' '}
                        Quantity Delivery Time
                      </label>
                    </NavLink>
                  </NavItem>
                  {/* {deliveryformData.id !== null ? (
                  <> */}
                  <NavItem>
                    <NavLink
                      style={{ cursor: 'pointer' }}
                      className={classnames({ active: activeTab === '2' })}
                      onClick={() => {
                        toggles('2');
                      }}
                    >
                      <label className='text-uppercase'> Pricing</label>
                    </NavLink>
                  </NavItem>
                  {/* </>
                  ) : null} */}
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId='1' id='home'>
                    <Col sm={12}>
                      {isLpgForm && (
                        <Row>
                          <Col sm={6}>
                            <div className='mb-2'>
                              <Label
                                for='deliveryProductTypeId'
                                className='form-label fw-semibold'
                              >
                                Product Type
                              </Label>
                              <Select
                                inputId='deliveryProductTypeId'
                                id='deliveryProductTypeId'
                                options={deliveryProductTypeOptions}
                                placeholder='Select the product type of this cargo'
                                value={
                                  deliveryProductTypeOptions.find(
                                    option =>
                                      option.value ===
                                      deliveryformData.productTypeId
                                  ) || null
                                }
                                onChange={handleDeliveryProductTypeChange}
                                isDisabled={
                                  form.isProcurementCompleted ||
                                  qtyDeliveryDataIncoterm.items.length > 0
                                }
                              />
                              {errors.deliveryProductTypeId && (
                                <div className='text-danger d-flex align-items-center gap-1'>
                                  <i className='ri-error-warning-line'></i>
                                  <span>{errors.deliveryProductTypeId}</span>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col sm={6}>
                          <div className='mb-2'>
                            <Label
                              for='deliveryUnit'
                              className='form-label fw-semibold '
                            >
                              Delivery Unit
                            </Label>

                            <Input
                              placeholder='e.g. Cargo'
                              type='text'
                              id='deliveryUnit'
                              name='deliveryUnit'
                              value={deliveryformData.deliveryUnit || ''}
                              className={
                                form.isProcurementCompleted
                                  ? 'form-control bg-light border-1'
                                  : 'form-control'
                              }
                              onChange={
                                form.isProcurementCompleted
                                  ? undefined
                                  : e => {
                                      handleDeliveryTextChange(e);
                                      setErrors(prev => ({
                                        ...prev,
                                        deliveryUnit: '',
                                      }));
                                    }
                              }
                              readOnly={form.isProcurementCompleted}
                            />
                          </div>
                          {errors.deliveryUnit && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='ri-error-warning-line'></i>
                              <span>{errors.deliveryUnit}</span>
                            </div>
                          )}{' '}
                        </Col>
                        {needsFrequency && (
                          <>
                            <Col sm={6} md={3}>
                              <div className='mb-2'>
                                <Label for='frequency' className='form-label '>
                                  Frequency
                                </Label>
                                <Input
                                  placeholder='e.g. 12'
                                  type='number'
                                  id='frequency'
                                  name='frequency'

                                  className={
                                    form.isProcurementCompleted
                                      ? 'form-control bg-light border-1'
                                      : 'form-control'
                                  }
                                  value={deliveryformData.frequency || ''}
                                  onChange={e => {
                                    handleDeliveryTextChange(e);
                                    setErrors(prev => ({
                                      ...prev,
                                      frequency: '',
                                    }));
                                  }}
                                  readOnly={form.isProcurementCompleted}
                                />
                              </div>
                              {errors.frequency && (
                                <div className='text-danger d-flex align-items-center gap-1'>
                                  <i className='ri-error-warning-line'></i>
                                  <span>{errors.frequency}</span>
                                </div>
                              )}{' '}
                            </Col>
                          </>
                        )}

                        {isGasolineForm && (
                          <Col sm={6} md={3}>
                            <div className='mb-2'>
                              <p className='form-label mb-1'>
                                Alpha <span className='text-danger'>*</span>
                              </p>
                              <div className='d-flex gap-3 pt-1'>
                                {[
                                  ['isDyed', 'Dyed'],
                                  ['isUndyed', 'Undyed'],
                                ].map(([field, label]) => (
                                  <div className='form-check' key={field}>
                                    <Input
                                      className='form-check-input'
                                      type='checkbox'
                                      id={`delivery_${field}`}
                                      checked={
                                        deliveryformData[field] !== false
                                      }
                                      onChange={e => {
                                        const { checked } = e.target;
                                        setDeliveryFormData(prev => ({
                                          ...prev,
                                          [field]: checked,
                                        }));
                                        setErrors(prev => ({
                                          ...prev,
                                          alphaVariant: '',
                                        }));
                                      }}
                                      disabled={form.isProcurementCompleted}
                                    />
                                    <Label
                                      className='form-check-label'
                                      for={`delivery_${field}`}
                                    >
                                      {label}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {errors.alphaVariant && (
                              <div className='text-danger d-flex align-items-center gap-1'>
                                <i className='ri-error-warning-line'></i>
                                <span>{errors.alphaVariant}</span>
                              </div>
                            )}
                          </Col>
                        )}

                        {isDeliveryMixType && (
                          <>
                            <Col sm={6} md={3}>
                              <div className='mb-2'>
                                <p className='form-label '>Cargo Type</p>
                                <Select
                                  id='deliveryCargoType'
                                  name='cargoType'
                                  options={[{ options: cargoTypeOptions }]}
                                  placeholder='Select…'
                                  isClearable
                                  value={
                                    cargoTypeOptions.find(
                                      option =>
                                        option.value ===
                                        deliveryformData.cargoType
                                    ) || null
                                  }
                                  onChange={e => {
                                    setErrors(prev => ({
                                      ...prev,
                                      cargoType: null,
                                    }));
                                    handleDeliverySelectChange(e, 'cargoType');
                                    generateQtyDeliveryTotalQtyByCargoType(
                                      e ? e.value : null,
                                      deliveryformData.productTypeId
                                    );
                                  }}
                                  isDisabled={
                                    form.isProcurementCompleted ||
                                    qtyDeliveryDataIncoterm.items.length > 0
                                  }
                                />
                                {errors.cargoType && (
                                  <div className='text-danger d-flex align-items-center gap-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>{errors.cargoType}</span>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </>
                        )}
                      </Row>
                    </Col>

                    {/* {deliveryformData.id !== null ? ( */}
                    <Row className='mb-3 '>
                      <Col md={3}>
                        <Row>
                          <Col sm={12}>
                            <div
                              className='d-flex align-items-center mb-2 border-top border-top-dashed'
                              style={{ minHeight: '48px' }}
                            >
                              <div className='flex-grow-1 '>
                                <Label className='form-label   fw-semibold'>
                                  Quantity
                                </Label>
                              </div>
                              <div className='flex-shrink-0'>
                                {form.isProcurementCompleted === false &&
                                  isNonLpgQuantityFlow &&
                                  qtyDeliveryDataIncoterm.items.length ===
                                    0 && (
                                    <ActionButton
                                      variant='primary'
                                      size='sm'
                                      icon='ri-function-line'
                                      className='mb-2 mt-2'
                                      onClick={() => handleAddQuantityRow()}
                                    >
                                      Add Quantity
                                    </ActionButton>
                                  )}
                              </div>
                            </div>
                          </Col>

                          <div className='d-flex overflow-x-auto'>
                            <table className='table table-hover table-centered align-middle table-nowrap mb-2'>
                              <thead className='text-muted table-light'>
                                <tr>
                                  <th scope='col' width='75%' className='py-2'>
                                    {unitSizeType}
                                  </th>
                                  <th
                                    scope='col'
                                    width='25%'
                                    className='py-2 text-center'
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {qtyDeliveryDataTotalQty.items.map(
                                  (data, index) => (
                                    <tr key={`${data.id}-${index}`}>
                                      <td className='py-1'>
                                        <Input
                                          placeholder='Enter quantity'
                                          type='number'
                                          min={
                                            quantityBounds
                                              ? quantityBounds.min
                                              : 1
                                          }
                                          max={quantityBounds?.max}
                                          className={
                                            qtyRowErrors[data.id]
                                              ? 'form-control form-control-sm is-invalid'
                                              : 'form-control form-control-sm'
                                          }
                                          aria-label={`Quantity ${index + 1}`}
                                          value={data.quantity ?? ''}
                                          onChange={e =>
                                            handleQuantityItemChange(
                                              data.id,
                                              e.target.value
                                            )
                                          }
                                          disabled={
                                            form.isProcurementCompleted ||
                                            (!isNonLpgQuantityFlow &&
                                              LPG_PRODUCT.includes(
                                                form.productNameId
                                              )) ||
                                            qtyDeliveryDataIncoterm.items
                                              .length > 0 ||
                                            String(editingQtyId) !==
                                              String(data.id)
                                          }
                                        />
                                      </td>

                                      <td className='py-1 text-center'>
                                        {form.isProcurementCompleted ===
                                          false &&
                                          isNonLpgQuantityFlow &&
                                          qtyDeliveryDataIncoterm.items
                                            .length === 0 && (
                                            <>
                                              {String(editingQtyId) !==
                                                String(data.id) && (
                                                <ActionButton
                                                  variant='icon'
                                                  size='sm'
                                                  icon='ri-edit-line'
                                                  label='Edit'
                                                  onClick={() =>
                                                    handleEditQuantityRow(
                                                      data.id
                                                    )
                                                  }
                                                />
                                              )}
                                              <ActionButton
                                                variant='icon'
                                                size='sm'
                                                danger
                                                icon='ri-delete-bin-line'
                                                label='Deleteh'
                                                confirm={{
                                                  title:
                                                    'Delete this quantity?',
                                                  message:
                                                    'This quantity item will be removed from the list.',
                                                  confirmLabel:
                                                    'Delete quantity',
                                                }}
                                                onClick={() =>
                                                  handleDeleteQuantityItem(
                                                    data.id
                                                  )
                                                }
                                              />
                                            </>
                                          )}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                          {[
                            ...new Set(
                              Object.values(qtyRowErrors).filter(Boolean)
                            ),
                          ].map(message => (
                            <Col sm={12} key={message}>
                              <div className='text-danger d-flex align-items-start gap-1 mb-2'>
                                <i className='mdi mdi-alert-circle-outline flex-shrink-0'></i>
                                <span>{message}</span>
                              </div>
                            </Col>
                          ))}
                          {errors.quantity && (
                            <Col sm={12}>
                              <div className='text-danger d-flex align-items-start gap-1 mb-2'>
                                <i className='mdi mdi-alert-circle-outline flex-shrink-0'></i>
                                <span>{errors.quantity}</span>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </Col>

                      <Col md={9}>
                        <Row>
                          <Col sm={12}>
                            <div
                              className='d-flex align-items-center mb-2 border-top border-top-dashed'
                              style={{ minHeight: '48px' }}
                            >
                              <div className='flex-grow-1'>
                                <Label className='form-label  text-uppercase fw-semibold'>
                                  Incoterm
                                </Label>
                              </div>
                              <div className='flex-shrink-0'>
                                {form.isProcurementCompleted === false &&
                                  (qtyDeliveryDataTotalQtySum > 0 &&
                                  deliveryformData.id !== null ? (
                                    <ActionButton
                                      variant='primary'
                                      size='sm'
                                      icon='ri-map-pin-line'
                                      className='mb-2 mt-2'
                                      onClick={handleAddIncotermClick}
                                    >
                                      Add Incoterm
                                    </ActionButton>
                                  ) : (
                                    <span className='text-muted fs-12 mb-2 mt-2 d-block text-end'>
                                      Before adding an Incoterm, save your
                                      changes first.
                                    </span>
                                  ))}
                              </div>
                            </div>
                          </Col>

                          <div className='d-flex overflow-x-auto'>
                            <table className='table table-hover table-centered align-middle table-nowrap mb-2'>
                              <thead className='text-muted table-light'>
                                <tr>
                                  <th scope='col' width='80%' className='py-2'>
                                    Delivery
                                  </th>
                                  <th scope='col' width='10%' className='py-2'>
                                    <span className='visually-hidden'>
                                      Details
                                    </span>
                                  </th>
                                  <th scope='col' width='10%' className='py-2'>
                                    Action
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {qtyDeliveryDataIncoterm.items.map(
                                  (data, index) => (
                                    <tr key={`${data.id}-${index}`}>
                                      <td>
                                        {(() => {
                                          const portLocations = [];

                                          if (
                                            Array.isArray(
                                              data.incotermFOBLocations
                                            ) &&
                                            data.incotermFOBLocations.length > 0
                                          ) {
                                            const names =
                                              data.incotermFOBLocations
                                                .map(loc => loc.name)
                                                .join(' or ');
                                            portLocations.push({
                                              location: `${data.routes} ${names}`,
                                              startDate: data.startDate,
                                              endDate: data.endDate,
                                            });
                                          }

                                          if (
                                            data.incotermLocation != null &&
                                            data.incotermLocation !== ''
                                          ) {
                                            portLocations.push({
                                              location: data.incotermLocation,
                                              startDate: data.startDate,
                                              endDate: data.endDate,
                                            });
                                          }
                                          if (
                                            data.incotermLocation2 != null &&
                                            data.incotermLocation2 !== ''
                                          ) {
                                            portLocations.push({
                                              location: data.incotermLocation2,
                                              startDate: data.startDate2,
                                              endDate: data.endDate2,
                                            });
                                          }
                                          if (
                                            data.incotermLocation3 != null &&
                                            data.incotermLocation3 !== ''
                                          ) {
                                            portLocations.push({
                                              location: data.incotermLocation3,
                                              startDate: data.startDate3,
                                              endDate: data.endDate3,
                                            });
                                          }
                                          return portLocations.map(
                                            (item, idx) => (
                                              <p
                                                className='mb-1'
                                                key={idx}
                                                style={{
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {data.incoterm}{' '}
                                                {data.incotermPortType}{' '}
                                                {data.incoterm === 'FOB' &&
                                                Array.isArray(
                                                  data.incotermFOBLocations
                                                ) &&
                                                data.incotermFOBLocations
                                                  .length > 3
                                                  ? `${data.routes} all ${data.routes}`
                                                  : item.location}
                                                ,{' on '}
                                                {data.timelineType}{' '}
                                                {item.startDate} -{' '}
                                                {item.endDate}
                                              </p>
                                            )
                                          );
                                        })()}
                                      </td>
                                      <td>
                                        {Array.isArray(
                                          data.incotermFOBLocations
                                        ) &&
                                          data.incotermFOBLocations.length >
                                            3 && (
                                            <ActionButton
                                              variant='tertiary'
                                              className='p-0'
                                              onClick={() =>
                                                handleViewOrigin(
                                                  data.incotermFOBLocations,
                                                  data.routes
                                                )
                                              }
                                            >{`View ${data.routes}`}</ActionButton>
                                          )}
                                      </td>
                                      <td>
                                        {/* <ActionMenu /> */}

                                        <ul className='list-inline hstack gap-2 mb-0 '>
                                          {form.isProcurementCompleted ===
                                            false && (
                                            <>
                                              <ActionButton
                                                id={`editBtn-${data.id}`}
                                                variant='icon'
                                                size='sm'
                                                icon='ri-file-list-2-line'
                                                label='Edit'
                                                onClick={() =>
                                                  handleIncotermOpenModal(
                                                    data.id,
                                                    data.incotermId,
                                                    'incoterm'
                                                  )
                                                }
                                              />
                                              <UncontrolledTooltip
                                                placement='top'
                                                target={`editBtn-${data.id}`}
                                                fade={true}
                                                delay={{ show: 0, hide: 0 }}
                                              >
                                                Edit incoterm
                                              </UncontrolledTooltip>
                                            </>
                                          )}

                                          {form.isProcurementCompleted ===
                                            false && (
                                            <>
                                              <ActionButton
                                                id={`deleteBtn-${data.id}`}
                                                variant='icon'
                                                size='sm'
                                                danger
                                                icon='ri-delete-bin-5-line'
                                                label='Delete'
                                                confirm={false}
                                                onClick={() =>
                                                  handleDeleteIncotermDeliveryDetail(
                                                    data.id,
                                                    data.incotermId,
                                                    'incoterm',
                                                    [data.incoterm, data.routes]
                                                      .filter(Boolean)
                                                      .join(' ')
                                                  )
                                                }
                                              />

                                              <UncontrolledTooltip
                                                placement='top'
                                                target={`deleteBtn-${data.id}`}
                                                fade={true}
                                                delay={{ show: 0, hide: 0 }}
                                              >
                                                Delete incoterm
                                              </UncontrolledTooltip>
                                            </>
                                          )}
                                        </ul>
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>

                              {/* {qtyDeliveryDataIncoterm.items.length > 0 && (
                                <tfoot className='table-light'>
                                  <tr>
                                    <td colSpan={3} className='py-2'>
                                      <span className='fw-semibold'>
                                        Total Location FOB:
                                      </span>{' '}
                                      {incotermLocationTotals.fob}
                                      <span className='fw-semibold ms-4'>
                                        Total Location:
                                      </span>{' '}
                                      {incotermLocationTotals.location}
                                    </td>
                                  </tr>
                                </tfoot>
                              )} */}
                            </table>
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId='2' id='product'>
                    <Col sm={12}>
                      <Row>
                        <Col sm={12}>
                          <Row>
                            <Col sm={4}>
                              <div className='mb-2'>
                                <Label className='form-label fw-semibold'>
                                  Pricing Period
                                </Label>
                                <Select
                                  id='pricingPeriod'
                                  name='pricingPeriod'
                                  options={PricingPeriodData}
                                  placeholder='Select…'
                                  isClearable
                                  value={
                                    PricingPeriodData[0].options.find(
                                      option =>
                                        option.value ===
                                        deliveryformData.pricingPeriod
                                    ) || null
                                  }
                                  onChange={e => {
                                    handleSelectChangeDelivery(
                                      e,
                                      'pricingPeriod'
                                    );
                                    setErrors(prev => ({
                                      ...prev,
                                      pricingPeriod: '',
                                    }));
                                  }}
                                  isDisabled={form.isProcurementCompleted}
                                />
                              </div>
                              {errors.pricingPeriod && (
                                <div className='text-danger d-flex align-items-center gap-1'>
                                  <i className='ri-error-warning-line'></i>
                                  <span>{errors.pricingPeriod}</span>
                                </div>
                              )}{' '}
                            </Col>

                            {deliveryformData.pricingPeriod !== 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label className='form-label '>
                                    Index Price Reference
                                  </Label>
                                  <Select
                                    name='indexPriceId'
                                    id='indexPriceId'
                                    options={indexPriceOptions}
                                    placeholder='Select…'
                                    isClearable
                                    value={
                                      dataindexprice.items?.find(
                                        obj =>
                                          obj.value ===
                                          deliveryformData.indexPriceId
                                      ) || null
                                    }
                                    onChange={e => {
                                      handleSelectChangeDelivery(
                                        e,
                                        'indexPriceId'
                                      );
                                      setErrors(prev => ({
                                        ...prev,
                                        indexPriceId: '',
                                      }));
                                    }}
                                    isDisabled={form.isProcurementCompleted}
                                  />
                                </div>
                                {errors.indexPriceId && (
                                  <div className='text-danger d-flex align-items-center gap-1'>
                                    <i className='ri-error-warning-line'></i>
                                    <span>{errors.indexPriceId}</span>
                                  </div>
                                )}{' '}
                              </Col>
                            )}
                            {deliveryformData.pricingPeriod !== 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label className='form-label '>
                                    Other Index Price
                                  </Label>
                                  <Select
                                    name='otherIndexPriceId'
                                    id='otherIndexPriceId'
                                    options={indexPriceOptions}
                                    placeholder='Select…'
                                    isClearable
                                    value={
                                      dataindexprice.items?.find(
                                        obj =>
                                          obj.value ===
                                          deliveryformData.otherIndexPriceId
                                      ) || null
                                    }
                                    onChange={e => {
                                      handleSelectChangeDelivery(
                                        e,
                                        'otherIndexPriceId'
                                      );
                                      setErrors(prev => ({
                                        ...prev,
                                        otherIndexPriceId: '',
                                      }));
                                    }}
                                    isDisabled={form.isProcurementCompleted}
                                  />
                                </div>
                                {errors.otherIndexPriceId && (
                                  <div className='text-danger d-flex align-items-center gap-1'>
                                    <i className='ri-error-warning-line'></i>
                                    <span>{errors.otherIndexPriceId}</span>
                                  </div>
                                )}{' '}
                              </Col>
                            )}
                            {deliveryformData.pricingPeriod === 1 &&
                              isMonthlyIndexPrice(
                                deliveryformData.indexPriceId
                              ) && (
                                <Col sm={6} md={4}>
                                  <div className='mb-2'>
                                    <Label
                                      for='startDatePublishIndex'
                                      className='form-label '
                                    >
                                      Publish Index Month
                                    </Label>
                                    <Flatpickr
                                      id='startDatePublishIndex'
                                      name='startDatePublishIndex'
                                      value={
                                        publishIndexDay(
                                          deliveryformData.startDatePublishIndex
                                        ) || ''
                                      }
                                      options={{
                                        altInput: true,
                                        altFormat: 'F Y',
                                        dateFormat: 'Y-m',
                                        plugins: [
                                          new monthSelectPlugin({
                                            shorthand: false,
                                            dateFormat: 'Y-m',
                                            altFormat: 'F Y',
                                            theme: 'light',
                                          }),
                                        ],
                                      }}
                                      placeholder='Select month...'
                                      className={
                                        form.isProcurementCompleted
                                          ? 'form-control bg-light border-1'
                                          : 'form-control'
                                      }
                                      disabled={form.isProcurementCompleted}
                                      onChange={([date]) => {
                                        if (
                                          !(date instanceof Date) ||
                                          Number.isNaN(date.getTime())
                                        ) {
                                          return;
                                        }
                                        const year = date.getFullYear();
                                        const month = date.getMonth();
                                        setDeliveryFormData(prev => ({
                                          ...prev,
                                          startDatePublishIndex: format(
                                            new Date(year, month, 1),
                                            'dd/MM/yyyy'
                                          ),
                                          endDatePublishIndex: format(
                                            new Date(year, month + 1, 0),
                                            'dd/MM/yyyy'
                                          ),
                                        }));
                                        setErrors(prev => ({
                                          ...prev,
                                          startDatePublishIndex: '',
                                          endDatePublishIndex: '',
                                        }));
                                      }}
                                    />
                                    {errors.startDatePublishIndex && (
                                      <div className='text-danger d-flex align-items-center gap-1'>
                                        <i className='ri-error-warning-line'></i>
                                        <span>
                                          {errors.startDatePublishIndex}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                            {deliveryformData.pricingPeriod === 1 &&
                              !isMonthlyIndexPrice(
                                deliveryformData.indexPriceId
                              ) && (
                                <Col sm={6} md={2}>
                                  <div className='mb-2'>
                                    <Label
                                      for='startDatePublishIndex'
                                      className='form-label '
                                    >
                                      Publish Index Period
                                    </Label>
                                    <Input
                                      type='date'
                                      id='startDatePublishIndex'
                                      name='startDatePublishIndex'
                                      value={getSafeInputDate(
                                        deliveryformData.startDatePublishIndex,
                                        convertToInputDateFormat
                                      )}
                                      className={
                                        form.isProcurementCompleted
                                          ? 'form-control bg-light border-1'
                                          : 'form-control'
                                      }

                                      onChange={e => {
                                        handleDeliveryDateChange(e);
                                        const newVal =
                                          convertToDisplayDateFormat(
                                            e.target.value
                                          );
                                        const err = validatePublishIndexDates(
                                          newVal,
                                          deliveryformData.endDatePublishIndex
                                        );
                                        setErrors(prev => ({
                                          ...prev,
                                          endDatePublishIndex: err,
                                          startDatePublishIndex: '',
                                        }));
                                      }}
                                      readOnly={form.isProcurementCompleted}
                                    />
                                    {errors.startDatePublishIndex && (
                                      <div className='text-danger d-flex align-items-center gap-1'>
                                        <i className='ri-error-warning-line'></i>
                                        <span>
                                          {errors.startDatePublishIndex}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}

                            {deliveryformData.pricingPeriod === 1 &&
                              !isMonthlyIndexPrice(
                                deliveryformData.indexPriceId
                              ) && (
                                <Col sm={6} md={2}>
                                  <div className='mb-2'>
                                    <Label
                                      for='endDatePublishIndex'
                                      className='form-label '
                                    >
                                      To
                                    </Label>

                                    <Input
                                      type='date'
                                      id='endDatePublishIndex'
                                      name='endDatePublishIndex'
                                      value={getSafeInputDate(
                                        deliveryformData.endDatePublishIndex,
                                        convertToInputDateFormat
                                      )}
                                      className={
                                        form.isProcurementCompleted
                                          ? 'form-control bg-light border-1'
                                          : 'form-control'
                                      }

                                      onChange={e => {
                                        handleDeliveryDateChange(e);
                                        const newVal =
                                          convertToDisplayDateFormat(
                                            e.target.value
                                          );
                                        const err = validatePublishIndexDates(
                                          deliveryformData.startDatePublishIndex,
                                          newVal
                                        );
                                        setErrors(prev => ({
                                          ...prev,
                                          endDatePublishIndex: err,
                                        }));
                                      }}
                                      readOnly={form.isProcurementCompleted}
                                    />
                                    {errors.endDatePublishIndex && (
                                      <div className='text-danger d-flex align-items-center gap-1'>
                                        <i className='ri-error-warning-line'></i>
                                        <span>
                                          {errors.endDatePublishIndex}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}

                            {deliveryformData.pricingPeriod === 2 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label for='schemaBL' className='form-label '>
                                    Schema B/L Date
                                  </Label>
                                  <Input
                                    placeholder='e.g. B/L date + 5 days'
                                    type='text'
                                    id='schemaBL'
                                    name='schemaBL'
                                    value={deliveryformData.schemaBL || ''}
                                    className='form-control'
                                    onChange={e => {
                                      handleDeliveryTextChange(e);
                                      setErrors(prev => ({
                                        ...prev,
                                        schemaBL: '',
                                      }));
                                    }}
                                  />
                                  {errors.schemaBL && (
                                    <div className='text-danger d-flex align-items-center gap-1'>
                                      <i className='ri-error-warning-line'></i>
                                      <span>{errors.schemaBL}</span>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}

                            {/* {deliveryformData.pricingPeriod === 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label
                                    for='priceAmount'
                                    className='form-label '
                                  >
                                    Price Amount
                                  </Label>

                                  <div className='input-group'>
                                    <Cleave
                                      placeholder='Enter numeral'
                                      id='priceAmount'
                                      name='priceAmount'
                                      value={deliveryformData.priceAmount || ''}
                                      options={{
                                        numeral: true,
                                        numeralThousandsGroupStyle: 'thousand',
                                      }}
                                      onChange={e => {
                                        handleDeliveryTextChange(e);
                                      }}
                                      // onChange={onNumberChange('priceAmount')}
                                      readOnly={
                                        form.isProcurementCompleted !== false
                                      }
                                      className={
                                        form.isProcurementCompleted === false
                                          ? 'form-control'
                                          : 'form-control bg-light border-1'
                                      }
                                    />

                                    <span
                                      className='input-group-text'
                                      id='basic-addon2'
                                    >
                                      USD/bbl
                                    </span>
                                  </div>
                                </div>
                              </Col>
                            )} */}

                            {deliveryformData.pricingPeriod !== 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label for='schemaBL' className='form-label '>
                                    {''}
                                  </Label>
                                  <Input
                                    type='text'
                                    id='isCorrelationBetweenTwoIndexRO'
                                    name='isCorrelationBetweenTwoIndexRO'
                                    readOnly
                                    value={
                                      (deliveryformData.isCorrelationBetweenTwoIndex &&
                                        '(whichever is lower)') ||
                                      ''
                                    }
                                    className='form-control bg-light border-0'
                                  />
                                </div>
                              </Col>
                            )}

                            <Col sm={4}>
                              <div className='mb-2'>
                                <Label className='form-label fw-semibold'>
                                  Information
                                </Label>
                                <Select
                                  id='infoPrompt'
                                  name='infoPrompt'
                                  options={InfoPrompt}
                                  placeholder='Select…'
                                  isClearable
                                  value={
                                    InfoPrompt[0].options.find(
                                      option =>
                                        option.value ===
                                        deliveryformData.infoPrompt
                                    ) || null
                                  }
                                  onChange={e => {
                                    handleSelectChangeDelivery(e, 'infoPrompt');
                                    setErrors(prev => ({
                                      ...prev,
                                      infoPrompt: '',
                                    }));
                                  }}
                                  isDisabled={form.isProcurementCompleted}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>

                    <Col sm={12}>
                      <Row>
                        <Col sm={12}>
                          <Row>
                            <Col sm={12}>
                              <div className='mb-2'>
                                <Label className='form-check-label'>
                                  Detail Information for Pricing
                                </Label>
                              </div>

                              <div className='snow-editor '>
                                <EditorProvider>
                                  <Editor
                                    placeholder='Add Detail Information about pricing (optional)'
                                    id='notesForPricing'
                                    name='notesForPricing'
                                    value={
                                      deliveryformData.notesForPricing || ''
                                    }
                                    onChange={handleTextChangeDelivery(
                                      'notesForPricing'
                                    )}
                                    style={{
                                      height: '200px',
                                      overflowY: 'auto',
                                    }}
                                    className={
                                      form.isProcurementCompleted === false
                                        ? 'form-control'
                                        : 'form-control bg-light border-0'
                                    }
                                  >
                                    {form.isProcurementCompleted === false && (
                                      <RichTextToolbar />
                                    )}
                                  </Editor>
                                </EditorProvider>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={() => {
              setModal(!modal);
            }}
          >
            Close
          </ActionButton>
          {form.isProcurementCompleted === false && (
            <ActionButton
              variant='primary'
              icon='ri-database-2-line'
              onClick={() =>
                deliveryformData.id !== null
                  ? handleUpdDataDelivery()
                  : handleAddDataDelivery()
              }
            >
              {deliveryformData.id !== null ? 'Update' : 'Save'}
            </ActionButton>
          )}
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={modalincoterm}
        role='dialog'
        autoFocus={true}
        centered
        scrollable
        id='addAddressModal'
        toggle={togglemodalincoterm}
        data-bs-backdrop='static'
        size='lg'
      >
        <ModalHeader
          toggle={() => {
            setModalIncoterm(!modalincoterm);
          }}
        >
          <span className='modal-title d-block' id='addAddressModalLabel'>
            {deliveryincotermformData.id === null
              ? 'Add incoterm'
              : 'Edit incoterm'}
          </span>
          <span className='d-block fs-12 fw-normal text-muted'>
            Total quantity {formatNumberUS(incotermQtyTotal) || 0}{' '}
            {unitSizeType}
            {incotermQtyRows.length > 1
              ? ` in ${incotermQtyRows.length} quantity rows`
              : ''}
          </span>
        </ModalHeader>

        <ModalBody>
          {incotermErrorList.length > 0 && (
            <div className='pd-error-summary' role='alert'>
              <strong>
                {incotermErrorList.length}{' '}
                {incotermErrorList.length === 1 ? 'item' : 'items'} to fix
                before saving
              </strong>
              <ul>
                {incotermErrorList.map(([key, message]) => (
                  <li key={key}>
                    <button
                      type='button'
                      className='btn btn-link p-0 text-start align-baseline fs-12'
                      onClick={() => focusFieldById(key)}
                    >
                      {message}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 1. Incoterm */}
          <section className='pd-inc-section'>
            <div className='pd-inc-section__head'>
              <h6 className='pd-inc-section__title'>
                <span className='pd-inc-section__num'>1</span>
                Incoterm <span className='text-danger'>*</span>
              </h6>
            </div>
            <div
              id='incotermId'
              className='pd-inc-cards'
              role='group'
              aria-label='Incoterm'
            >
              {(incotermtype.items || []).map(option => (
                <button
                  key={option.value}
                  type='button'
                  className='pd-inc-card'
                  aria-pressed={
                    deliveryincotermformData.incotermId === option.value
                  }
                  onClick={() =>
                    handleSelectChangeIncoterm(option, 'incotermId')
                  }
                >
                  <strong>{option.label}</strong>
                  <span>
                    {option.value === FOB_INCOTERM_ID
                      ? 'Loading at seller port'
                      : 'Delivered to discharge port'}
                  </span>
                </button>
              ))}
            </div>
            {incotermErrors.incotermId && (
              <div className='text-danger d-flex align-items-center gap-1'>
                <i className='ri-error-warning-line'></i>
                <span>{incotermErrors.incotermId}</span>
              </div>
            )}
            {deliveryincotermformData.incotermId && (
              <div className='d-flex flex-wrap align-items-center gap-2 fs-12 text-muted'>
                <span className='pd-chip pd-chip--info'>
                  {deliveryincotermformData.timelineType === 'ALD'
                    ? 'Accepted Loading Dates (ALD)'
                    : 'Accepted Discharged Dates (ADD)'}
                </span>
                Timeline is set from the incoterm.
              </div>
            )}
          </section>

          {/* 2a. FOB: route, loading locations, loading dates */}
          {deliveryincotermformData.incotermId && isFobIncoterm && (
            <section className='pd-inc-section'>
              <div className='pd-inc-section__head'>
                <h6 className='pd-inc-section__title'>
                  <span className='pd-inc-section__num'>2</span>
                  Loading
                </h6>
              </div>
              <Row className='g-3'>
                <Col md={5}>
                  <Label for='incotermRouteId' className='form-label'>
                    Route <span className='text-danger'>*</span>
                  </Label>
                  <Select
                    {...MODAL_SELECT_PROPS}
                    name='incotermRouteId'
                    id='incotermRouteId'
                    options={
                      IncotermRouteData.items?.filter(
                        item => item.label !== 'N/A'
                      ) || []
                    }
                    placeholder='Select route'
                    value={
                      IncotermRouteData.items
                        ?.filter(item => item.label !== 'N/A')
                        ?.find(
                          obj =>
                            obj.value ===
                            deliveryincotermformData.incotermRouteId
                        ) || null
                    }
                    onChange={e => {
                      handleSelectChangeIncoterm(e, 'incotermRouteId');
                    }}
                  />
                  {incotermErrors.incotermRouteId && (
                    <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                      <i className='ri-error-warning-line'></i>
                      <span>{incotermErrors.incotermRouteId}</span>
                    </div>
                  )}
                </Col>
                <Col md={7}>
                  <Label className='form-label'>Loading locations</Label>
                  <MultiSelect
                    options={IncotermLocationData.items || []}
                    name='incotermFOB'
                    id='incotermFOB'
                    labelledBy='Select loading locations'
                    overrideStrings={{
                      selectSomeItems: 'Select loading locations',
                    }}
                    // Falsy falls back to the library default (names, or "All items are selected").
                    valueRenderer={(selected, options) =>
                      selected.length > 2 &&
                      selected.length < options.length &&
                      `${selected.length} of ${options.length} locations selected`
                    }
                    value={
                      IncotermLocationData.items?.filter(obj => {
                        const ids = deliveryincotermformData.incotermFOB;
                        return Array.isArray(ids)
                          ? ids.includes(obj.value)
                          : ids === obj.value;
                      }) || []
                    }
                    onChange={selectedItems => {
                      const value =
                        selectedItems?.map(item => item.value) || [];
                      setDeliveryIncotermFormData(prev => ({
                        ...prev,
                        incotermFOB: value,
                      }));
                    }}
                  />
                </Col>
                <Col md={5}>
                  <Label className='form-label'>Quantity</Label>
                  {incotermQtyRows.map((data, index) => (
                    <div
                      className='input-group pd-qty pd-qty--readonly mb-1'
                      key={`${data.id}-${index}`}
                    >
                      <Input
                        type='text'
                        className='form-control'
                        value={formatNumberUS(data.quantity) || ''}
                        placeholder='0'
                        aria-label={`Quantity row ${index + 1}`}
                        readOnly
                      />
                      <span className='input-group-text'>{unitSizeType}</span>
                    </div>
                  ))}
                </Col>
                <Col md={7}>
                  {renderIncotermDates('', 'Accepted loading dates', true)}
                </Col>
              </Row>
            </section>
          )}

          {/* 2b. Other incoterms: one row per discharge port */}
          {deliveryincotermformData.incotermId && !isFobIncoterm && (
            <section className='pd-inc-section'>
              <div className='pd-inc-section__head'>
                <h6 className='pd-inc-section__title'>
                  <span className='pd-inc-section__num'>2</span>
                  Discharge ports <span className='text-danger'>*</span>
                </h6>
              </div>
              <div
                className={classnames('pd-portpick', {
                  'pd-portpick--empty': incotermPortCount === 0,
                  'pd-portpick--invalid': Boolean(
                    incotermErrors.incotermPortType
                  ),
                })}
              >
                <div className='pd-portpick__label'>
                  How many discharge ports?{' '}
                  <span className='text-danger'>*</span>
                  <small>
                    {incotermPortCount === 0
                      ? 'Choose one before filling in the ports.'
                      : `A delivery can have at most ${MAX_INCOTERM_LOCATIONS} discharge locations across all of its incoterms.`}
                  </small>
                </div>
                <div
                  id='incotermPortType'
                  className='pd-portpick__options'
                  role='group'
                  aria-label='Number of discharge ports'
                  tabIndex={-1}
                >
                  {[1, 2, 3].map(count => (
                    <button
                      key={count}
                      type='button'
                      className='pd-portpick__btn'
                      aria-pressed={incotermPortCount === count}
                      onClick={() => setIncotermPortSetup(count)}
                    >
                      <span className='pd-portpick__pins' aria-hidden='true'>
                        {Array.from({ length: count }, (_, i) => (
                          <i key={i} className='ri-map-pin-2-fill'></i>
                        ))}
                      </span>
                      {count === 1 ? 'Single port' : `${count} ports`}
                    </button>
                  ))}
                </div>
              </div>
              {incotermErrors.incotermPortType && (
                <div className='text-danger d-flex align-items-center gap-1'>
                  <i className='ri-error-warning-line'></i>
                  <span>{incotermErrors.incotermPortType}</span>
                </div>
              )}

              {INCOTERM_PORTS.slice(0, Math.max(incotermPortCount, 1)).map(
                port => (
                  <div className='pd-port' key={port.no}>
                    <div className='pd-port__head'>
                      <span
                        className={`pd-port__swatch pd-swatch-${port.no}`}
                        aria-hidden='true'
                      ></span>
                      Port {port.no}
                    </div>
                    <Row className='g-3'>
                      <Col md={5}>
                        <Label for={port.locationField} className='form-label'>
                          Location <span className='text-danger'>*</span>
                        </Label>
                        <Select
                          {...MODAL_SELECT_PROPS}
                          name={port.locationField}
                          id={port.locationField}
                          options={IncotermLocationData.items}
                          placeholder='Select location'
                          value={
                            IncotermLocationData.items?.find(
                              obj =>
                                obj.value ===
                                deliveryincotermformData[port.locationField]
                            ) || null
                          }
                          onChange={e => {
                            handleSelectChangeIncoterm(e, port.locationField);
                          }}
                        />
                        {incotermErrors[port.locationField] && (
                          <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                            <i className='ri-error-warning-line'></i>
                            <span>{incotermErrors[port.locationField]}</span>
                          </div>
                        )}
                      </Col>
                      <Col md={7}>
                        {renderIncotermDates(
                          port.no === 1 ? '' : String(port.no),
                          'Discharge dates',
                          true
                        )}
                      </Col>
                    </Row>
                  </div>
                )
              )}

              {/* Quantity per port: ports side by side on each quantity row,
                  so the split can be compared at a glance. */}
              {incotermQtyRows.length > 0 &&
                (() => {
                  const qtyPorts = INCOTERM_PORTS.slice(
                    0,
                    Math.max(incotermPortCount, 1)
                  );
                  const gridStyle = {
                    gridTemplateColumns: `minmax(6.5rem, auto) repeat(${qtyPorts.length}, minmax(7.5rem, 1fr)) minmax(7rem, auto)`,
                  };
                  return (
                    <div className='pd-qsplit'>
                      <div className='pd-qsplit__title'>
                        Quantity per port
                        {isIncotermSplit && (
                          <span className='text-danger'> *</span>
                        )}
                        <small>
                          {isIncotermSplit
                            ? 'Split each quantity row across the ports.'
                            : 'Each port takes the full quantity.'}
                        </small>
                      </div>
                      <div className='pd-qsplit__scroll'>
                        <div className='pd-qsplit__grid' style={gridStyle}>
                          <div className='pd-qsplit__th'>Total</div>
                          {qtyPorts.map(port => (
                            <div className='pd-qsplit__th' key={port.no}>
                              <span
                                className={`pd-port__swatch pd-swatch-${port.no}`}
                                aria-hidden='true'
                              ></span>
                              Port {port.no}
                            </div>
                          ))}
                          <div className='pd-qsplit__th'>Status</div>

                          {incotermQtyRows.map((data, index) => {
                            const total = Number(data.quantity) || 0;
                            const parts = qtyPorts.map(
                              port =>
                                Number(data[`quantityLocation${port.no}`]) || 0
                            );
                            const allocated = parts.reduce(
                              (sum, qty) => sum + qty,
                              0
                            );
                            return (
                              <React.Fragment key={`${data.id}-${index}`}>
                                <div className='pd-qsplit__total'>
                                  {incotermQtyRows.length > 1 && (
                                    <span>Row {index + 1}</span>
                                  )}
                                  {formatNumberUS(total) || 0} {unitSizeType}
                                </div>
                                {qtyPorts.map(port => {
                                  const field = `quantityLocation${port.no}`;
                                  const errorKey = `${field}_${index}`;
                                  return (
                                    <div key={port.no}>
                                      <div
                                        className={`input-group input-group-sm pd-qty${
                                          isIncotermSplit
                                            ? ''
                                            : ' pd-qty--readonly'
                                        }`}
                                      >
                                        {isIncotermSplit ? (
                                          <Input
                                            type='text'
                                            id={errorKey}
                                            className='form-control'
                                            placeholder={`Max ${
                                              formatNumberUS(data.quantity) || 0
                                            }`}
                                            aria-label={`Port ${port.no} quantity, row ${
                                              index + 1
                                            }`}
                                            invalid={Boolean(
                                              incotermErrors[errorKey]
                                            )}
                                            value={data[field] || ''}
                                            onChange={e =>
                                              handleQuantityChange(
                                                index,
                                                field,
                                                e.target.value
                                              )
                                            }
                                          />
                                        ) : (
                                          <Input
                                            type='text'
                                            className='form-control'
                                            placeholder='0'
                                            aria-label={`Port ${port.no} quantity, row ${
                                              index + 1
                                            }`}
                                            value={
                                              formatNumberUS(data.quantity) ||
                                              ''
                                            }
                                            readOnly
                                          />
                                        )}
                                        <span className='input-group-text'>
                                          {unitSizeType}
                                        </span>
                                      </div>
                                      {incotermErrors[errorKey] && (
                                        <div className='text-danger d-flex align-items-center gap-1 mt-1 fs-12'>
                                          <i className='ri-error-warning-line'></i>
                                          <span>
                                            {incotermErrors[errorKey]}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                <div className='pd-qsplit__status'>
                                  {!isIncotermSplit ? (
                                    <span className='text-muted'>
                                      Full quantity
                                    </span>
                                  ) : allocated === total ? (
                                    <span className='pd-alloc__ok'>
                                      <i className='ri-check-line'></i> Matches
                                      total
                                    </span>
                                  ) : (
                                    <span className='pd-alloc__bad'>
                                      {formatNumberUS(
                                        Math.abs(total - allocated)
                                      )}{' '}
                                      {unitSizeType}{' '}
                                      {total > allocated ? 'left' : 'over'}
                                    </span>
                                  )}
                                </div>
                                {isIncotermSplit && (
                                  <div
                                    className='pd-alloc__bar pd-qsplit__bar'
                                    role='img'
                                    aria-label={`Row ${index + 1}: allocated ${allocated} of ${total}`}
                                  >
                                    {parts.map((qty, i) =>
                                      qty > 0 && total > 0 ? (
                                        <span
                                          key={i}
                                          className={`pd-swatch-${i + 1}`}
                                          style={{
                                            width: `${Math.min(
                                              100,
                                              (qty / total) * 100
                                            )}%`,
                                          }}
                                        ></span>
                                      ) : null
                                    )}
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </section>
          )}

          {!deliveryincotermformData.incotermId && (
            <p className='text-muted fs-12 mt-3 mb-0'>
              Pick an incoterm to set its locations, quantity and dates.
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={() => {
              setModalIncoterm(!modalincoterm);
            }}
          >
            Cancel
          </ActionButton>

          <ActionButton
            variant='primary'
            icon='ri-save-line'
            onClick={() => {
              incotermFocusRef.current = true;
              return deliveryincotermformData.id === null
                ? handleAddDataDeliveryIncoterm()
                : handleUpdDataDeliveryIncoterm();
            }}
          >
            {deliveryincotermformData.id === null
              ? 'Save incoterm'
              : 'Update incoterm'}
          </ActionButton>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalloadingdischarge}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={toggleloadingdischrge}
        data-bs-backdrop='static'
        size='xl'
      >
        <ModalHeader
          toggle={() => {
            setModalLoadingDischarge(!modalloadingdischarge);
          }}
        >
          <div className='flex-grow-1'>
            <Label className='form-label  text-uppercase fw-semibold'>
              Loading / Discharge Port/s
            </Label>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className='d-flex mt-1'>
            <Col sm={12}>
              {!deliveryincotermformData.incotermLocation?.includes('STS') && (
                <DeliveryVesselFirstLocation
                  form={form}
                  deliveryincotermformData={deliveryincotermformData}
                  handleDeliveryNumberChangeIncoterm={
                    handleDeliveryNumberChangeIncoterm
                  }
                />
              )}

              {!deliveryincotermformData.incotermLocation2?.includes('STS') &&
                (deliveryincotermformData.incotermMultiPortType === '2Port' ||
                  deliveryincotermformData.incotermMultiPortType === '3Port') &&
                deliveryincotermformData.incotermId !==
                  'e0173dfd-e109-4054-8640-23d95befd326' && (
                  <DeliveryVesselSecondLocation
                    form={form}
                    deliveryincotermformData={deliveryincotermformData}
                    handleDeliveryNumberChangeIncoterm={
                      handleDeliveryNumberChangeIncoterm
                    }
                  />
                )}

              {deliveryincotermformData.incotermLocation?.includes('STS') && (
                <DeliveryVesselFirstLocationNotes
                  form={form}
                  deliveryincotermformData={deliveryincotermformData}
                  handleDeliveryTextChangeIncoterm={
                    handleDeliveryTextChangeIncoterm
                  }
                />
              )}

              <Row>
                <Col sm={12}>
                  <div className='mb-1  '>
                    <Label
                      for='additionalInformation'
                      className='form-label  text-uppercase fw-semibold'
                    >
                      Notes for Loading / Discharged Port
                    </Label>
                  </div>
                </Col>

                <Col sm={12}>
                  <Card>
                    <div className='snow-editor '>
                      <EditorProvider>
                        <Editor
                          placeholder='Add information for the loading and discharge ports (optional)'
                          id='additionalInformation'
                          name='additionalInformation'
                          value={
                            deliveryincotermformData.additionalInformation || ''
                          }
                          onChange={e => {
                            handleNoteTextChange(e, 'additionalInformation');
                          }}
                          style={{ height: '150px', overflowY: 'auto' }}
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-0'
                          }
                        >
                          {form.isProcurementCompleted === false && (
                            <RichTextToolbar />
                          )}
                        </Editor>
                      </EditorProvider>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>
          </div>
        </ModalBody>

        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={() => {
              setModalLoadingDischarge(!modalloadingdischarge);
            }}
          >
            Close
          </ActionButton>
          {form.isProcurementCompleted === false && (
            <ActionButton
              variant='primary'
              icon='ri-database-2-line'
              onClick={() => {
                setModalLoadingDischarge(!modalloadingdischarge);
                return deliveryformData.id !== null
                  ? handleUpdDataDeliveryIncoterm()
                  : handleAddDataDelivery();
              }}
            >
              {deliveryformData.id !== null ? 'Update' : 'Save'}
            </ActionButton>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalquantityincoterm}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={togglemodalquantityincoterm}
        data-bs-backdrop='static'
        size='sm'
      >
        <ModalBody>
          <div className='d-flex mt-2'>
            <Col sm={12}>
              <Row>
                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Location 1 :</Label>

                    <div className='input-group'>
                      <Input
                        placeholder='Enter quantity'
                        type='number'
                        id='quantityLocation1'
                        name='quantityLocation1'
                        className='form-control'
                        value={deliveryquantityformData.quantityLocation1 || ''}
                        onChange={e => {
                          const { name, value, type } = e.target;
                          const newValue =
                            type === 'number'
                              ? value === ''
                                ? ''
                                : parseFloat(value)
                              : value;
                          setDeliveryQuantityFormData({
                            ...deliveryquantityformData,
                            [name]: newValue,
                          });
                        }}
                      />
                      <span className='input-group-text' id='basic-addon2'>
                        MT
                      </span>
                    </div>
                  </div>
                </Col>

                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Location 2 :</Label>

                    <div className='input-group'>
                      <Input
                        placeholder='Enter quantity'
                        type='number'
                        id='quantityLocation2'
                        name='quantityLocation2'
                        className='form-control'
                        value={deliveryquantityformData.quantityLocation2 || ''}
                        onChange={e => {
                          const { name, value, type } = e.target;
                          const newValue =
                            type === 'number'
                              ? value === ''
                                ? ''
                                : parseFloat(value)
                              : value;
                          setDeliveryQuantityFormData({
                            ...deliveryquantityformData,
                            [name]: newValue,
                          });
                        }}
                      />
                      <span className='input-group-text' id='basic-addon2'>
                        {unitSizeType}
                      </span>
                    </div>
                  </div>
                </Col>

                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Total Quantity</Label>

                    <div className='input-group'>
                      <Input
                        type='number'
                        id='quantity'
                        name='quantity'
                        className='form-control  bg-light border-0'
                        value={deliveryquantityformData.quantity || ''}
                        readOnly
                      />
                      <span
                        className='input-group-text bg-light border-0'
                        id='basic-addon2'
                      >
                        {unitSizeType}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </div>
        </ModalBody>

        <ModalFooter>
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-database-2-line'
            onClick={() => {
              setModalQuantityIncoterm(!modalquantityincoterm);
              return handleUpdDataDeliveryQuantity(deliveryquantityformData.id);
            }}
          >
            Update
          </ActionButton>
        </ModalFooter>
      </Modal>

      <ViewOriginModal
        isOpen={viewOriginModal}
        toggle={() => setViewOriginModal(false)}
        locations={viewOriginLocations}
        routes={viewOriginRoutes}
      />
    </React.Fragment>
  );
};

export default RequirementTab;
